// The Usage demo's reports as complete HTML documents, generated with
// il-report from plans/usage-demo-reports/*.layout.json by
// scripts/build-usage-demo-reports.py. Each carries its own styles, so the
// project page renders it in an iframe. Generated; do not edit.

export const USAGE_V1 = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Report</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap" rel="stylesheet">
  <style>
/* GLOBAL ────────────────────────────────────────────────────────────────────────── */
:root {
  --container-min-width: 1080px;

  --report-header-expanded-offset: 17.125rem;

  /* FONT FAMILY */
  --font-family: "Inter", sans-serif;

  /* FONT WEIGHTS */
  --font-regular: 400;
  --font-semibold: 600;
  --font-bold: 700;

  /* FONT SIZES */
  --font-xs: 0.625rem;
  --font-sm: 0.75rem;
  --font-md: 0.875rem;
  --font-default: 1rem;
  --font-lg: 1.25rem;
  --font-xl: 1.5rem;

  /* LABEL */
  --label-sm-size: 0.625rem;
  --label-md-size: 0.75rem;

  /* SLATE */
  --slate-900: #141E2E;
  --slate-600: #2F466B;

  /* TEAL */
  --teal-900: #00B2FE;
  --teal-300: #75D6FF;

  /* GREY */
  --grey-900: #404040;
  --grey-600: #666666;
  --grey-500: #999999;
  --grey-400: #BFBFBF;
  --grey-300: #D9D9D9;
  --grey-100: #F0F0F0;
  --grey-50: #F5F5F5;
  --grey-10: #FAFAFA;
  --grey-white: #FFFFFF;

  /* BLUE */
  --blue-900: #006ACC;
  --blue-600: #1890FF;
  --blue-300: #99CEFF;
  --blue-100: #E5F3FF;
  --blue-50: #F0F8FF;


  /* GREEN */
  --green-900: #2FB801;
  --green-600: #5AD81D;
  --green-500: #77E043;
  --green-300: #C1F2A9;
  --green-100: #E6FADC;
  --green-50: #F2FCED;

  /* RED */
  --red-900: #FD3559;
  --red-600: #FF758E;
  --red-300: #FEA8B7;
  --red-100: #FFE6EA;
  --red-50: #FFF0F2;
  --red-10: #FFF7F9;

  /* ORANGE */
  --orange-900: #FF8400;
  --orange-600: #FF981F;
  --orange-300: #FFCA99;
  --orange-100: #FFE8CC;
  --orange-50: #FFF4EB;

  /* MAGENTA */
  --magenta-900: #EA00FF;

  /* SPECIAL */
  --slate-alt-900: #101525;
  --green-alt-900: #124002;
  --green-alt-700: #315724;
  --orange-alt-900: #591E00;
  --orange-alt-700: #663600;
  --red-alt-900: #4D000E;
  --red-alt-700: #660012;

  /* GRADIENT */
  --pink-blue: linear-gradient(90deg, #77ADFF 31.25%, #FF3FEC 100%);
}

body {
  margin: 0;
  background: var(--grey-white);
  font-family: var(--font-family);
  display: flex;
  justify-content: center;
}

.container {
  box-sizing: border-box;
  width: 100%;
  min-width: var(--container-min-width);
  padding: 0 1.5rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

p {
  margin: 0;
}

/* REPORT CONTAINER ────────────────────────────────────────────────────────────────────────── */
.report-container-row {
  display: flex;
  align-items: flex-start;
  background: var(--grey-white);
  gap: 16px;
}

.report-container {
  display: flex;
  flex-direction: column;
  background: var(--grey-white);
  flex: 1 1 0;
  min-width: 0;
  padding-bottom: 80vh;
}

/* REPORT HEADER ────────────────────────────────────────────────────────────────────────── */
.report-header-container {
  display: flex;
  flex-direction: column;
  padding: 0 0 1.25rem 0;
  position: sticky;
  top: 0;
  z-index: 1000;
  gap: 1rem;
  background: var(--grey-white);
  transition:
    padding 0.32s ease,
    gap 0.32s ease,
    box-shadow 0.32s ease;
  will-change: padding, gap;
}


/* REPORT SUMMARY CARDS ────────────────────────────────────────────────────────────────────────── */
.report-summary-container {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  background: var(--grey-white);
  transition: gap 0.22s ease;
}

.report-summary-card {
  --card-bg: var(--grey-10);
  --card-border: var(--grey-300);

  background: var(--card-bg);
  border-bottom: 1px solid var(--card-border);

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem 1rem 0.75rem 1rem;
  gap: 0.5rem;
  border-radius: 0.5rem;
  min-width: 0;
  transition:
    padding 0.32s ease,
    gap 0.32s ease,
    border-radius 0.32s ease,
    background-color 0.32s ease,
    border-color 0.32s ease;
}

.report-summary-card.wide {
  flex: 5;
}

.report-summary-card.narrow {
  flex: 2.5;
}

.report-summary-card-positive {
  --card-bg: var(--green-50);
  --card-border: var(--green-500);
}

.report-summary-card-warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-summary-card-negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

/* Collapse instantly (no animation) while a nav-item click / tab switch
   re-pins the scroll, so the header height settles in one synchronous reflow
   that browser scroll anchoring can't chase off the target. Restored next
   frame, so manual scrolling keeps its smooth collapse. */
.report-header-container.is-anim-suppressed,
.report-header-container.is-anim-suppressed * {
  transition: none !important;
}

.report-header-container.is-compact {
  padding: 0 0 1.25rem 0;
  gap: 0.75rem;
}

.report-header-container.is-compact .report-summary-container {
  gap: 0.5rem;
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.report-header-container.is-compact .report-summary-card {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  gap: 0.75rem;
  border-radius: 0.5rem;
}

.report-header-container.is-compact .report-summary-card .card-description,
.report-header-container.is-compact .report-summary-card .card-data-container {
  opacity: 0;
  transform: translateY(-0.25rem);
  pointer-events: none;
  display: none;
}

.report-header-container.is-compact .report-summary-card .label {
  flex: 0 0 auto;
  white-space: nowrap;
}

.report-header-container.is-compact .report-summary-card .report-tag {
  flex: 0 0 auto;
  min-width: auto;
  white-space: nowrap;
}

.report-header-container.is-compact .report-summary-card .card-title-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  width: auto;
  min-width: 0;
  flex: 1 1 auto;
}

.report-header-container.is-compact .report-summary-card .card-subtitle {
  opacity: 0;
  transform: translateY(-0.25rem);
  pointer-events: none;
  display: none;
}

.report-header-container.is-compact .report-summary-card .card-title {
  font-size: var(--font-md);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.report-header-container.is-compact .report-summary-card.narrow .card-title-container {
  justify-content: flex-end;
}

.card-title-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  gap: 0.25rem;
  transition: gap 0.22s ease;
}

.card-title,
.card-subtitle,
.card-description {
  width: 100%;
  min-width: 0;
}

.card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-lg);
  color: var(--slate-600);
  width: 100%;
  transition: font-size 0.22s ease, color 0.22s ease;
}

.report-summary-card-positive .card-title {
  color: var(--green-900);
}

.report-summary-card-warning .card-title {
  color: var(--orange-900);
}

.report-summary-card-negative .card-title {
  color: var(--red-900);
}

.card-subtitle {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  transition: opacity 0.18s ease, transform 0.18s ease, color 0.22s ease;
}

.card-description {
  width: 100%;
  min-width: 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  line-height: 1.6;
  color: var(--grey-600);
  transition: opacity 0.18s ease, transform 0.18s ease, color 0.22s ease;
}

.card-data-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-width: 0;
  gap: 0.5rem;
}

.card-data-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
}

.card-data-row-label {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.card-data-row-value {
  font-family: var(--font-family);
  font-weight: var(--font-bold);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.report-summary-card-positive .card-data-row-value {
  color: var(--green-900);
}

.report-summary-card-warning .card-data-row-value {
  color: var(--orange-900);
}

.report-summary-card-negative .card-data-row-value {
  color: var(--red-900);
}

.truncate {
  display: block;
  width: 100%;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* REPORT SECTION ────────────────────────────────────────────────────────────────────────── */
.report-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
  padding: 16px;
  margin: 0 0 20px 0;
  border-radius: 8px;
  border-bottom: 1px solid var(--grey-300);
  background: var(--grey-10);
}

.report-section-header-column {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  gap: 4px;
}

.report-section-card-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}

.report-section-card-container.row {
  flex-direction: row;
  justify-content: space-between;
}

/* CARD GRID — CSS-grid layout. \`grid-template-columns\` is set inline by the
   CardGrid component (via the \`columns\` prop; defaults to items.length). */
.report-card-grid {
  display: grid;
  gap: 1rem;
  width: 100%;
}

/* BASE CARD — shared layout for section / banner / callout cards */
.report-card {
  --card-bg: var(--grey-white);
  --card-border: var(--grey-300);
  background: var(--card-bg);
  border-bottom: 1px solid var(--card-border);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem 1rem 0.75rem 1rem;
  gap: 0.5rem;
  border-radius: 0.5rem;
  width: 100%;
  box-sizing: border-box;
}

.report-card .card-title-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.report-card .card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

/* Inside a card-grid, titles ellipsize to fit fixed-width grid cells. */
.report-card-grid .card-title {
  display: block;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.report-card .card-title-stat {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--green-900);
}

.report-card .card-description,
.report-card .report-text {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  line-height: 1.6;
  color: var(--grey-500);
}

/* BANNER modifier — tighter gap, severity color variants */
.report-card.banner {
  gap: 0.325rem;
}

.report-card.banner.positive {
  --card-bg: var(--green-50);
  --card-border: var(--green-500);
}

.report-card.banner.warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-card.banner.negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

.report-card.banner .card-title.positive {
  color: var(--green-900);
}

.report-card.banner .card-title.warning {
  color: var(--orange-900);
}

.report-card.banner .card-title.negative {
  color: var(--red-900);
}

.report-card.banner .card-description {
  color: var(--grey-600);
}

/* CALLOUT modifier — teal accent, smaller title */
.report-card.callout {
  --card-border: var(--teal-300);
  gap: 0.325rem;
}

.report-card.callout.warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-card.callout.negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

.report-card.callout .card-title {
  font-size: var(--font-sm);
  color: var(--teal-900);
}

.report-card.callout .card-title.warning {
  color: var(--orange-900);
}

.report-card.callout .card-title.negative {
  color: var(--red-900);
}

/* BLOCKQUOTE — verbatim quoted text (e.g. suggested contract language) */
.report-blockquote {
  margin: 0;
  padding: 0.5rem 0.7rem;
  background: var(--grey-50);
  border-left: 2px solid var(--grey-300);
  border-radius: 0 4px 4px 0;
  color: var(--grey-600);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: var(--font-xs);
  line-height: 1.6;
  white-space: pre-wrap;
}

.report-section-subheader {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 4px;
  align-self: stretch;
  padding: 0 2px;
}

.report-section-subheader-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  align-self: stretch;
  padding: 0 2px;
}

.report-section-title {
  display: flex;
  align-items: center;
  align-self: stretch;
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

.report-section-description {
  display: flex;
  align-items: center;
  max-width: 900px;
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.report-section-stat {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

.report-section-stat.positive {
  color: var(--green-900);
}

.report-section-stat.warning {
  color: var(--orange-900);
}

.report-section-stat.negative {
  color: var(--red-900);
}

/* REPORT EXPANDERS ────────────────────────────────────────────────────────────────────────── */
.report-expander {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  font-family: var(--font-family);
  border-bottom: 0.0625rem solid var(--grey-300);
  border-radius: 0.375rem;
  overflow: hidden;
  background: var(--grey-white);
}

/* HEADER BASE */
.report-expander summary {
  list-style: none;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-self: stretch;
  align-items: center;
  padding: 0.5rem 1rem;
  background: var(--grey-white);
  transition: background 0.2s ease;
}

/* HOVER ONLY WHEN CLOSED */
.report-expander:not([open]) summary:hover {
  background: var(--blue-600);
}

/* TEXT + ICON WHITE ON HOVER */
.report-expander:not([open]) summary:hover .report-title,
.report-expander:not([open]) summary:hover .report-subtitle,
.report-expander:not([open]) summary:hover .report-icon {
  color: var(--grey-white);
}

/* BORDER CHANGE */
.report-expander:not([open]):hover {
  border-bottom: 0.0625rem solid var(--blue-600);
}

/* OPEN STATE */
.report-expander[open] summary {
  background: var(--blue-600);
}

/* TEXT + ICON WHITE WHEN OPEN */
.report-expander[open] .report-title,
.report-expander[open] .report-subtitle,
.report-expander[open] .report-icon {
  color: var(--grey-white);
}

/* TEXT BLOCK */
.report-expander-text {
  display: flex;
  flex-direction: column;
}

/* TYPOGRAPHY */
.report-title {
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-bold);
  font-size: var(--font-md);
  color: var(--blue-600);
}

.report-subtitle {
  margin: 0.125rem 0 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

/* ICON */
.report-icon {
  display: flex;
  width: 0.875rem;
  height: 0.875rem;
  flex: 0 0 1rem;
  color: var(--blue-600);
  transition: transform 0.25s ease, color 0.2s ease;
}

.report-icon svg {
  width: 100%;
  height: 100%;
  display: flex;
}

.report-icon svg path {
  fill: currentColor;
}

.report-expander[open] .report-icon {
  transform: rotate(180deg);
}

/* CONTENT */
.report-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  padding: 0.75rem 1rem;
  background: var(--grey-white);
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-regular);
  line-height: 1.5;
  color: var(--grey-600);
}

/* ANIMATION */
.report-expander .report-content {
  transform-origin: top;
  transform: scaleY(0.95);
  opacity: 0;
  min-height: 200px;
  overflow: hidden;
  transition:
    transform 0.2s ease,
    opacity 0.2s ease,
    max-height 0.25s ease;
}

.report-expander[open] .report-content {
  transform: scaleY(1);
  opacity: 1;
  max-height: 31.25rem;
}

/* REMOVE DEFAULT ARROW */
.report-expander summary {
  list-style: none;
}

.report-expander summary::-webkit-details-marker {
  display: none;
}

.report-expander summary::marker {
  content: "";
}

/* REPORT TAGS ────────────────────────────────────────────────────────────────────────── */
.report-tag {
  display: flex;
  padding: 4px 10px 4px 10px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  min-width: 68px;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  color: var(--grey-white);
}

.report-tag-positive {
  background: var(--green-900);
  color: var(--grey-white);
}

.report-tag-warning {
  background: var(--orange-900);
  color: var(--grey-white);
}

.report-tag-negative {
  background: var(--red-900);
  color: var(--grey-white);
}

.report-tag-alt-positive {
  background: var(--green-100);
  color: var(--green-900);
}

.report-tag-alt-warning {
  background: var(--orange-100);
  color: var(--orange-900);
}

.report-tag-alt-negative {
  background: var(--red-100);
  color: var(--red-900);
}

/* LABELS ────────────────────────────────────────────────────────────────────────── */
.label {
  font-family: var(--font-family);
  font-size: var(--label-sm-size);
  font-weight: var(--font-semibold);
  color: var(--teal-900);
  text-transform: uppercase;
}

.label-positive {
  color: var(--green-alt-700);
}

.label-warning {
  color: var(--orange-alt-700);
}

.label-negative {
  color: var(--red-alt-900);
}

/* REPORT UTILITY NAV ────────────────────────────────────────────────────────────────────────── */
.report-utility-nav-container {
  display: flex;
  padding: 4px;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  border-radius: 6px;
  background: var(--blue-100);
}

/* Inactive */
.report-utility-nav-btn {
  display: flex;
  padding: 0.25rem 1rem;
  justify-content: center;
  align-items: center;
  border-radius: 0.25rem;
  border: none;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  color: var(--blue-600);
  cursor: pointer;

  background: var(--blue-100); /* softer inactive bg */
  color: var(--blue-600);

  box-shadow: none;
  transition: background 0.2s ease, color 0.2s ease;
}

/* HOVER (inactive only) */
.report-utility-nav-btn:hover {
  background: var(--grey-white);
}

/* ACTIVE */
.report-utility-nav-btn.active {
  background: var(--grey-white);
  color: var(--blue-600);
  box-shadow: 0 0.0625rem 0.25rem 0 var(--blue-300);
}

/* prevent hover from overriding active */
.report-utility-nav-btn.active:hover {
  background: var(--grey-white);
}

.report-header-container.is-compact .report-utility-nav-container {
  padding: 0.25rem;
}

.report-header-container.is-compact .report-utility-nav-btn {
  padding: 0.375rem 0.875rem;
}

/* REPORT SIDENAV NAV ────────────────────────────────────────────────────────────────────────── */
.report-sidenav-container {
  display: flex;
  padding-right: 16px;
  flex-direction: column;
  align-items: flex-start;
  align-self: flex-start;
  border-right: 1px solid var(--grey-100);
  gap: 4px;
  position: relative;
}

/* Stick the sidenav flush beneath the sticky header so its top items are
   never hidden behind it. The header's height is dynamic — summary-card
   content varies and the header collapses on scroll — so the runtime JS
   publishes its live border-box height as --report-header-height and the
   sidenav tracks it exactly (through the collapse transition too). The
   expanded-offset token is only the pre-JS fallback. */
.report-container-row .report-sidenav-container {
  position: sticky;
  top: var(--report-header-height, var(--report-header-expanded-offset));
}

/* BASE = inactive */
.report-sidenav-btn {
  display: flex;
  width: 240px;
  padding: 0.5rem 0.5rem 0.5rem 0.625rem;
  justify-content: space-between;
  align-items: center;
  border-radius: 0.375rem;
  border: none;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);

  color: var(--grey-500);
  cursor: pointer;

  background: var(--grey-white);
  transition: background 0.2s ease, color 0.2s ease;
}

/* HOVER (inactive only) */
.report-sidenav-btn:not(.active):hover {
  background: var(--grey-10);
}

/* ACTIVE */
.report-sidenav-btn.active {
  background: var(--grey-10);
  color: var(--slate-600);
}

/* keep active stable */
.report-sidenav-btn.active:hover {
  background: var(--grey-10);
}


/* CHEVRON (reuse report-icon) */
.report-sidenav-btn .report-icon {
  margin-left: auto;
  transform: rotate(0deg); /* DOWN */
  transition: transform 0.2s ease;
}

/* ACTIVE → RIGHT */
.report-sidenav-btn.active .report-icon {
  transform: rotate(-90deg);
}

/* SIZE */
.report-sidenav-btn .report-icon {
  width: 0.625rem;
  height: 0.625rem;

  margin-left: auto;
  display: flex;
  align-items: center;

  /* COLOR (inherits from button) */
  color: inherit;

  /* DEFAULT → DOWN */
  transform: rotate(0deg);
  transition: transform 0.2s ease;
}

/* ACTIVE → RIGHT */
.report-sidenav-btn.active .report-icon {
  transform: rotate(-90deg);
}

/* BANNER BOX — now uses .report-card.banner (see BASE CARD above) */

/* SUPPORTING ARGUMENTS BOX ────────────────────────────────────────────────────────────────────────── */
.report-description-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  width: 100%;
  margin: .5rem 0 0 0;
}

.report-description-list-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
}

.report-description-list .label {
  font-size: var(--label-md-size);
}

.report-description-list .card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--slate-600);
}

.report-description-list .card-description {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-500);
}

/* TABLE ────────────────────────────────────────────────────────────────────────── */
.report-table-wrapper {
  width: 100%;
}

.report-table-title {
  margin: 0 0 0.25rem 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--label-sm-size);
  color: var(--slate-900);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  background: var(--grey-white);
  margin: 0.5rem 0;
}

.report-table th,
.report-table td {
  overflow-wrap: anywhere;
}

/* HEADER */
.report-table thead th {
  padding:  0.5rem 0.75rem;
  text-align: left;
  background: var(--grey-100);

  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--slate-600);
}

/* BODY */
.report-table tbody td {
  padding:  0.5rem 0.75rem;

  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

/* NUMERIC */
.report-table-cell-numeric {
  text-align: left;
  white-space: nowrap;
}

/* POSITION */
.report-table-position {
  font-weight: var(--font-semibold);
  color: var(--slate-600);
}

/* CELL / COLUMN SENTIMENT — applies to spans in both <th> and <td>. */
.report-table .positive { color: var(--green-900); }
.report-table .warning  { color: var(--orange-900); }
.report-table .negative { color: var(--red-900); }

/* TWO-LINE CELL — value on top, muted sub underneath. */
.report-table-cell-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.report-table-cell-sub {
  font-size: var(--font-xs);
  font-weight: var(--font-regular);
  color: var(--grey-500);
}

/* PILL CELL — renders the cell value as a rounded badge. */
.report-table-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  background: var(--grey-100);
  color: var(--grey-600);
}
.report-table-pill-positive { background: var(--green-100); color: var(--green-900); }
.report-table-pill-warning  { background: var(--orange-100); color: var(--orange-900); }
.report-table-pill-negative { background: var(--red-100); color: var(--red-900); }

/* ROW VARIANTS — zebra stripes apply unless a row has its own
   background treatment (focus variant, or row sentiment). */
.report-table tbody tr:nth-child(odd):not(.report-table-row-focus):not(.report-table-row-positive):not(.report-table-row-warning):not(.report-table-row-negative):not(.report-table-row-heading):not(.report-table-row-total) td {
  background: var(--grey-white);
}

.report-table tbody tr:nth-child(even):not(.report-table-row-focus):not(.report-table-row-positive):not(.report-table-row-warning):not(.report-table-row-negative):not(.report-table-row-heading):not(.report-table-row-total) td {
  background: var(--grey-10);
}

.report-table-row-focus td {
  background: var(--blue-100);
}

.report-table-row-positive td {
  background: var(--green-100);
}

.report-table-row-warning td {
  background: var(--orange-100);
}

.report-table-row-negative td {
  background: var(--red-100);
}

/* Total row: bolden every cell (td + nested span override the base
   \`.report-table tbody td\` regular weight and \`.report-table-position\`
   semibold weight). */
.report-table tbody tr.report-table-row-total td,
.report-table tbody tr.report-table-row-total td span {
  font-weight: var(--font-semibold);
}

.report-table tbody tr.report-table-row-total td {
  border-top: 2px solid var(--grey-500);
  background: var(--grey-50);
}

/* Heading row: group-label spanning every column. Uses the same visual
   treatment as the standalone \`.report-heading\` block (uppercase, tight
   tracking, slate-900) with a distinct background so it stands apart
   from the zebra-striped data rows. */
.report-table tbody tr.report-table-row-heading td {
  padding: 0.625rem 0.75rem;
  background: var(--blue-50);
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* VALUE EMPHASIS */
.report-table .report-table-cell-numeric {
  color: var(--grey-600);
}

/* HEADING ─────────────────────────────────────────────────────────────────────── */
.report-heading {
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* TEXT ────────────────────────────────────────────────────────────────────────── */
/* Text is a single paragraph — rendered as \`<p class="report-text">\`. */
.report-text {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
  line-height: 1.6;
  width: 100%;
  margin: 0;
}

/* Sentiment tint. Specificity (0,2,0) beats the in-card
   \`.report-card .report-text\` color override (0,2,0) via order. */
.report-text.positive { color: var(--green-900); }
.report-text.warning  { color: var(--orange-900); }
.report-text.negative { color: var(--red-900); }

/* METRIC LIST ────────────────────────────────────────────────────────────────────────── */
.report-metric-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--grey-white);
  border: 0.0625rem solid var(--grey-300);
  border-radius: 0.5rem;
  width: 100%;
}

.report-metric-list-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-family: var(--font-family);
  font-size: var(--font-sm);
}

.report-metric-list-label {
  color: var(--grey-600);
  font-weight: var(--font-regular);
}

.report-metric-list-value {
  color: var(--slate-600);
  font-weight: var(--font-semibold);
}

.report-metric-list-value.positive { color: var(--green-900); }
.report-metric-list-value.warning  { color: var(--orange-900); }
.report-metric-list-value.negative { color: var(--red-900); }

/* STAT GRID ────────────────────────────────────────────────────────────────────────── */
.report-stat-grid {
  display: grid;
  gap: 1rem;
  width: 100%;
}

.report-stat-grid-item {
  display: flex;
  flex-direction: column;
  padding: 0.875rem 1rem;
  background: var(--grey-10);
  border-bottom: 1px solid var(--grey-300);
  border-radius: 0.5rem;
  gap: 0.25rem;
  min-width: 0;
}

.report-stat-grid-label {
  font-family: var(--font-family);
  font-size: var(--label-sm-size);
  font-weight: var(--font-semibold);
  color: var(--grey-600);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.report-stat-grid-value {
  font-family: var(--font-family);
  font-size: var(--font-xl);
  font-weight: var(--font-bold);
  color: var(--slate-600);
  line-height: 1.2;
}

.report-stat-grid-value.positive { color: var(--green-900); }
.report-stat-grid-value.warning  { color: var(--orange-900); }
.report-stat-grid-value.negative { color: var(--red-900); }

.report-stat-grid-sub {
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-regular);
  color: var(--grey-600);
}

.report-stat-grid-badge {
  align-self: flex-start;
  padding: 2px 8px;
  margin-top: 0.25rem;
  border-radius: 4px;
  background: var(--blue-100);
  color: var(--blue-900);
  font-family: var(--font-family);
  font-size: var(--font-xs);
  font-weight: var(--font-semibold);
}

/* WATERFALL CHART ────────────────────────────────────────────────────────────────────────── */
.report-waterfall-wrap {
  position: relative;
  width: 100%;
}

.report-waterfall {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  font-family: var(--font-family);
}

.report-waterfall-gridline {
  stroke: var(--grey-100);
  stroke-width: 1;
}

.report-waterfall-baseline {
  stroke: var(--grey-300);
  stroke-width: 1;
}

.report-waterfall-connector {
  stroke: var(--grey-300);
  stroke-width: 1;
  stroke-dasharray: 4, 3;
}

.report-waterfall-tick {
  fill: var(--grey-500);
  font-size: 10px;
}

.report-waterfall-label {
  fill: var(--grey-600);
  font-size: 11px;
  font-weight: var(--font-semibold);
}

.report-waterfall-label-secondary {
  font-weight: var(--font-regular);
}

.report-waterfall-bar-total            { fill: var(--blue-900); }
.report-waterfall-bar-captured-saving  { fill: var(--green-900); }
.report-waterfall-bar-avoided-exposure { fill: var(--grey-500); }

.report-waterfall-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  margin: 8px 0 0;
  font-size: var(--font-xs);
  color: var(--grey-600);
}
.report-waterfall-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.report-waterfall-legend-swatch {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
}
.report-waterfall-legend-swatch-total            { background: var(--blue-900); }
.report-waterfall-legend-swatch-captured-saving  { background: var(--green-900); }
.report-waterfall-legend-swatch-avoided-exposure { background: var(--grey-500); }

.report-waterfall-value {
  font-size: 11px;
  font-weight: var(--font-bold);
}
.report-waterfall-value-total            { fill: var(--blue-900); }
.report-waterfall-value-captured-saving  { fill: var(--green-900); }
.report-waterfall-value-avoided-exposure { fill: var(--grey-600); }

/* PIE CHART ────────────────────────────────────────────────────────────────────────── */
.report-pie-wrap {
  position: relative;
  width: 100%;
}

.report-pie {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  font-family: var(--font-family);
}

.report-pie-slice {
  /* Fill colours are applied per-element from the JS palette. */
}

.report-pie-leader {
  stroke: var(--grey-400);
  stroke-width: 1;
  fill: none;
}

.report-pie-label {
  fill: var(--slate-900);
  font-size: 11px;
  font-weight: var(--font-semibold);
}

.report-pie-value {
  fill: var(--grey-600);
  font-size: 10px;
  font-weight: var(--font-regular);
}

.report-pie-empty {
  text-align: center;
  color: var(--grey-500);
  font-size: var(--font-sm);
  margin: 24px 0;
}

/* VEGA-LITE CHART ────────────────────────────────────────────────────────────────────────── */
.report-vega-chart {
  width: 100%;
}

.report-vega-chart-svg {
  display: flex;
  justify-content: center;
}

.report-vega-chart-svg svg {
  max-width: 100%;
  height: auto;
}
  
/* Added by Oasis. Embedded under the project header card, the report spans the
   frame's full width: the page around it supplies the gutters. */
.container { min-width: 0; padding: 0 0 1.5rem 0; }
/* The side nav's entries: left aligned, one line each, cut with an ellipsis. */
.report-sidenav-btn > span:first-child { flex: 1; min-width: 0; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
</style>
</head>
<body>
<div class="container"><div class="report-header-container"><div class="report-summary-container"><div class="report-summary-card report-summary-card-positive wide"><div class="label label-positive">Recommendation</div><div class="report-tag report-tag-positive">Usage analysis v1</div><div class="card-title-container"><div class="card-title"><span class="truncate">Renew 10,400 licenses</span></div><div class="card-subtitle"><span class="card-subtitle">8,300 E5 and 2,100 E3, down from 12,400</span></div></div><div class="card-data-container"><div class="card-data-row"><span class="card-data-row-label">Saving at current prices</span><span class="card-data-row-value">$1.9M a year</span></div><div class="card-data-row"><span class="card-data-row-label">Net after the vendor&apos;s response</span><span class="card-data-row-value">$1.6M to $1.7M</span></div><div class="card-data-row"><span class="card-data-row-label">Next-term cost</span><span class="card-data-row-value">$6.6M against $8.5M today</span></div></div></div><div class="report-summary-card report-summary-card-warning wide"><div class="label label-warning">Deadline</div><div class="card-title-container"><div class="card-title"><span class="truncate">Notice by 31 Dec 2026</span></div><div class="card-subtitle"><span class="card-subtitle">89 days from this report; renews 31 Mar 2027</span></div></div><div class="card-description"><p>Owner reviews on <strong>1,150 seats</strong> are due 21 Nov. Seats under review are kept in the count until the owners answer. Business owner: End-user technology.</p></div></div><div class="report-summary-card report-summary-card-positive narrow"><div class="label label-positive">Price lever</div><div class="card-title-container"><div class="card-title"><span class="truncate">$1.0M to $1.1M more</span></div><div class="card-subtitle"><span class="card-subtitle">E5 at $48 against $57; see the playbook</span></div></div></div></div><div class="report-utility-nav-container"><button class="report-utility-nav-btn active" data-tab-id="usage">Usage analysis</button></div></div><div class="report-container-row"><div class="report-sidenav-container"><div class="report-tab-sidenav" data-tab-id="usage"><button class="report-sidenav-btn" data-target="recommendation"><span>Recommendation</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="quantity"><span>How the quantity was set</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="edition-mix"><span>Edition mix</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="range"><span>What is kept, and the range</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="asks"><span>What to ask for</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="vendor-response"><span>Vendor response and the net saving</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="after-signing"><span>After signing</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="evidence"><span>Evidence</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button></div></div><div class="report-container"><div class="report-tab-content" data-tab-id="usage"><div class="report-section" id="recommendation"><div class="report-section-header-column"><div class="report-section-subheader-row"><p class="report-section-title">Recommendation</p><p class="report-section-stat positive">10,400 licenses</p></div><div class="card-description"><p>Microsoft 365 renewal: how much to buy and at which edition. Prepared 3 Oct 2026, usage to 26 Sep 2026.</p></div></div><div class="report-section-card-container"><div class="report-card callout"><div class="card-title-row"><span class="card-title">Basis</span></div><div class="card-description"><p>Based on usage to 26 Sep 2026; <strong>94% of users matched</strong> to the contract. Seats under owner review (1,150) are kept in the count.</p></div></div></div><div class="report-section-card-container"><p class="report-text">Renew <strong>10,400 licenses</strong>, down from 12,400: 8,300 E5 and 2,100 E3. At current prices that is $6.6M a year against $8.5M today, a saving of <strong>$1.9M</strong>; net after the vendor&apos;s expected response, <strong>$1.6M to $1.7M</strong>. The price lever adds $1.0M on top (see the playbook), for a target of $2.9M a year.</p></div><div class="report-section-card-container"><div class="report-stat-grid" style="grid-template-columns: repeat(4, minmax(0, 1fr));"><div class="report-stat-grid-item"><span class="report-stat-grid-label">Licenses next term</span><span class="report-stat-grid-value">10,400</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Saving a year</span><span class="report-stat-grid-value positive">$1.9M</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Days to notice</span><span class="report-stat-grid-value warning">89</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Owner reviews due</span><span class="report-stat-grid-value">21 Nov</span></div></div></div></div><div class="report-section" id="quantity"><div class="report-section-subheader-row"><p class="report-section-title">How the quantity was set</p></div><div class="report-section-card-container"><p class="report-text">From licenses bought to licenses needed: what was never assigned and what departed staff still hold goes now; what the function owners confirmed as not needed goes at renewal; the headcount plan adds back the hires. Seats inactive but <strong>still under owner review stay in the count</strong>.</p></div><div class="report-section-card-container"><div class="report-table-wrapper"><p class="report-table-title">From licenses bought to licenses needed</p><table class="report-table"><colgroup><col><col style="width: 20%"><col></colgroup><thead><tr><th><span>Step</span></th><th class="report-table-cell-numeric"><span>Licenses</span></th><th><span>Source</span></th></tr></thead><tbody><tr class="report-table-row-total"><td><span class="report-table-position">Bought</span></td><td class="report-table-cell-numeric"><span>12,400</span></td><td><span>Enterprise Agreement, 12 Sep 2026 extraction</span></td></tr><tr><td><span class="report-table-position">Never assigned</span></td><td class="report-table-cell-numeric"><span>−550</span></td><td><span>M365 admin center, 25 Oct 2026</span></td></tr><tr><td><span class="report-table-position">Held by departed staff</span></td><td class="report-table-cell-numeric"><span>−560</span></td><td><span>Workday headcount, 20 Oct 2026</span></td></tr><tr><td><span class="report-table-position">Inactive and confirmed not needed by the owner</span></td><td class="report-table-cell-numeric"><span>−1,240</span></td><td><span>Owner reviews confirmed 24 Oct (three functions)</span></td></tr><tr><td><span class="report-table-position">Planned hires</span></td><td class="report-table-cell-numeric"><span>+350</span></td><td><span>Headcount plan to 31 Mar 2028</span></td></tr><tr class="report-table-row-focus"><td><span class="report-table-position">Needed next term</span></td><td class="report-table-cell-numeric"><span>10,400</span></td><td><span>8,300 E5 and 2,100 E3</span></td></tr></tbody></table></div></div></div><div class="report-section" id="edition-mix"><div class="report-section-subheader-row"><p class="report-section-title">Edition mix</p><p class="report-section-stat">2,100 fit E3</p></div><div class="report-section-card-container"><p class="report-text"><strong>2,100 active users</strong> used none of the E5-only features in the 90 days to 26 Sep 2026 (Defender and Purview, Teams Phone, audio conferencing, Power BI Pro, advanced eDiscovery). They fit E3 at $36 a user a month instead of $57. The remaining 6,800 used at least one E5-only feature.</p></div><div class="report-section-card-container"><div class="report-table-wrapper"><p class="report-table-title">By function</p><table class="report-table"><thead><tr><th><span>Function</span></th><th class="report-table-cell-numeric"><span>Active users</span></th><th class="report-table-cell-numeric"><span>Fit E3</span></th><th class="report-table-cell-numeric"><span>Keep E5</span></th><th class="report-table-cell-numeric"><span>Awaiting review</span></th><th><span>Review status</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Client services</span></td><td class="report-table-cell-numeric"><span>2,360</span></td><td class="report-table-cell-numeric"><span>900</span></td><td class="report-table-cell-numeric"><span>1,460</span></td><td class="report-table-cell-numeric"><span>510</span></td><td><span class="warning">Due 21 Nov</span></td></tr><tr><td><span class="report-table-position">Advisory and wealth</span></td><td class="report-table-cell-numeric"><span>2,050</span></td><td class="report-table-cell-numeric"><span>300</span></td><td class="report-table-cell-numeric"><span>1,750</span></td><td class="report-table-cell-numeric"><span>0</span></td><td><span class="positive">Confirmed 24 Oct</span></td></tr><tr><td><span class="report-table-position">Technology</span></td><td class="report-table-cell-numeric"><span>2,300</span></td><td class="report-table-cell-numeric"><span>100</span></td><td class="report-table-cell-numeric"><span>2,200</span></td><td class="report-table-cell-numeric"><span>0</span></td><td><span class="positive">Confirmed 24 Oct</span></td></tr><tr><td><span class="report-table-position">Operations</span></td><td class="report-table-cell-numeric"><span>1,500</span></td><td class="report-table-cell-numeric"><span>600</span></td><td class="report-table-cell-numeric"><span>900</span></td><td class="report-table-cell-numeric"><span>640</span></td><td><span class="warning">Due 21 Nov</span></td></tr><tr><td><span class="report-table-position">Corporate functions</span></td><td class="report-table-cell-numeric"><span>690</span></td><td class="report-table-cell-numeric"><span>200</span></td><td class="report-table-cell-numeric"><span>490</span></td><td class="report-table-cell-numeric"><span>0</span></td><td><span class="positive">Confirmed 24 Oct</span></td></tr><tr class="report-table-row-total"><td><span class="report-table-position">Total</span></td><td class="report-table-cell-numeric"><span>8,900</span></td><td class="report-table-cell-numeric"><span>2,100</span></td><td class="report-table-cell-numeric"><span>6,800</span></td><td class="report-table-cell-numeric"><span>1,150</span></td><td><span></span></td></tr></tbody></table></div></div><div class="report-section-card-container"><p class="report-text">The 8,300 E5 is the 6,800 active E5 users, plus the 1,150 seats under review, plus the 350 planned hires.</p></div></div><div class="report-section" id="range"><div class="report-section-subheader-row"><p class="report-section-title">What is kept, and the range</p></div><div class="report-section-card-container"><div class="report-metric-list"><div class="report-metric-list-row"><span class="report-metric-list-label">All 1,150 reviewed seats released</span><span class="report-metric-list-value">7,150 E5</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">As recommended</span><span class="report-metric-list-value positive">8,300 E5</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">All kept, plus 300 hired ahead of plan</span><span class="report-metric-list-value">8,600 E5</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Each 100 seats released</span><span class="report-metric-list-value">$68K a year</span></div></div></div><div class="report-section-card-container"><p class="report-text">The E3 count (2,100) does not move with the reviews: those users are active, only their edition changes. The two outstanding reviews (Client services 510, Operations 640) decide where in the range the renewal lands.</p></div></div><div class="report-section" id="asks"><div class="report-section-subheader-row"><p class="report-section-title">What to ask for</p></div><div class="report-section-card-container"><div class="report-card banner negative"><div class="card-title-row"><span class="card-title negative">Reduce the quantity to 10,400 at renewal</span><span class="report-tag report-tag-negative">Quantity</span></div><div class="card-description"><p>8,300 E5 and 2,100 E3, with the reviewed seats settled before the order.</p></div></div><div class="report-card banner negative"><div class="card-title-row"><span class="card-title negative">A right to reduce during the term</span><span class="report-tag report-tag-negative">Terms</span></div><div class="card-description"><p>The contract review found no right to reduce and no price protection. Ask for 10% a year without penalty.</p></div></div><div class="report-card banner warning"><div class="card-title-row"><span class="card-title warning">An E3 mix</span><span class="report-tag report-tag-warning">Edition</span></div><div class="card-description"><p>2,100 users at $36 instead of $57, with the right to move users between editions at the anniversary.</p></div></div><div class="report-card banner warning"><div class="card-title-row"><span class="card-title warning">Price E5 at the peer target</span><span class="report-tag report-tag-warning">Price</span></div><div class="card-description"><p>$48.00 against $57.00 today; the peer median is $49.50 over 23 agreements.</p></div></div><div class="report-card banner warning"><div class="card-title-row"><span class="card-title warning">Protection against the announced list rise</span><span class="report-tag report-tag-warning">Terms</span></div><div class="card-description"><p>Microsoft raises list prices 5% from 1 Apr 2027, the day after the renewal. Fix the price for the term.</p></div></div></div></div><div class="report-section" id="vendor-response"><div class="report-section-subheader-row"><p class="report-section-title">Vendor response and the net saving</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col><col style="width: 18%"><col style="width: 22%"></colgroup><thead><tr><th><span>Lever</span></th><th class="report-table-cell-numeric"><span>Gross</span></th><th class="report-table-cell-numeric"><span>Net after response</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Right-size: quantity and edition</span></td><td class="report-table-cell-numeric"><span>$1.9M</span></td><td class="report-table-cell-numeric"><span>$1.6M to $1.7M</span></td></tr><tr><td><span class="report-table-position">Price: E5 at $48</span></td><td class="report-table-cell-numeric"><span>$1.1M</span></td><td class="report-table-cell-numeric"><span>$1.0M</span></td></tr><tr class="report-table-row-total"><td><span class="report-table-position">Total</span></td><td class="report-table-cell-numeric"><span>$3.0M</span></td><td class="report-table-cell-numeric"><span class="positive">$2.5M to $2.7M</span></td></tr></tbody></table></div><div class="report-section-card-container"><div class="report-description-list"><span class="label">Expected vendor moves</span><div class="report-description-list-content"><div class="card-title"><span class="truncate">Re-pricing of the retained E5:</span></div><div class="card-description"><p>The per-user discount shrinks as the quantity falls; part of the right-size saving comes back as price.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">Loss of a discount tier:</span></div><div class="card-description"><p>Below 10,000 E5 the agreement drops a tier; the E3 mix keeps the total above 10,000 licenses.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">Bundling Copilot:</span></div><div class="card-description"><p>Expect an offer to fold Copilot into the renewal to hold the total; the 460 inactive Copilot seats say no.</p></div></div></div></div><div class="report-section-card-container"><p class="report-text">Figures are illustrative; the playbook carries the negotiation plan and the rounds.</p></div></div><div class="report-section" id="after-signing"><div class="report-section-subheader-row"><p class="report-section-title">After signing</p></div><div class="report-section-card-container"><p class="report-text">Check the first invoice, due 30 Jun 2027, against the signed quantities and unit prices.</p></div><div class="report-section-card-container"><p class="report-text">Re-run this analysis 90 days after the renewal date. The owner reviews become the baseline for the next term&apos;s reduction right.</p></div></div><div class="report-section" id="evidence"><div class="report-section-subheader-row"><p class="report-section-title">Evidence</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 30%"><col style="width: 20%"><col></colgroup><thead><tr><th><span>Source</span></th><th><span>As of</span></th><th><span>Supports</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Microsoft Entra sign-ins</span></td><td><span>29 Oct 2026, daily</span></td><td><span>Last sign-in and days inactive per user</span></td></tr><tr><td><span class="report-table-position">Microsoft 365 admin center usage reports</span></td><td><span>25 Oct 2026</span></td><td><span>Licenses assigned, E5 feature use per user (94% matched to the contract)</span></td></tr><tr><td><span class="report-table-position">Workday headcount</span></td><td><span>20 Oct 2026</span></td><td><span>Employment status, departures, planned hires</span></td></tr><tr><td><span class="report-table-position">Coupa AP</span></td><td><span>24 Oct 2026</span></td><td><span>Invoices paid; unit price $57 a user a month</span></td></tr><tr><td><span class="report-table-position">Enterprise Agreement extraction</span></td><td><span>12 Sep 2026</span></td><td><span>Quantities, term, no right to reduce, no price protection</span></td></tr><tr><td><span class="report-table-position">Market data and playbook</span></td><td><span>3 Oct 2026</span></td><td><span>Peer median $49.50 over 23 agreements; target $48.00</span></td></tr></tbody></table></div></div></div></div></div></div>
<script>
  const tabButtons = document.querySelectorAll(".report-utility-nav-btn");
  const header = document.querySelector(".report-header-container");

  // The sidenav sticks flush beneath the header (see styles.css). The header's
  // height varies with its content and shrinks as it collapses, so publish its
  // live height and keep the sidenav in sync — ResizeObserver fires through the
  // collapse transition too, so the sidenav tracks the header frame by frame.
  function publishHeaderHeight() {
    if (!header) return;
    document.documentElement.style.setProperty(
      "--report-header-height",
      header.offsetHeight + "px"
    );
  }

  if (header && "ResizeObserver" in window) {
    new ResizeObserver(publishHeaderHeight).observe(header);
  }
  publishHeaderHeight();

  // Measure the header's expanded and compact heights so the collapse
  // thresholds adapt to the report (summary-card content varies the height).
  // Toggling with the transition suppressed makes the probe instant and
  // invisible. Re-measured on resize / once webfonts settle, since both shift
  // the height.
  let expandedHeaderH = 0;
  let compactHeaderH = 0;
  function measureHeaderHeights() {
    if (!header) return;
    const wasCompact = header.classList.contains("is-compact");
    header.classList.add("is-anim-suppressed");
    header.classList.remove("is-compact");
    expandedHeaderH = header.offsetHeight;
    header.classList.add("is-compact");
    compactHeaderH = header.offsetHeight;
    header.classList.toggle("is-compact", wasCompact);
    header.classList.remove("is-anim-suppressed");
  }
  measureHeaderHeights();
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(measureHeaderHeights);
  }

  // Header collapse controller. The header is expanded at the very top of the
  // report and compact once you've scrolled down into the content. Run on
  // every scroll and after programmatic scrolls (nav-item clicks, tab
  // switches) so both paths behave identically.
  //
  // Driven by scrollY with hysteresis: expand near the very top, collapse once
  // you've scrolled past the header. The catch is that collapsing shrinks the
  // header by \`delta\`, and browser scroll anchoring then shifts scrollY by that
  // same delta to keep your place — so the gap between the two thresholds must
  // exceed \`delta\`, otherwise the post-toggle anchoring shift lands past the
  // opposite threshold and the header flickers. We collapse \`delta\` + margin
  // down and expand within \`margin\` of the top, giving a band of delta + margin.
  const HEADER_MARGIN_PX = 32;
  function updateHeaderCompact() {
    if (!header) return;
    const delta = Math.max(0, expandedHeaderH - compactHeaderH);
    const y = window.pageYOffset;
    if (y <= HEADER_MARGIN_PX) {
      header.classList.remove("is-compact");
    } else if (y >= delta + 2 * HEADER_MARGIN_PX) {
      header.classList.add("is-compact");
    }
  }

  // Scroll a section flush beneath the header (used by nav-item clicks and tab
  // switches). The offset depends on the header height, but landing the scroll
  // *also* changes whether the header is collapsed — and an animated collapse
  // reflows content under the new scroll position, which browser scroll
  // anchoring then chases, drifting us onto the previous section. So: jump,
  // settle the header into its destination state with the collapse animation
  // suppressed (one synchronous reflow, nothing for anchoring to track), then
  // re-pin against the now-final header height. Animation is restored next
  // frame so manual scrolling keeps its smooth collapse.
  function scrollSectionToTop(target) {
    if (!target) return;
    const pin = () =>
      Math.max(0, target.getBoundingClientRect().top + window.pageYOffset - getHeaderOffset());
    window.scrollTo({ top: pin(), behavior: "auto" });
    if (header) header.classList.add("is-anim-suppressed");
    updateHeaderCompact();
    window.scrollTo({ top: pin(), behavior: "auto" });
    if (header) {
      requestAnimationFrame(() => header.classList.remove("is-anim-suppressed"));
    }
  }

  function getHeaderOffset() {
    return header ? header.offsetHeight : 0;
  }

  function updateActiveSidenav() {
    const activePane = document.querySelector('.report-tab-content:not([hidden])');
    if (!activePane) return;

    const sections = activePane.querySelectorAll('.report-section');
    if (!sections.length) return;

    let activeId = sections[0].id;
    const headerOffset = getHeaderOffset() + 24;

    sections.forEach(section => {
      const rect = section.getBoundingClientRect();
      if (rect.top <= headerOffset) {
        activeId = section.id;
      }
    });

    document
      .querySelectorAll('.report-tab-sidenav:not([hidden]) .report-sidenav-btn')
      .forEach(button => {
        button.classList.toggle('active', button.dataset.target === activeId);
      });
  }

  function onScroll() {
    updateHeaderCompact();
    updateActiveSidenav();
  }

  function activateTab(tabId) {
    tabButtons.forEach(button => button.classList.toggle("active", button.dataset.tabId === tabId));

    document.querySelectorAll(".report-tab-sidenav").forEach(element => {
      element.hidden = element.dataset.tabId !== tabId;
    });

    document.querySelectorAll(".report-tab-content").forEach(element => {
      element.hidden = element.dataset.tabId !== tabId;
    });

    document.querySelectorAll(".report-sidenav-btn").forEach(button => button.classList.remove("active"));

    const firstSide = document.querySelector('.report-tab-sidenav:not([hidden]) .report-sidenav-btn');
    if (firstSide) firstSide.classList.add('active');

    const firstSection = document.querySelector('.report-tab-content:not([hidden]) .report-section');
    scrollSectionToTop(firstSection);

    updateActiveSidenav();
  }

  tabButtons.forEach(button => {
    button.addEventListener("click", () => activateTab(button.dataset.tabId));
  });

  const firstExpander = document.querySelector(".report-expander");
  if (firstExpander) firstExpander.setAttribute("open", "");

  const firstSideOnLoad = document.querySelector('.report-tab-sidenav:not([hidden]) .report-sidenav-btn');
  if (firstSideOnLoad) firstSideOnLoad.classList.add('active');

  document.querySelectorAll(".report-sidenav-btn").forEach(button => {
    button.addEventListener("click", () => {
      const target = document.getElementById(button.dataset.target);
      if (!target) return;

      document.querySelectorAll('.report-tab-sidenav:not([hidden]) .report-sidenav-btn').forEach(sideButton => {
        sideButton.classList.remove('active');
      });
      button.classList.add('active');

      scrollSectionToTop(target);
      updateActiveSidenav();
    });
  });

  updateHeaderCompact();
  updateActiveSidenav();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
    measureHeaderHeights();
    onScroll();
  });

    // Fallback colgroup for tables whose schema didn't declare column
    // widths. If the server-side renderer already emitted a <colgroup>
    // (because at least one column in the JSON had a \`width\`), respect
    // it and bail — don't strip and replace.
  document.querySelectorAll('.report-table').forEach((table) => {
    if (table.querySelector(':scope > colgroup')) return;

    const firstRow = table.querySelector('thead tr, tbody tr');
    if (!firstRow) return;

    const columnCount = firstRow.children.length;

    const colgroup = document.createElement('colgroup');

    let widths = [];

    if (columnCount === 2) {
      widths = ['13.75rem', 'auto']; // 220px / rest
    } else if (columnCount === 3) {
      widths = ['13.75rem', '13.125rem', 'auto']; // 220 / 210 / rest
    } else if (columnCount === 4) {
      widths = ['13.75rem', '13.125rem', '13.125rem', 'auto']; // 220 / 210 / 210 / rest
    } else if (columnCount === 5) {
      widths = ['13.75rem', '13.125rem', '12rem', '12rem', '10rem'];
    } else {
      // fallback: first two anchored, rest auto
      widths = Array.from({ length: columnCount }, (_, i) => {
        if (i === 0) return '13.75rem';
        if (i === 1) return '13.125rem';
        return 'auto';
      });
    }

    widths.forEach((width) => {
      const col = document.createElement('col');
      if (width !== 'auto') {
        col.style.width = width;
      }
      colgroup.appendChild(col);
    });

    table.insertBefore(colgroup, table.firstChild);
  });
</script>
</body>
</html>

`;

export const USAGE_V2 = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Report</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap" rel="stylesheet">
  <style>
/* GLOBAL ────────────────────────────────────────────────────────────────────────── */
:root {
  --container-min-width: 1080px;

  --report-header-expanded-offset: 17.125rem;

  /* FONT FAMILY */
  --font-family: "Inter", sans-serif;

  /* FONT WEIGHTS */
  --font-regular: 400;
  --font-semibold: 600;
  --font-bold: 700;

  /* FONT SIZES */
  --font-xs: 0.625rem;
  --font-sm: 0.75rem;
  --font-md: 0.875rem;
  --font-default: 1rem;
  --font-lg: 1.25rem;
  --font-xl: 1.5rem;

  /* LABEL */
  --label-sm-size: 0.625rem;
  --label-md-size: 0.75rem;

  /* SLATE */
  --slate-900: #141E2E;
  --slate-600: #2F466B;

  /* TEAL */
  --teal-900: #00B2FE;
  --teal-300: #75D6FF;

  /* GREY */
  --grey-900: #404040;
  --grey-600: #666666;
  --grey-500: #999999;
  --grey-400: #BFBFBF;
  --grey-300: #D9D9D9;
  --grey-100: #F0F0F0;
  --grey-50: #F5F5F5;
  --grey-10: #FAFAFA;
  --grey-white: #FFFFFF;

  /* BLUE */
  --blue-900: #006ACC;
  --blue-600: #1890FF;
  --blue-300: #99CEFF;
  --blue-100: #E5F3FF;
  --blue-50: #F0F8FF;


  /* GREEN */
  --green-900: #2FB801;
  --green-600: #5AD81D;
  --green-500: #77E043;
  --green-300: #C1F2A9;
  --green-100: #E6FADC;
  --green-50: #F2FCED;

  /* RED */
  --red-900: #FD3559;
  --red-600: #FF758E;
  --red-300: #FEA8B7;
  --red-100: #FFE6EA;
  --red-50: #FFF0F2;
  --red-10: #FFF7F9;

  /* ORANGE */
  --orange-900: #FF8400;
  --orange-600: #FF981F;
  --orange-300: #FFCA99;
  --orange-100: #FFE8CC;
  --orange-50: #FFF4EB;

  /* MAGENTA */
  --magenta-900: #EA00FF;

  /* SPECIAL */
  --slate-alt-900: #101525;
  --green-alt-900: #124002;
  --green-alt-700: #315724;
  --orange-alt-900: #591E00;
  --orange-alt-700: #663600;
  --red-alt-900: #4D000E;
  --red-alt-700: #660012;

  /* GRADIENT */
  --pink-blue: linear-gradient(90deg, #77ADFF 31.25%, #FF3FEC 100%);
}

body {
  margin: 0;
  background: var(--grey-white);
  font-family: var(--font-family);
  display: flex;
  justify-content: center;
}

.container {
  box-sizing: border-box;
  width: 100%;
  min-width: var(--container-min-width);
  padding: 0 1.5rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

p {
  margin: 0;
}

/* REPORT CONTAINER ────────────────────────────────────────────────────────────────────────── */
.report-container-row {
  display: flex;
  align-items: flex-start;
  background: var(--grey-white);
  gap: 16px;
}

.report-container {
  display: flex;
  flex-direction: column;
  background: var(--grey-white);
  flex: 1 1 0;
  min-width: 0;
  padding-bottom: 80vh;
}

/* REPORT HEADER ────────────────────────────────────────────────────────────────────────── */
.report-header-container {
  display: flex;
  flex-direction: column;
  padding: 0 0 1.25rem 0;
  position: sticky;
  top: 0;
  z-index: 1000;
  gap: 1rem;
  background: var(--grey-white);
  transition:
    padding 0.32s ease,
    gap 0.32s ease,
    box-shadow 0.32s ease;
  will-change: padding, gap;
}


/* REPORT SUMMARY CARDS ────────────────────────────────────────────────────────────────────────── */
.report-summary-container {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  background: var(--grey-white);
  transition: gap 0.22s ease;
}

.report-summary-card {
  --card-bg: var(--grey-10);
  --card-border: var(--grey-300);

  background: var(--card-bg);
  border-bottom: 1px solid var(--card-border);

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem 1rem 0.75rem 1rem;
  gap: 0.5rem;
  border-radius: 0.5rem;
  min-width: 0;
  transition:
    padding 0.32s ease,
    gap 0.32s ease,
    border-radius 0.32s ease,
    background-color 0.32s ease,
    border-color 0.32s ease;
}

.report-summary-card.wide {
  flex: 5;
}

.report-summary-card.narrow {
  flex: 2.5;
}

.report-summary-card-positive {
  --card-bg: var(--green-50);
  --card-border: var(--green-500);
}

.report-summary-card-warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-summary-card-negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

/* Collapse instantly (no animation) while a nav-item click / tab switch
   re-pins the scroll, so the header height settles in one synchronous reflow
   that browser scroll anchoring can't chase off the target. Restored next
   frame, so manual scrolling keeps its smooth collapse. */
.report-header-container.is-anim-suppressed,
.report-header-container.is-anim-suppressed * {
  transition: none !important;
}

.report-header-container.is-compact {
  padding: 0 0 1.25rem 0;
  gap: 0.75rem;
}

.report-header-container.is-compact .report-summary-container {
  gap: 0.5rem;
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.report-header-container.is-compact .report-summary-card {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  gap: 0.75rem;
  border-radius: 0.5rem;
}

.report-header-container.is-compact .report-summary-card .card-description,
.report-header-container.is-compact .report-summary-card .card-data-container {
  opacity: 0;
  transform: translateY(-0.25rem);
  pointer-events: none;
  display: none;
}

.report-header-container.is-compact .report-summary-card .label {
  flex: 0 0 auto;
  white-space: nowrap;
}

.report-header-container.is-compact .report-summary-card .report-tag {
  flex: 0 0 auto;
  min-width: auto;
  white-space: nowrap;
}

.report-header-container.is-compact .report-summary-card .card-title-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  width: auto;
  min-width: 0;
  flex: 1 1 auto;
}

.report-header-container.is-compact .report-summary-card .card-subtitle {
  opacity: 0;
  transform: translateY(-0.25rem);
  pointer-events: none;
  display: none;
}

.report-header-container.is-compact .report-summary-card .card-title {
  font-size: var(--font-md);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.report-header-container.is-compact .report-summary-card.narrow .card-title-container {
  justify-content: flex-end;
}

.card-title-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  gap: 0.25rem;
  transition: gap 0.22s ease;
}

.card-title,
.card-subtitle,
.card-description {
  width: 100%;
  min-width: 0;
}

.card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-lg);
  color: var(--slate-600);
  width: 100%;
  transition: font-size 0.22s ease, color 0.22s ease;
}

.report-summary-card-positive .card-title {
  color: var(--green-900);
}

.report-summary-card-warning .card-title {
  color: var(--orange-900);
}

.report-summary-card-negative .card-title {
  color: var(--red-900);
}

.card-subtitle {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  transition: opacity 0.18s ease, transform 0.18s ease, color 0.22s ease;
}

.card-description {
  width: 100%;
  min-width: 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  line-height: 1.6;
  color: var(--grey-600);
  transition: opacity 0.18s ease, transform 0.18s ease, color 0.22s ease;
}

.card-data-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-width: 0;
  gap: 0.5rem;
}

.card-data-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
}

.card-data-row-label {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.card-data-row-value {
  font-family: var(--font-family);
  font-weight: var(--font-bold);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.report-summary-card-positive .card-data-row-value {
  color: var(--green-900);
}

.report-summary-card-warning .card-data-row-value {
  color: var(--orange-900);
}

.report-summary-card-negative .card-data-row-value {
  color: var(--red-900);
}

.truncate {
  display: block;
  width: 100%;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* REPORT SECTION ────────────────────────────────────────────────────────────────────────── */
.report-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
  padding: 16px;
  margin: 0 0 20px 0;
  border-radius: 8px;
  border-bottom: 1px solid var(--grey-300);
  background: var(--grey-10);
}

.report-section-header-column {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  gap: 4px;
}

.report-section-card-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}

.report-section-card-container.row {
  flex-direction: row;
  justify-content: space-between;
}

/* CARD GRID — CSS-grid layout. \`grid-template-columns\` is set inline by the
   CardGrid component (via the \`columns\` prop; defaults to items.length). */
.report-card-grid {
  display: grid;
  gap: 1rem;
  width: 100%;
}

/* BASE CARD — shared layout for section / banner / callout cards */
.report-card {
  --card-bg: var(--grey-white);
  --card-border: var(--grey-300);
  background: var(--card-bg);
  border-bottom: 1px solid var(--card-border);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem 1rem 0.75rem 1rem;
  gap: 0.5rem;
  border-radius: 0.5rem;
  width: 100%;
  box-sizing: border-box;
}

.report-card .card-title-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.report-card .card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

/* Inside a card-grid, titles ellipsize to fit fixed-width grid cells. */
.report-card-grid .card-title {
  display: block;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.report-card .card-title-stat {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--green-900);
}

.report-card .card-description,
.report-card .report-text {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  line-height: 1.6;
  color: var(--grey-500);
}

/* BANNER modifier — tighter gap, severity color variants */
.report-card.banner {
  gap: 0.325rem;
}

.report-card.banner.positive {
  --card-bg: var(--green-50);
  --card-border: var(--green-500);
}

.report-card.banner.warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-card.banner.negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

.report-card.banner .card-title.positive {
  color: var(--green-900);
}

.report-card.banner .card-title.warning {
  color: var(--orange-900);
}

.report-card.banner .card-title.negative {
  color: var(--red-900);
}

.report-card.banner .card-description {
  color: var(--grey-600);
}

/* CALLOUT modifier — teal accent, smaller title */
.report-card.callout {
  --card-border: var(--teal-300);
  gap: 0.325rem;
}

.report-card.callout.warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-card.callout.negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

.report-card.callout .card-title {
  font-size: var(--font-sm);
  color: var(--teal-900);
}

.report-card.callout .card-title.warning {
  color: var(--orange-900);
}

.report-card.callout .card-title.negative {
  color: var(--red-900);
}

/* BLOCKQUOTE — verbatim quoted text (e.g. suggested contract language) */
.report-blockquote {
  margin: 0;
  padding: 0.5rem 0.7rem;
  background: var(--grey-50);
  border-left: 2px solid var(--grey-300);
  border-radius: 0 4px 4px 0;
  color: var(--grey-600);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: var(--font-xs);
  line-height: 1.6;
  white-space: pre-wrap;
}

.report-section-subheader {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 4px;
  align-self: stretch;
  padding: 0 2px;
}

.report-section-subheader-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  align-self: stretch;
  padding: 0 2px;
}

.report-section-title {
  display: flex;
  align-items: center;
  align-self: stretch;
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

.report-section-description {
  display: flex;
  align-items: center;
  max-width: 900px;
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.report-section-stat {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

.report-section-stat.positive {
  color: var(--green-900);
}

.report-section-stat.warning {
  color: var(--orange-900);
}

.report-section-stat.negative {
  color: var(--red-900);
}

/* REPORT EXPANDERS ────────────────────────────────────────────────────────────────────────── */
.report-expander {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  font-family: var(--font-family);
  border-bottom: 0.0625rem solid var(--grey-300);
  border-radius: 0.375rem;
  overflow: hidden;
  background: var(--grey-white);
}

/* HEADER BASE */
.report-expander summary {
  list-style: none;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-self: stretch;
  align-items: center;
  padding: 0.5rem 1rem;
  background: var(--grey-white);
  transition: background 0.2s ease;
}

/* HOVER ONLY WHEN CLOSED */
.report-expander:not([open]) summary:hover {
  background: var(--blue-600);
}

/* TEXT + ICON WHITE ON HOVER */
.report-expander:not([open]) summary:hover .report-title,
.report-expander:not([open]) summary:hover .report-subtitle,
.report-expander:not([open]) summary:hover .report-icon {
  color: var(--grey-white);
}

/* BORDER CHANGE */
.report-expander:not([open]):hover {
  border-bottom: 0.0625rem solid var(--blue-600);
}

/* OPEN STATE */
.report-expander[open] summary {
  background: var(--blue-600);
}

/* TEXT + ICON WHITE WHEN OPEN */
.report-expander[open] .report-title,
.report-expander[open] .report-subtitle,
.report-expander[open] .report-icon {
  color: var(--grey-white);
}

/* TEXT BLOCK */
.report-expander-text {
  display: flex;
  flex-direction: column;
}

/* TYPOGRAPHY */
.report-title {
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-bold);
  font-size: var(--font-md);
  color: var(--blue-600);
}

.report-subtitle {
  margin: 0.125rem 0 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

/* ICON */
.report-icon {
  display: flex;
  width: 0.875rem;
  height: 0.875rem;
  flex: 0 0 1rem;
  color: var(--blue-600);
  transition: transform 0.25s ease, color 0.2s ease;
}

.report-icon svg {
  width: 100%;
  height: 100%;
  display: flex;
}

.report-icon svg path {
  fill: currentColor;
}

.report-expander[open] .report-icon {
  transform: rotate(180deg);
}

/* CONTENT */
.report-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  padding: 0.75rem 1rem;
  background: var(--grey-white);
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-regular);
  line-height: 1.5;
  color: var(--grey-600);
}

/* ANIMATION */
.report-expander .report-content {
  transform-origin: top;
  transform: scaleY(0.95);
  opacity: 0;
  min-height: 200px;
  overflow: hidden;
  transition:
    transform 0.2s ease,
    opacity 0.2s ease,
    max-height 0.25s ease;
}

.report-expander[open] .report-content {
  transform: scaleY(1);
  opacity: 1;
  max-height: 31.25rem;
}

/* REMOVE DEFAULT ARROW */
.report-expander summary {
  list-style: none;
}

.report-expander summary::-webkit-details-marker {
  display: none;
}

.report-expander summary::marker {
  content: "";
}

/* REPORT TAGS ────────────────────────────────────────────────────────────────────────── */
.report-tag {
  display: flex;
  padding: 4px 10px 4px 10px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  min-width: 68px;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  color: var(--grey-white);
}

.report-tag-positive {
  background: var(--green-900);
  color: var(--grey-white);
}

.report-tag-warning {
  background: var(--orange-900);
  color: var(--grey-white);
}

.report-tag-negative {
  background: var(--red-900);
  color: var(--grey-white);
}

.report-tag-alt-positive {
  background: var(--green-100);
  color: var(--green-900);
}

.report-tag-alt-warning {
  background: var(--orange-100);
  color: var(--orange-900);
}

.report-tag-alt-negative {
  background: var(--red-100);
  color: var(--red-900);
}

/* LABELS ────────────────────────────────────────────────────────────────────────── */
.label {
  font-family: var(--font-family);
  font-size: var(--label-sm-size);
  font-weight: var(--font-semibold);
  color: var(--teal-900);
  text-transform: uppercase;
}

.label-positive {
  color: var(--green-alt-700);
}

.label-warning {
  color: var(--orange-alt-700);
}

.label-negative {
  color: var(--red-alt-900);
}

/* REPORT UTILITY NAV ────────────────────────────────────────────────────────────────────────── */
.report-utility-nav-container {
  display: flex;
  padding: 4px;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  border-radius: 6px;
  background: var(--blue-100);
}

/* Inactive */
.report-utility-nav-btn {
  display: flex;
  padding: 0.25rem 1rem;
  justify-content: center;
  align-items: center;
  border-radius: 0.25rem;
  border: none;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  color: var(--blue-600);
  cursor: pointer;

  background: var(--blue-100); /* softer inactive bg */
  color: var(--blue-600);

  box-shadow: none;
  transition: background 0.2s ease, color 0.2s ease;
}

/* HOVER (inactive only) */
.report-utility-nav-btn:hover {
  background: var(--grey-white);
}

/* ACTIVE */
.report-utility-nav-btn.active {
  background: var(--grey-white);
  color: var(--blue-600);
  box-shadow: 0 0.0625rem 0.25rem 0 var(--blue-300);
}

/* prevent hover from overriding active */
.report-utility-nav-btn.active:hover {
  background: var(--grey-white);
}

.report-header-container.is-compact .report-utility-nav-container {
  padding: 0.25rem;
}

.report-header-container.is-compact .report-utility-nav-btn {
  padding: 0.375rem 0.875rem;
}

/* REPORT SIDENAV NAV ────────────────────────────────────────────────────────────────────────── */
.report-sidenav-container {
  display: flex;
  padding-right: 16px;
  flex-direction: column;
  align-items: flex-start;
  align-self: flex-start;
  border-right: 1px solid var(--grey-100);
  gap: 4px;
  position: relative;
}

/* Stick the sidenav flush beneath the sticky header so its top items are
   never hidden behind it. The header's height is dynamic — summary-card
   content varies and the header collapses on scroll — so the runtime JS
   publishes its live border-box height as --report-header-height and the
   sidenav tracks it exactly (through the collapse transition too). The
   expanded-offset token is only the pre-JS fallback. */
.report-container-row .report-sidenav-container {
  position: sticky;
  top: var(--report-header-height, var(--report-header-expanded-offset));
}

/* BASE = inactive */
.report-sidenav-btn {
  display: flex;
  width: 240px;
  padding: 0.5rem 0.5rem 0.5rem 0.625rem;
  justify-content: space-between;
  align-items: center;
  border-radius: 0.375rem;
  border: none;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);

  color: var(--grey-500);
  cursor: pointer;

  background: var(--grey-white);
  transition: background 0.2s ease, color 0.2s ease;
}

/* HOVER (inactive only) */
.report-sidenav-btn:not(.active):hover {
  background: var(--grey-10);
}

/* ACTIVE */
.report-sidenav-btn.active {
  background: var(--grey-10);
  color: var(--slate-600);
}

/* keep active stable */
.report-sidenav-btn.active:hover {
  background: var(--grey-10);
}


/* CHEVRON (reuse report-icon) */
.report-sidenav-btn .report-icon {
  margin-left: auto;
  transform: rotate(0deg); /* DOWN */
  transition: transform 0.2s ease;
}

/* ACTIVE → RIGHT */
.report-sidenav-btn.active .report-icon {
  transform: rotate(-90deg);
}

/* SIZE */
.report-sidenav-btn .report-icon {
  width: 0.625rem;
  height: 0.625rem;

  margin-left: auto;
  display: flex;
  align-items: center;

  /* COLOR (inherits from button) */
  color: inherit;

  /* DEFAULT → DOWN */
  transform: rotate(0deg);
  transition: transform 0.2s ease;
}

/* ACTIVE → RIGHT */
.report-sidenav-btn.active .report-icon {
  transform: rotate(-90deg);
}

/* BANNER BOX — now uses .report-card.banner (see BASE CARD above) */

/* SUPPORTING ARGUMENTS BOX ────────────────────────────────────────────────────────────────────────── */
.report-description-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  width: 100%;
  margin: .5rem 0 0 0;
}

.report-description-list-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
}

.report-description-list .label {
  font-size: var(--label-md-size);
}

.report-description-list .card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--slate-600);
}

.report-description-list .card-description {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-500);
}

/* TABLE ────────────────────────────────────────────────────────────────────────── */
.report-table-wrapper {
  width: 100%;
}

.report-table-title {
  margin: 0 0 0.25rem 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--label-sm-size);
  color: var(--slate-900);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  background: var(--grey-white);
  margin: 0.5rem 0;
}

.report-table th,
.report-table td {
  overflow-wrap: anywhere;
}

/* HEADER */
.report-table thead th {
  padding:  0.5rem 0.75rem;
  text-align: left;
  background: var(--grey-100);

  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--slate-600);
}

/* BODY */
.report-table tbody td {
  padding:  0.5rem 0.75rem;

  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

/* NUMERIC */
.report-table-cell-numeric {
  text-align: left;
  white-space: nowrap;
}

/* POSITION */
.report-table-position {
  font-weight: var(--font-semibold);
  color: var(--slate-600);
}

/* CELL / COLUMN SENTIMENT — applies to spans in both <th> and <td>. */
.report-table .positive { color: var(--green-900); }
.report-table .warning  { color: var(--orange-900); }
.report-table .negative { color: var(--red-900); }

/* TWO-LINE CELL — value on top, muted sub underneath. */
.report-table-cell-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.report-table-cell-sub {
  font-size: var(--font-xs);
  font-weight: var(--font-regular);
  color: var(--grey-500);
}

/* PILL CELL — renders the cell value as a rounded badge. */
.report-table-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  background: var(--grey-100);
  color: var(--grey-600);
}
.report-table-pill-positive { background: var(--green-100); color: var(--green-900); }
.report-table-pill-warning  { background: var(--orange-100); color: var(--orange-900); }
.report-table-pill-negative { background: var(--red-100); color: var(--red-900); }

/* ROW VARIANTS — zebra stripes apply unless a row has its own
   background treatment (focus variant, or row sentiment). */
.report-table tbody tr:nth-child(odd):not(.report-table-row-focus):not(.report-table-row-positive):not(.report-table-row-warning):not(.report-table-row-negative):not(.report-table-row-heading):not(.report-table-row-total) td {
  background: var(--grey-white);
}

.report-table tbody tr:nth-child(even):not(.report-table-row-focus):not(.report-table-row-positive):not(.report-table-row-warning):not(.report-table-row-negative):not(.report-table-row-heading):not(.report-table-row-total) td {
  background: var(--grey-10);
}

.report-table-row-focus td {
  background: var(--blue-100);
}

.report-table-row-positive td {
  background: var(--green-100);
}

.report-table-row-warning td {
  background: var(--orange-100);
}

.report-table-row-negative td {
  background: var(--red-100);
}

/* Total row: bolden every cell (td + nested span override the base
   \`.report-table tbody td\` regular weight and \`.report-table-position\`
   semibold weight). */
.report-table tbody tr.report-table-row-total td,
.report-table tbody tr.report-table-row-total td span {
  font-weight: var(--font-semibold);
}

.report-table tbody tr.report-table-row-total td {
  border-top: 2px solid var(--grey-500);
  background: var(--grey-50);
}

/* Heading row: group-label spanning every column. Uses the same visual
   treatment as the standalone \`.report-heading\` block (uppercase, tight
   tracking, slate-900) with a distinct background so it stands apart
   from the zebra-striped data rows. */
.report-table tbody tr.report-table-row-heading td {
  padding: 0.625rem 0.75rem;
  background: var(--blue-50);
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* VALUE EMPHASIS */
.report-table .report-table-cell-numeric {
  color: var(--grey-600);
}

/* HEADING ─────────────────────────────────────────────────────────────────────── */
.report-heading {
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* TEXT ────────────────────────────────────────────────────────────────────────── */
/* Text is a single paragraph — rendered as \`<p class="report-text">\`. */
.report-text {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
  line-height: 1.6;
  width: 100%;
  margin: 0;
}

/* Sentiment tint. Specificity (0,2,0) beats the in-card
   \`.report-card .report-text\` color override (0,2,0) via order. */
.report-text.positive { color: var(--green-900); }
.report-text.warning  { color: var(--orange-900); }
.report-text.negative { color: var(--red-900); }

/* METRIC LIST ────────────────────────────────────────────────────────────────────────── */
.report-metric-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--grey-white);
  border: 0.0625rem solid var(--grey-300);
  border-radius: 0.5rem;
  width: 100%;
}

.report-metric-list-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-family: var(--font-family);
  font-size: var(--font-sm);
}

.report-metric-list-label {
  color: var(--grey-600);
  font-weight: var(--font-regular);
}

.report-metric-list-value {
  color: var(--slate-600);
  font-weight: var(--font-semibold);
}

.report-metric-list-value.positive { color: var(--green-900); }
.report-metric-list-value.warning  { color: var(--orange-900); }
.report-metric-list-value.negative { color: var(--red-900); }

/* STAT GRID ────────────────────────────────────────────────────────────────────────── */
.report-stat-grid {
  display: grid;
  gap: 1rem;
  width: 100%;
}

.report-stat-grid-item {
  display: flex;
  flex-direction: column;
  padding: 0.875rem 1rem;
  background: var(--grey-10);
  border-bottom: 1px solid var(--grey-300);
  border-radius: 0.5rem;
  gap: 0.25rem;
  min-width: 0;
}

.report-stat-grid-label {
  font-family: var(--font-family);
  font-size: var(--label-sm-size);
  font-weight: var(--font-semibold);
  color: var(--grey-600);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.report-stat-grid-value {
  font-family: var(--font-family);
  font-size: var(--font-xl);
  font-weight: var(--font-bold);
  color: var(--slate-600);
  line-height: 1.2;
}

.report-stat-grid-value.positive { color: var(--green-900); }
.report-stat-grid-value.warning  { color: var(--orange-900); }
.report-stat-grid-value.negative { color: var(--red-900); }

.report-stat-grid-sub {
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-regular);
  color: var(--grey-600);
}

.report-stat-grid-badge {
  align-self: flex-start;
  padding: 2px 8px;
  margin-top: 0.25rem;
  border-radius: 4px;
  background: var(--blue-100);
  color: var(--blue-900);
  font-family: var(--font-family);
  font-size: var(--font-xs);
  font-weight: var(--font-semibold);
}

/* WATERFALL CHART ────────────────────────────────────────────────────────────────────────── */
.report-waterfall-wrap {
  position: relative;
  width: 100%;
}

.report-waterfall {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  font-family: var(--font-family);
}

.report-waterfall-gridline {
  stroke: var(--grey-100);
  stroke-width: 1;
}

.report-waterfall-baseline {
  stroke: var(--grey-300);
  stroke-width: 1;
}

.report-waterfall-connector {
  stroke: var(--grey-300);
  stroke-width: 1;
  stroke-dasharray: 4, 3;
}

.report-waterfall-tick {
  fill: var(--grey-500);
  font-size: 10px;
}

.report-waterfall-label {
  fill: var(--grey-600);
  font-size: 11px;
  font-weight: var(--font-semibold);
}

.report-waterfall-label-secondary {
  font-weight: var(--font-regular);
}

.report-waterfall-bar-total            { fill: var(--blue-900); }
.report-waterfall-bar-captured-saving  { fill: var(--green-900); }
.report-waterfall-bar-avoided-exposure { fill: var(--grey-500); }

.report-waterfall-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  margin: 8px 0 0;
  font-size: var(--font-xs);
  color: var(--grey-600);
}
.report-waterfall-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.report-waterfall-legend-swatch {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
}
.report-waterfall-legend-swatch-total            { background: var(--blue-900); }
.report-waterfall-legend-swatch-captured-saving  { background: var(--green-900); }
.report-waterfall-legend-swatch-avoided-exposure { background: var(--grey-500); }

.report-waterfall-value {
  font-size: 11px;
  font-weight: var(--font-bold);
}
.report-waterfall-value-total            { fill: var(--blue-900); }
.report-waterfall-value-captured-saving  { fill: var(--green-900); }
.report-waterfall-value-avoided-exposure { fill: var(--grey-600); }

/* PIE CHART ────────────────────────────────────────────────────────────────────────── */
.report-pie-wrap {
  position: relative;
  width: 100%;
}

.report-pie {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  font-family: var(--font-family);
}

.report-pie-slice {
  /* Fill colours are applied per-element from the JS palette. */
}

.report-pie-leader {
  stroke: var(--grey-400);
  stroke-width: 1;
  fill: none;
}

.report-pie-label {
  fill: var(--slate-900);
  font-size: 11px;
  font-weight: var(--font-semibold);
}

.report-pie-value {
  fill: var(--grey-600);
  font-size: 10px;
  font-weight: var(--font-regular);
}

.report-pie-empty {
  text-align: center;
  color: var(--grey-500);
  font-size: var(--font-sm);
  margin: 24px 0;
}

/* VEGA-LITE CHART ────────────────────────────────────────────────────────────────────────── */
.report-vega-chart {
  width: 100%;
}

.report-vega-chart-svg {
  display: flex;
  justify-content: center;
}

.report-vega-chart-svg svg {
  max-width: 100%;
  height: auto;
}
  
/* Added by Oasis. Embedded under the project header card, the report spans the
   frame's full width: the page around it supplies the gutters. */
.container { min-width: 0; padding: 0 0 1.5rem 0; }
/* The side nav's entries: left aligned, one line each, cut with an ellipsis. */
.report-sidenav-btn > span:first-child { flex: 1; min-width: 0; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
</style>
</head>
<body>
<div class="container"><div class="report-header-container"><div class="report-summary-container"><div class="report-summary-card report-summary-card-positive wide"><div class="label label-positive">Recommendation</div><div class="report-tag report-tag-positive">Usage analysis v2</div><div class="card-title-container"><div class="card-title"><span class="truncate">Renew 10,400 licenses</span></div><div class="card-subtitle"><span class="card-subtitle">8,300 E5 and 2,100 E3, down from 12,400</span></div></div><div class="card-data-container"><div class="card-data-row"><span class="card-data-row-label">Saving at current prices</span><span class="card-data-row-value">$1.9M a year</span></div><div class="card-data-row"><span class="card-data-row-label">Net after the vendor&apos;s response</span><span class="card-data-row-value">$1.6M to $1.7M</span></div><div class="card-data-row"><span class="card-data-row-label">Next-term cost</span><span class="card-data-row-value">$6.6M against $8.5M today</span></div></div></div><div class="report-summary-card report-summary-card-warning wide"><div class="label label-warning">Deadline</div><div class="card-title-container"><div class="card-title"><span class="truncate">Notice by 31 Dec 2026</span></div><div class="card-subtitle"><span class="card-subtitle">62 days from this report; renews 31 Mar 2027</span></div></div><div class="card-description"><p>Owner reviews on <strong>1,150 seats</strong> are due 21 Nov. Seats under review are kept in the count until the owners answer. Business owner: End-user technology.</p></div></div><div class="report-summary-card report-summary-card-positive narrow"><div class="label label-positive">Price lever</div><div class="card-title-container"><div class="card-title"><span class="truncate">$1.0M to $1.1M more</span></div><div class="card-subtitle"><span class="card-subtitle">E5 at $48 against $57; see the playbook</span></div></div></div></div><div class="report-utility-nav-container"><button class="report-utility-nav-btn active" data-tab-id="usage">Usage analysis</button></div></div><div class="report-container-row"><div class="report-sidenav-container"><div class="report-tab-sidenav" data-tab-id="usage"><button class="report-sidenav-btn" data-target="recommendation"><span>Recommendation</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="quantity"><span>How the quantity was set</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="edition-mix"><span>Edition mix</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="range"><span>What is kept, and the range</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="asks"><span>What to ask for</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="vendor-response"><span>Vendor response and the net saving</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="after-signing"><span>After signing</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="evidence"><span>Evidence</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button></div></div><div class="report-container"><div class="report-tab-content" data-tab-id="usage"><div class="report-section" id="recommendation"><div class="report-section-header-column"><div class="report-section-subheader-row"><p class="report-section-title">Recommendation</p><p class="report-section-stat positive">10,400 licenses</p></div><div class="card-description"><p>Microsoft 365 renewal: how much to buy and at which edition. Prepared 28 Oct 2026, usage to 24 Oct 2026.</p></div></div><div class="report-section-card-container"><div class="report-card callout"><div class="card-title-row"><span class="card-title">Basis</span></div><div class="card-description"><p>Based on usage to 24 Oct 2026; <strong>94% of users matched</strong> to the contract. Seats under owner review (1,150) are kept in the count.</p></div></div></div><div class="report-section-card-container"><p class="report-text">Renew <strong>10,400 licenses</strong>, down from 12,400: 8,300 E5 and 2,100 E3. At current prices that is $6.6M a year against $8.5M today, a saving of <strong>$1.9M</strong>; net after the vendor&apos;s expected response, <strong>$1.6M to $1.7M</strong>. The price lever adds $1.0M on top (see the playbook), for a target of $2.9M a year.</p></div><div class="report-section-card-container"><div class="report-stat-grid" style="grid-template-columns: repeat(4, minmax(0, 1fr));"><div class="report-stat-grid-item"><span class="report-stat-grid-label">Licenses next term</span><span class="report-stat-grid-value">10,400</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Saving a year</span><span class="report-stat-grid-value positive">$1.9M</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Days to notice</span><span class="report-stat-grid-value warning">62</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Owner reviews due</span><span class="report-stat-grid-value">21 Nov</span></div></div></div></div><div class="report-section" id="quantity"><div class="report-section-subheader-row"><p class="report-section-title">How the quantity was set</p></div><div class="report-section-card-container"><p class="report-text">From licenses bought to licenses needed: what was never assigned and what departed staff still hold goes now; what the function owners confirmed as not needed goes at renewal; the headcount plan adds back the hires. Seats inactive but <strong>still under owner review stay in the count</strong>.</p></div><div class="report-section-card-container"><div class="report-table-wrapper"><p class="report-table-title">From licenses bought to licenses needed</p><table class="report-table"><colgroup><col><col style="width: 20%"><col></colgroup><thead><tr><th><span>Step</span></th><th class="report-table-cell-numeric"><span>Licenses</span></th><th><span>Source</span></th></tr></thead><tbody><tr class="report-table-row-total"><td><span class="report-table-position">Bought</span></td><td class="report-table-cell-numeric"><span>12,400</span></td><td><span>Enterprise Agreement, 12 Sep 2026 extraction</span></td></tr><tr><td><span class="report-table-position">Never assigned</span></td><td class="report-table-cell-numeric"><span>−550</span></td><td><span>M365 admin center, 25 Oct 2026</span></td></tr><tr><td><span class="report-table-position">Held by departed staff</span></td><td class="report-table-cell-numeric"><span>−560</span></td><td><span>Workday headcount, 20 Oct 2026</span></td></tr><tr><td><span class="report-table-position">Inactive and confirmed not needed by the owner</span></td><td class="report-table-cell-numeric"><span>−1,240</span></td><td><span>Owner reviews confirmed 24 Oct (three functions)</span></td></tr><tr><td><span class="report-table-position">Planned hires</span></td><td class="report-table-cell-numeric"><span>+350</span></td><td><span>Headcount plan to 31 Mar 2028</span></td></tr><tr class="report-table-row-focus"><td><span class="report-table-position">Needed next term</span></td><td class="report-table-cell-numeric"><span>10,400</span></td><td><span>8,300 E5 and 2,100 E3</span></td></tr></tbody></table></div></div></div><div class="report-section" id="edition-mix"><div class="report-section-subheader-row"><p class="report-section-title">Edition mix</p><p class="report-section-stat">2,100 fit E3</p></div><div class="report-section-card-container"><p class="report-text"><strong>2,100 active users</strong> used none of the E5-only features in the 90 days to 24 Oct 2026 (Defender and Purview, Teams Phone, audio conferencing, Power BI Pro, advanced eDiscovery). They fit E3 at $36 a user a month instead of $57. The remaining 6,800 used at least one E5-only feature.</p></div><div class="report-section-card-container"><div class="report-table-wrapper"><p class="report-table-title">By function</p><table class="report-table"><thead><tr><th><span>Function</span></th><th class="report-table-cell-numeric"><span>Active users</span></th><th class="report-table-cell-numeric"><span>Fit E3</span></th><th class="report-table-cell-numeric"><span>Keep E5</span></th><th class="report-table-cell-numeric"><span>Awaiting review</span></th><th><span>Review status</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Client services</span></td><td class="report-table-cell-numeric"><span>2,360</span></td><td class="report-table-cell-numeric"><span>900</span></td><td class="report-table-cell-numeric"><span>1,460</span></td><td class="report-table-cell-numeric"><span>510</span></td><td><span class="warning">Due 21 Nov</span></td></tr><tr><td><span class="report-table-position">Advisory and wealth</span></td><td class="report-table-cell-numeric"><span>2,050</span></td><td class="report-table-cell-numeric"><span>300</span></td><td class="report-table-cell-numeric"><span>1,750</span></td><td class="report-table-cell-numeric"><span>0</span></td><td><span class="positive">Confirmed 24 Oct</span></td></tr><tr><td><span class="report-table-position">Technology</span></td><td class="report-table-cell-numeric"><span>2,300</span></td><td class="report-table-cell-numeric"><span>100</span></td><td class="report-table-cell-numeric"><span>2,200</span></td><td class="report-table-cell-numeric"><span>0</span></td><td><span class="positive">Confirmed 24 Oct</span></td></tr><tr><td><span class="report-table-position">Operations</span></td><td class="report-table-cell-numeric"><span>1,500</span></td><td class="report-table-cell-numeric"><span>600</span></td><td class="report-table-cell-numeric"><span>900</span></td><td class="report-table-cell-numeric"><span>640</span></td><td><span class="warning">Due 21 Nov</span></td></tr><tr><td><span class="report-table-position">Corporate functions</span></td><td class="report-table-cell-numeric"><span>690</span></td><td class="report-table-cell-numeric"><span>200</span></td><td class="report-table-cell-numeric"><span>490</span></td><td class="report-table-cell-numeric"><span>0</span></td><td><span class="positive">Confirmed 24 Oct</span></td></tr><tr class="report-table-row-total"><td><span class="report-table-position">Total</span></td><td class="report-table-cell-numeric"><span>8,900</span></td><td class="report-table-cell-numeric"><span>2,100</span></td><td class="report-table-cell-numeric"><span>6,800</span></td><td class="report-table-cell-numeric"><span>1,150</span></td><td><span></span></td></tr></tbody></table></div></div><div class="report-section-card-container"><p class="report-text">The 8,300 E5 is the 6,800 active E5 users, plus the 1,150 seats under review, plus the 350 planned hires.</p></div></div><div class="report-section" id="range"><div class="report-section-subheader-row"><p class="report-section-title">What is kept, and the range</p></div><div class="report-section-card-container"><div class="report-metric-list"><div class="report-metric-list-row"><span class="report-metric-list-label">All 1,150 reviewed seats released</span><span class="report-metric-list-value">7,150 E5</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">As recommended</span><span class="report-metric-list-value positive">8,300 E5</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">All kept, plus 300 hired ahead of plan</span><span class="report-metric-list-value">8,600 E5</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Each 100 seats released</span><span class="report-metric-list-value">$68K a year</span></div></div></div><div class="report-section-card-container"><p class="report-text">The E3 count (2,100) does not move with the reviews: those users are active, only their edition changes. The two outstanding reviews (Client services 510, Operations 640) decide where in the range the renewal lands.</p></div></div><div class="report-section" id="asks"><div class="report-section-subheader-row"><p class="report-section-title">What to ask for</p></div><div class="report-section-card-container"><div class="report-card banner negative"><div class="card-title-row"><span class="card-title negative">Reduce the quantity to 10,400 at renewal</span><span class="report-tag report-tag-negative">Quantity</span></div><div class="card-description"><p>8,300 E5 and 2,100 E3, with the reviewed seats settled before the order.</p></div></div><div class="report-card banner negative"><div class="card-title-row"><span class="card-title negative">A right to reduce during the term</span><span class="report-tag report-tag-negative">Terms</span></div><div class="card-description"><p>The contract review found no right to reduce and no price protection. Ask for 10% a year without penalty.</p></div></div><div class="report-card banner warning"><div class="card-title-row"><span class="card-title warning">An E3 mix</span><span class="report-tag report-tag-warning">Edition</span></div><div class="card-description"><p>2,100 users at $36 instead of $57, with the right to move users between editions at the anniversary.</p></div></div><div class="report-card banner warning"><div class="card-title-row"><span class="card-title warning">Price E5 at the peer target</span><span class="report-tag report-tag-warning">Price</span></div><div class="card-description"><p>$48.00 against $57.00 today; the peer median is $49.50 over 23 agreements.</p></div></div><div class="report-card banner warning"><div class="card-title-row"><span class="card-title warning">Protection against the announced list rise</span><span class="report-tag report-tag-warning">Terms</span></div><div class="card-description"><p>Microsoft raises list prices 5% from 1 Apr 2027, the day after the renewal. Fix the price for the term.</p></div></div></div></div><div class="report-section" id="vendor-response"><div class="report-section-subheader-row"><p class="report-section-title">Vendor response and the net saving</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col><col style="width: 18%"><col style="width: 22%"></colgroup><thead><tr><th><span>Lever</span></th><th class="report-table-cell-numeric"><span>Gross</span></th><th class="report-table-cell-numeric"><span>Net after response</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Right-size: quantity and edition</span></td><td class="report-table-cell-numeric"><span>$1.9M</span></td><td class="report-table-cell-numeric"><span>$1.6M to $1.7M</span></td></tr><tr><td><span class="report-table-position">Price: E5 at $48</span></td><td class="report-table-cell-numeric"><span>$1.1M</span></td><td class="report-table-cell-numeric"><span>$1.0M</span></td></tr><tr class="report-table-row-total"><td><span class="report-table-position">Total</span></td><td class="report-table-cell-numeric"><span>$3.0M</span></td><td class="report-table-cell-numeric"><span class="positive">$2.5M to $2.7M</span></td></tr></tbody></table></div><div class="report-section-card-container"><div class="report-description-list"><span class="label">Expected vendor moves</span><div class="report-description-list-content"><div class="card-title"><span class="truncate">Re-pricing of the retained E5:</span></div><div class="card-description"><p>The per-user discount shrinks as the quantity falls; part of the right-size saving comes back as price.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">Loss of a discount tier:</span></div><div class="card-description"><p>Below 10,000 E5 the agreement drops a tier; the E3 mix keeps the total above 10,000 licenses.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">Bundling Copilot:</span></div><div class="card-description"><p>Expect an offer to fold Copilot into the renewal to hold the total; the 460 inactive Copilot seats say no.</p></div></div></div></div><div class="report-section-card-container"><p class="report-text">Figures are illustrative; the playbook carries the negotiation plan and the rounds.</p></div></div><div class="report-section" id="after-signing"><div class="report-section-subheader-row"><p class="report-section-title">After signing</p></div><div class="report-section-card-container"><p class="report-text">Check the first invoice, due 30 Jun 2027, against the signed quantities and unit prices.</p></div><div class="report-section-card-container"><p class="report-text">Re-run this analysis 90 days after the renewal date. The owner reviews become the baseline for the next term&apos;s reduction right.</p></div></div><div class="report-section" id="evidence"><div class="report-section-subheader-row"><p class="report-section-title">Evidence</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 30%"><col style="width: 20%"><col></colgroup><thead><tr><th><span>Source</span></th><th><span>As of</span></th><th><span>Supports</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Microsoft Entra sign-ins</span></td><td><span>29 Oct 2026, daily</span></td><td><span>Last sign-in and days inactive per user</span></td></tr><tr><td><span class="report-table-position">Microsoft 365 admin center usage reports</span></td><td><span>25 Oct 2026</span></td><td><span>Licenses assigned, E5 feature use per user (94% matched to the contract)</span></td></tr><tr><td><span class="report-table-position">Workday headcount</span></td><td><span>20 Oct 2026</span></td><td><span>Employment status, departures, planned hires</span></td></tr><tr><td><span class="report-table-position">Coupa AP</span></td><td><span>24 Oct 2026</span></td><td><span>Invoices paid; unit price $57 a user a month</span></td></tr><tr><td><span class="report-table-position">Enterprise Agreement extraction</span></td><td><span>12 Sep 2026</span></td><td><span>Quantities, term, no right to reduce, no price protection</span></td></tr><tr><td><span class="report-table-position">Market data and playbook</span></td><td><span>28 Oct 2026</span></td><td><span>Peer median $49.50 over 23 agreements; target $48.00</span></td></tr></tbody></table></div></div></div></div></div></div>
<script>
  const tabButtons = document.querySelectorAll(".report-utility-nav-btn");
  const header = document.querySelector(".report-header-container");

  // The sidenav sticks flush beneath the header (see styles.css). The header's
  // height varies with its content and shrinks as it collapses, so publish its
  // live height and keep the sidenav in sync — ResizeObserver fires through the
  // collapse transition too, so the sidenav tracks the header frame by frame.
  function publishHeaderHeight() {
    if (!header) return;
    document.documentElement.style.setProperty(
      "--report-header-height",
      header.offsetHeight + "px"
    );
  }

  if (header && "ResizeObserver" in window) {
    new ResizeObserver(publishHeaderHeight).observe(header);
  }
  publishHeaderHeight();

  // Measure the header's expanded and compact heights so the collapse
  // thresholds adapt to the report (summary-card content varies the height).
  // Toggling with the transition suppressed makes the probe instant and
  // invisible. Re-measured on resize / once webfonts settle, since both shift
  // the height.
  let expandedHeaderH = 0;
  let compactHeaderH = 0;
  function measureHeaderHeights() {
    if (!header) return;
    const wasCompact = header.classList.contains("is-compact");
    header.classList.add("is-anim-suppressed");
    header.classList.remove("is-compact");
    expandedHeaderH = header.offsetHeight;
    header.classList.add("is-compact");
    compactHeaderH = header.offsetHeight;
    header.classList.toggle("is-compact", wasCompact);
    header.classList.remove("is-anim-suppressed");
  }
  measureHeaderHeights();
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(measureHeaderHeights);
  }

  // Header collapse controller. The header is expanded at the very top of the
  // report and compact once you've scrolled down into the content. Run on
  // every scroll and after programmatic scrolls (nav-item clicks, tab
  // switches) so both paths behave identically.
  //
  // Driven by scrollY with hysteresis: expand near the very top, collapse once
  // you've scrolled past the header. The catch is that collapsing shrinks the
  // header by \`delta\`, and browser scroll anchoring then shifts scrollY by that
  // same delta to keep your place — so the gap between the two thresholds must
  // exceed \`delta\`, otherwise the post-toggle anchoring shift lands past the
  // opposite threshold and the header flickers. We collapse \`delta\` + margin
  // down and expand within \`margin\` of the top, giving a band of delta + margin.
  const HEADER_MARGIN_PX = 32;
  function updateHeaderCompact() {
    if (!header) return;
    const delta = Math.max(0, expandedHeaderH - compactHeaderH);
    const y = window.pageYOffset;
    if (y <= HEADER_MARGIN_PX) {
      header.classList.remove("is-compact");
    } else if (y >= delta + 2 * HEADER_MARGIN_PX) {
      header.classList.add("is-compact");
    }
  }

  // Scroll a section flush beneath the header (used by nav-item clicks and tab
  // switches). The offset depends on the header height, but landing the scroll
  // *also* changes whether the header is collapsed — and an animated collapse
  // reflows content under the new scroll position, which browser scroll
  // anchoring then chases, drifting us onto the previous section. So: jump,
  // settle the header into its destination state with the collapse animation
  // suppressed (one synchronous reflow, nothing for anchoring to track), then
  // re-pin against the now-final header height. Animation is restored next
  // frame so manual scrolling keeps its smooth collapse.
  function scrollSectionToTop(target) {
    if (!target) return;
    const pin = () =>
      Math.max(0, target.getBoundingClientRect().top + window.pageYOffset - getHeaderOffset());
    window.scrollTo({ top: pin(), behavior: "auto" });
    if (header) header.classList.add("is-anim-suppressed");
    updateHeaderCompact();
    window.scrollTo({ top: pin(), behavior: "auto" });
    if (header) {
      requestAnimationFrame(() => header.classList.remove("is-anim-suppressed"));
    }
  }

  function getHeaderOffset() {
    return header ? header.offsetHeight : 0;
  }

  function updateActiveSidenav() {
    const activePane = document.querySelector('.report-tab-content:not([hidden])');
    if (!activePane) return;

    const sections = activePane.querySelectorAll('.report-section');
    if (!sections.length) return;

    let activeId = sections[0].id;
    const headerOffset = getHeaderOffset() + 24;

    sections.forEach(section => {
      const rect = section.getBoundingClientRect();
      if (rect.top <= headerOffset) {
        activeId = section.id;
      }
    });

    document
      .querySelectorAll('.report-tab-sidenav:not([hidden]) .report-sidenav-btn')
      .forEach(button => {
        button.classList.toggle('active', button.dataset.target === activeId);
      });
  }

  function onScroll() {
    updateHeaderCompact();
    updateActiveSidenav();
  }

  function activateTab(tabId) {
    tabButtons.forEach(button => button.classList.toggle("active", button.dataset.tabId === tabId));

    document.querySelectorAll(".report-tab-sidenav").forEach(element => {
      element.hidden = element.dataset.tabId !== tabId;
    });

    document.querySelectorAll(".report-tab-content").forEach(element => {
      element.hidden = element.dataset.tabId !== tabId;
    });

    document.querySelectorAll(".report-sidenav-btn").forEach(button => button.classList.remove("active"));

    const firstSide = document.querySelector('.report-tab-sidenav:not([hidden]) .report-sidenav-btn');
    if (firstSide) firstSide.classList.add('active');

    const firstSection = document.querySelector('.report-tab-content:not([hidden]) .report-section');
    scrollSectionToTop(firstSection);

    updateActiveSidenav();
  }

  tabButtons.forEach(button => {
    button.addEventListener("click", () => activateTab(button.dataset.tabId));
  });

  const firstExpander = document.querySelector(".report-expander");
  if (firstExpander) firstExpander.setAttribute("open", "");

  const firstSideOnLoad = document.querySelector('.report-tab-sidenav:not([hidden]) .report-sidenav-btn');
  if (firstSideOnLoad) firstSideOnLoad.classList.add('active');

  document.querySelectorAll(".report-sidenav-btn").forEach(button => {
    button.addEventListener("click", () => {
      const target = document.getElementById(button.dataset.target);
      if (!target) return;

      document.querySelectorAll('.report-tab-sidenav:not([hidden]) .report-sidenav-btn').forEach(sideButton => {
        sideButton.classList.remove('active');
      });
      button.classList.add('active');

      scrollSectionToTop(target);
      updateActiveSidenav();
    });
  });

  updateHeaderCompact();
  updateActiveSidenav();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
    measureHeaderHeights();
    onScroll();
  });

    // Fallback colgroup for tables whose schema didn't declare column
    // widths. If the server-side renderer already emitted a <colgroup>
    // (because at least one column in the JSON had a \`width\`), respect
    // it and bail — don't strip and replace.
  document.querySelectorAll('.report-table').forEach((table) => {
    if (table.querySelector(':scope > colgroup')) return;

    const firstRow = table.querySelector('thead tr, tbody tr');
    if (!firstRow) return;

    const columnCount = firstRow.children.length;

    const colgroup = document.createElement('colgroup');

    let widths = [];

    if (columnCount === 2) {
      widths = ['13.75rem', 'auto']; // 220px / rest
    } else if (columnCount === 3) {
      widths = ['13.75rem', '13.125rem', 'auto']; // 220 / 210 / rest
    } else if (columnCount === 4) {
      widths = ['13.75rem', '13.125rem', '13.125rem', 'auto']; // 220 / 210 / 210 / rest
    } else if (columnCount === 5) {
      widths = ['13.75rem', '13.125rem', '12rem', '12rem', '10rem'];
    } else {
      // fallback: first two anchored, rest auto
      widths = Array.from({ length: columnCount }, (_, i) => {
        if (i === 0) return '13.75rem';
        if (i === 1) return '13.125rem';
        return 'auto';
      });
    }

    widths.forEach((width) => {
      const col = document.createElement('col');
      if (width !== 'auto') {
        col.style.width = width;
      }
      colgroup.appendChild(col);
    });

    table.insertBefore(colgroup, table.firstChild);
  });
</script>
</body>
</html>

`;

export const USAGE_V3 = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Report</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap" rel="stylesheet">
  <style>
/* GLOBAL ────────────────────────────────────────────────────────────────────────── */
:root {
  --container-min-width: 1080px;

  --report-header-expanded-offset: 17.125rem;

  /* FONT FAMILY */
  --font-family: "Inter", sans-serif;

  /* FONT WEIGHTS */
  --font-regular: 400;
  --font-semibold: 600;
  --font-bold: 700;

  /* FONT SIZES */
  --font-xs: 0.625rem;
  --font-sm: 0.75rem;
  --font-md: 0.875rem;
  --font-default: 1rem;
  --font-lg: 1.25rem;
  --font-xl: 1.5rem;

  /* LABEL */
  --label-sm-size: 0.625rem;
  --label-md-size: 0.75rem;

  /* SLATE */
  --slate-900: #141E2E;
  --slate-600: #2F466B;

  /* TEAL */
  --teal-900: #00B2FE;
  --teal-300: #75D6FF;

  /* GREY */
  --grey-900: #404040;
  --grey-600: #666666;
  --grey-500: #999999;
  --grey-400: #BFBFBF;
  --grey-300: #D9D9D9;
  --grey-100: #F0F0F0;
  --grey-50: #F5F5F5;
  --grey-10: #FAFAFA;
  --grey-white: #FFFFFF;

  /* BLUE */
  --blue-900: #006ACC;
  --blue-600: #1890FF;
  --blue-300: #99CEFF;
  --blue-100: #E5F3FF;
  --blue-50: #F0F8FF;


  /* GREEN */
  --green-900: #2FB801;
  --green-600: #5AD81D;
  --green-500: #77E043;
  --green-300: #C1F2A9;
  --green-100: #E6FADC;
  --green-50: #F2FCED;

  /* RED */
  --red-900: #FD3559;
  --red-600: #FF758E;
  --red-300: #FEA8B7;
  --red-100: #FFE6EA;
  --red-50: #FFF0F2;
  --red-10: #FFF7F9;

  /* ORANGE */
  --orange-900: #FF8400;
  --orange-600: #FF981F;
  --orange-300: #FFCA99;
  --orange-100: #FFE8CC;
  --orange-50: #FFF4EB;

  /* MAGENTA */
  --magenta-900: #EA00FF;

  /* SPECIAL */
  --slate-alt-900: #101525;
  --green-alt-900: #124002;
  --green-alt-700: #315724;
  --orange-alt-900: #591E00;
  --orange-alt-700: #663600;
  --red-alt-900: #4D000E;
  --red-alt-700: #660012;

  /* GRADIENT */
  --pink-blue: linear-gradient(90deg, #77ADFF 31.25%, #FF3FEC 100%);
}

body {
  margin: 0;
  background: var(--grey-white);
  font-family: var(--font-family);
  display: flex;
  justify-content: center;
}

.container {
  box-sizing: border-box;
  width: 100%;
  min-width: var(--container-min-width);
  padding: 0 1.5rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

p {
  margin: 0;
}

/* REPORT CONTAINER ────────────────────────────────────────────────────────────────────────── */
.report-container-row {
  display: flex;
  align-items: flex-start;
  background: var(--grey-white);
  gap: 16px;
}

.report-container {
  display: flex;
  flex-direction: column;
  background: var(--grey-white);
  flex: 1 1 0;
  min-width: 0;
  padding-bottom: 80vh;
}

/* REPORT HEADER ────────────────────────────────────────────────────────────────────────── */
.report-header-container {
  display: flex;
  flex-direction: column;
  padding: 0 0 1.25rem 0;
  position: sticky;
  top: 0;
  z-index: 1000;
  gap: 1rem;
  background: var(--grey-white);
  transition:
    padding 0.32s ease,
    gap 0.32s ease,
    box-shadow 0.32s ease;
  will-change: padding, gap;
}


/* REPORT SUMMARY CARDS ────────────────────────────────────────────────────────────────────────── */
.report-summary-container {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  background: var(--grey-white);
  transition: gap 0.22s ease;
}

.report-summary-card {
  --card-bg: var(--grey-10);
  --card-border: var(--grey-300);

  background: var(--card-bg);
  border-bottom: 1px solid var(--card-border);

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem 1rem 0.75rem 1rem;
  gap: 0.5rem;
  border-radius: 0.5rem;
  min-width: 0;
  transition:
    padding 0.32s ease,
    gap 0.32s ease,
    border-radius 0.32s ease,
    background-color 0.32s ease,
    border-color 0.32s ease;
}

.report-summary-card.wide {
  flex: 5;
}

.report-summary-card.narrow {
  flex: 2.5;
}

.report-summary-card-positive {
  --card-bg: var(--green-50);
  --card-border: var(--green-500);
}

.report-summary-card-warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-summary-card-negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

/* Collapse instantly (no animation) while a nav-item click / tab switch
   re-pins the scroll, so the header height settles in one synchronous reflow
   that browser scroll anchoring can't chase off the target. Restored next
   frame, so manual scrolling keeps its smooth collapse. */
.report-header-container.is-anim-suppressed,
.report-header-container.is-anim-suppressed * {
  transition: none !important;
}

.report-header-container.is-compact {
  padding: 0 0 1.25rem 0;
  gap: 0.75rem;
}

.report-header-container.is-compact .report-summary-container {
  gap: 0.5rem;
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.report-header-container.is-compact .report-summary-card {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  gap: 0.75rem;
  border-radius: 0.5rem;
}

.report-header-container.is-compact .report-summary-card .card-description,
.report-header-container.is-compact .report-summary-card .card-data-container {
  opacity: 0;
  transform: translateY(-0.25rem);
  pointer-events: none;
  display: none;
}

.report-header-container.is-compact .report-summary-card .label {
  flex: 0 0 auto;
  white-space: nowrap;
}

.report-header-container.is-compact .report-summary-card .report-tag {
  flex: 0 0 auto;
  min-width: auto;
  white-space: nowrap;
}

.report-header-container.is-compact .report-summary-card .card-title-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  width: auto;
  min-width: 0;
  flex: 1 1 auto;
}

.report-header-container.is-compact .report-summary-card .card-subtitle {
  opacity: 0;
  transform: translateY(-0.25rem);
  pointer-events: none;
  display: none;
}

.report-header-container.is-compact .report-summary-card .card-title {
  font-size: var(--font-md);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.report-header-container.is-compact .report-summary-card.narrow .card-title-container {
  justify-content: flex-end;
}

.card-title-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  gap: 0.25rem;
  transition: gap 0.22s ease;
}

.card-title,
.card-subtitle,
.card-description {
  width: 100%;
  min-width: 0;
}

.card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-lg);
  color: var(--slate-600);
  width: 100%;
  transition: font-size 0.22s ease, color 0.22s ease;
}

.report-summary-card-positive .card-title {
  color: var(--green-900);
}

.report-summary-card-warning .card-title {
  color: var(--orange-900);
}

.report-summary-card-negative .card-title {
  color: var(--red-900);
}

.card-subtitle {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  transition: opacity 0.18s ease, transform 0.18s ease, color 0.22s ease;
}

.card-description {
  width: 100%;
  min-width: 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  line-height: 1.6;
  color: var(--grey-600);
  transition: opacity 0.18s ease, transform 0.18s ease, color 0.22s ease;
}

.card-data-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-width: 0;
  gap: 0.5rem;
}

.card-data-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
}

.card-data-row-label {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.card-data-row-value {
  font-family: var(--font-family);
  font-weight: var(--font-bold);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.report-summary-card-positive .card-data-row-value {
  color: var(--green-900);
}

.report-summary-card-warning .card-data-row-value {
  color: var(--orange-900);
}

.report-summary-card-negative .card-data-row-value {
  color: var(--red-900);
}

.truncate {
  display: block;
  width: 100%;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* REPORT SECTION ────────────────────────────────────────────────────────────────────────── */
.report-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
  padding: 16px;
  margin: 0 0 20px 0;
  border-radius: 8px;
  border-bottom: 1px solid var(--grey-300);
  background: var(--grey-10);
}

.report-section-header-column {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  gap: 4px;
}

.report-section-card-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}

.report-section-card-container.row {
  flex-direction: row;
  justify-content: space-between;
}

/* CARD GRID — CSS-grid layout. \`grid-template-columns\` is set inline by the
   CardGrid component (via the \`columns\` prop; defaults to items.length). */
.report-card-grid {
  display: grid;
  gap: 1rem;
  width: 100%;
}

/* BASE CARD — shared layout for section / banner / callout cards */
.report-card {
  --card-bg: var(--grey-white);
  --card-border: var(--grey-300);
  background: var(--card-bg);
  border-bottom: 1px solid var(--card-border);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem 1rem 0.75rem 1rem;
  gap: 0.5rem;
  border-radius: 0.5rem;
  width: 100%;
  box-sizing: border-box;
}

.report-card .card-title-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.report-card .card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

/* Inside a card-grid, titles ellipsize to fit fixed-width grid cells. */
.report-card-grid .card-title {
  display: block;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.report-card .card-title-stat {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--green-900);
}

.report-card .card-description,
.report-card .report-text {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  line-height: 1.6;
  color: var(--grey-500);
}

/* BANNER modifier — tighter gap, severity color variants */
.report-card.banner {
  gap: 0.325rem;
}

.report-card.banner.positive {
  --card-bg: var(--green-50);
  --card-border: var(--green-500);
}

.report-card.banner.warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-card.banner.negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

.report-card.banner .card-title.positive {
  color: var(--green-900);
}

.report-card.banner .card-title.warning {
  color: var(--orange-900);
}

.report-card.banner .card-title.negative {
  color: var(--red-900);
}

.report-card.banner .card-description {
  color: var(--grey-600);
}

/* CALLOUT modifier — teal accent, smaller title */
.report-card.callout {
  --card-border: var(--teal-300);
  gap: 0.325rem;
}

.report-card.callout.warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-card.callout.negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

.report-card.callout .card-title {
  font-size: var(--font-sm);
  color: var(--teal-900);
}

.report-card.callout .card-title.warning {
  color: var(--orange-900);
}

.report-card.callout .card-title.negative {
  color: var(--red-900);
}

/* BLOCKQUOTE — verbatim quoted text (e.g. suggested contract language) */
.report-blockquote {
  margin: 0;
  padding: 0.5rem 0.7rem;
  background: var(--grey-50);
  border-left: 2px solid var(--grey-300);
  border-radius: 0 4px 4px 0;
  color: var(--grey-600);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: var(--font-xs);
  line-height: 1.6;
  white-space: pre-wrap;
}

.report-section-subheader {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 4px;
  align-self: stretch;
  padding: 0 2px;
}

.report-section-subheader-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  align-self: stretch;
  padding: 0 2px;
}

.report-section-title {
  display: flex;
  align-items: center;
  align-self: stretch;
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

.report-section-description {
  display: flex;
  align-items: center;
  max-width: 900px;
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.report-section-stat {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

.report-section-stat.positive {
  color: var(--green-900);
}

.report-section-stat.warning {
  color: var(--orange-900);
}

.report-section-stat.negative {
  color: var(--red-900);
}

/* REPORT EXPANDERS ────────────────────────────────────────────────────────────────────────── */
.report-expander {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  font-family: var(--font-family);
  border-bottom: 0.0625rem solid var(--grey-300);
  border-radius: 0.375rem;
  overflow: hidden;
  background: var(--grey-white);
}

/* HEADER BASE */
.report-expander summary {
  list-style: none;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-self: stretch;
  align-items: center;
  padding: 0.5rem 1rem;
  background: var(--grey-white);
  transition: background 0.2s ease;
}

/* HOVER ONLY WHEN CLOSED */
.report-expander:not([open]) summary:hover {
  background: var(--blue-600);
}

/* TEXT + ICON WHITE ON HOVER */
.report-expander:not([open]) summary:hover .report-title,
.report-expander:not([open]) summary:hover .report-subtitle,
.report-expander:not([open]) summary:hover .report-icon {
  color: var(--grey-white);
}

/* BORDER CHANGE */
.report-expander:not([open]):hover {
  border-bottom: 0.0625rem solid var(--blue-600);
}

/* OPEN STATE */
.report-expander[open] summary {
  background: var(--blue-600);
}

/* TEXT + ICON WHITE WHEN OPEN */
.report-expander[open] .report-title,
.report-expander[open] .report-subtitle,
.report-expander[open] .report-icon {
  color: var(--grey-white);
}

/* TEXT BLOCK */
.report-expander-text {
  display: flex;
  flex-direction: column;
}

/* TYPOGRAPHY */
.report-title {
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-bold);
  font-size: var(--font-md);
  color: var(--blue-600);
}

.report-subtitle {
  margin: 0.125rem 0 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

/* ICON */
.report-icon {
  display: flex;
  width: 0.875rem;
  height: 0.875rem;
  flex: 0 0 1rem;
  color: var(--blue-600);
  transition: transform 0.25s ease, color 0.2s ease;
}

.report-icon svg {
  width: 100%;
  height: 100%;
  display: flex;
}

.report-icon svg path {
  fill: currentColor;
}

.report-expander[open] .report-icon {
  transform: rotate(180deg);
}

/* CONTENT */
.report-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  padding: 0.75rem 1rem;
  background: var(--grey-white);
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-regular);
  line-height: 1.5;
  color: var(--grey-600);
}

/* ANIMATION */
.report-expander .report-content {
  transform-origin: top;
  transform: scaleY(0.95);
  opacity: 0;
  min-height: 200px;
  overflow: hidden;
  transition:
    transform 0.2s ease,
    opacity 0.2s ease,
    max-height 0.25s ease;
}

.report-expander[open] .report-content {
  transform: scaleY(1);
  opacity: 1;
  max-height: 31.25rem;
}

/* REMOVE DEFAULT ARROW */
.report-expander summary {
  list-style: none;
}

.report-expander summary::-webkit-details-marker {
  display: none;
}

.report-expander summary::marker {
  content: "";
}

/* REPORT TAGS ────────────────────────────────────────────────────────────────────────── */
.report-tag {
  display: flex;
  padding: 4px 10px 4px 10px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  min-width: 68px;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  color: var(--grey-white);
}

.report-tag-positive {
  background: var(--green-900);
  color: var(--grey-white);
}

.report-tag-warning {
  background: var(--orange-900);
  color: var(--grey-white);
}

.report-tag-negative {
  background: var(--red-900);
  color: var(--grey-white);
}

.report-tag-alt-positive {
  background: var(--green-100);
  color: var(--green-900);
}

.report-tag-alt-warning {
  background: var(--orange-100);
  color: var(--orange-900);
}

.report-tag-alt-negative {
  background: var(--red-100);
  color: var(--red-900);
}

/* LABELS ────────────────────────────────────────────────────────────────────────── */
.label {
  font-family: var(--font-family);
  font-size: var(--label-sm-size);
  font-weight: var(--font-semibold);
  color: var(--teal-900);
  text-transform: uppercase;
}

.label-positive {
  color: var(--green-alt-700);
}

.label-warning {
  color: var(--orange-alt-700);
}

.label-negative {
  color: var(--red-alt-900);
}

/* REPORT UTILITY NAV ────────────────────────────────────────────────────────────────────────── */
.report-utility-nav-container {
  display: flex;
  padding: 4px;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  border-radius: 6px;
  background: var(--blue-100);
}

/* Inactive */
.report-utility-nav-btn {
  display: flex;
  padding: 0.25rem 1rem;
  justify-content: center;
  align-items: center;
  border-radius: 0.25rem;
  border: none;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  color: var(--blue-600);
  cursor: pointer;

  background: var(--blue-100); /* softer inactive bg */
  color: var(--blue-600);

  box-shadow: none;
  transition: background 0.2s ease, color 0.2s ease;
}

/* HOVER (inactive only) */
.report-utility-nav-btn:hover {
  background: var(--grey-white);
}

/* ACTIVE */
.report-utility-nav-btn.active {
  background: var(--grey-white);
  color: var(--blue-600);
  box-shadow: 0 0.0625rem 0.25rem 0 var(--blue-300);
}

/* prevent hover from overriding active */
.report-utility-nav-btn.active:hover {
  background: var(--grey-white);
}

.report-header-container.is-compact .report-utility-nav-container {
  padding: 0.25rem;
}

.report-header-container.is-compact .report-utility-nav-btn {
  padding: 0.375rem 0.875rem;
}

/* REPORT SIDENAV NAV ────────────────────────────────────────────────────────────────────────── */
.report-sidenav-container {
  display: flex;
  padding-right: 16px;
  flex-direction: column;
  align-items: flex-start;
  align-self: flex-start;
  border-right: 1px solid var(--grey-100);
  gap: 4px;
  position: relative;
}

/* Stick the sidenav flush beneath the sticky header so its top items are
   never hidden behind it. The header's height is dynamic — summary-card
   content varies and the header collapses on scroll — so the runtime JS
   publishes its live border-box height as --report-header-height and the
   sidenav tracks it exactly (through the collapse transition too). The
   expanded-offset token is only the pre-JS fallback. */
.report-container-row .report-sidenav-container {
  position: sticky;
  top: var(--report-header-height, var(--report-header-expanded-offset));
}

/* BASE = inactive */
.report-sidenav-btn {
  display: flex;
  width: 240px;
  padding: 0.5rem 0.5rem 0.5rem 0.625rem;
  justify-content: space-between;
  align-items: center;
  border-radius: 0.375rem;
  border: none;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);

  color: var(--grey-500);
  cursor: pointer;

  background: var(--grey-white);
  transition: background 0.2s ease, color 0.2s ease;
}

/* HOVER (inactive only) */
.report-sidenav-btn:not(.active):hover {
  background: var(--grey-10);
}

/* ACTIVE */
.report-sidenav-btn.active {
  background: var(--grey-10);
  color: var(--slate-600);
}

/* keep active stable */
.report-sidenav-btn.active:hover {
  background: var(--grey-10);
}


/* CHEVRON (reuse report-icon) */
.report-sidenav-btn .report-icon {
  margin-left: auto;
  transform: rotate(0deg); /* DOWN */
  transition: transform 0.2s ease;
}

/* ACTIVE → RIGHT */
.report-sidenav-btn.active .report-icon {
  transform: rotate(-90deg);
}

/* SIZE */
.report-sidenav-btn .report-icon {
  width: 0.625rem;
  height: 0.625rem;

  margin-left: auto;
  display: flex;
  align-items: center;

  /* COLOR (inherits from button) */
  color: inherit;

  /* DEFAULT → DOWN */
  transform: rotate(0deg);
  transition: transform 0.2s ease;
}

/* ACTIVE → RIGHT */
.report-sidenav-btn.active .report-icon {
  transform: rotate(-90deg);
}

/* BANNER BOX — now uses .report-card.banner (see BASE CARD above) */

/* SUPPORTING ARGUMENTS BOX ────────────────────────────────────────────────────────────────────────── */
.report-description-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  width: 100%;
  margin: .5rem 0 0 0;
}

.report-description-list-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
}

.report-description-list .label {
  font-size: var(--label-md-size);
}

.report-description-list .card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--slate-600);
}

.report-description-list .card-description {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-500);
}

/* TABLE ────────────────────────────────────────────────────────────────────────── */
.report-table-wrapper {
  width: 100%;
}

.report-table-title {
  margin: 0 0 0.25rem 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--label-sm-size);
  color: var(--slate-900);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  background: var(--grey-white);
  margin: 0.5rem 0;
}

.report-table th,
.report-table td {
  overflow-wrap: anywhere;
}

/* HEADER */
.report-table thead th {
  padding:  0.5rem 0.75rem;
  text-align: left;
  background: var(--grey-100);

  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--slate-600);
}

/* BODY */
.report-table tbody td {
  padding:  0.5rem 0.75rem;

  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

/* NUMERIC */
.report-table-cell-numeric {
  text-align: left;
  white-space: nowrap;
}

/* POSITION */
.report-table-position {
  font-weight: var(--font-semibold);
  color: var(--slate-600);
}

/* CELL / COLUMN SENTIMENT — applies to spans in both <th> and <td>. */
.report-table .positive { color: var(--green-900); }
.report-table .warning  { color: var(--orange-900); }
.report-table .negative { color: var(--red-900); }

/* TWO-LINE CELL — value on top, muted sub underneath. */
.report-table-cell-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.report-table-cell-sub {
  font-size: var(--font-xs);
  font-weight: var(--font-regular);
  color: var(--grey-500);
}

/* PILL CELL — renders the cell value as a rounded badge. */
.report-table-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  background: var(--grey-100);
  color: var(--grey-600);
}
.report-table-pill-positive { background: var(--green-100); color: var(--green-900); }
.report-table-pill-warning  { background: var(--orange-100); color: var(--orange-900); }
.report-table-pill-negative { background: var(--red-100); color: var(--red-900); }

/* ROW VARIANTS — zebra stripes apply unless a row has its own
   background treatment (focus variant, or row sentiment). */
.report-table tbody tr:nth-child(odd):not(.report-table-row-focus):not(.report-table-row-positive):not(.report-table-row-warning):not(.report-table-row-negative):not(.report-table-row-heading):not(.report-table-row-total) td {
  background: var(--grey-white);
}

.report-table tbody tr:nth-child(even):not(.report-table-row-focus):not(.report-table-row-positive):not(.report-table-row-warning):not(.report-table-row-negative):not(.report-table-row-heading):not(.report-table-row-total) td {
  background: var(--grey-10);
}

.report-table-row-focus td {
  background: var(--blue-100);
}

.report-table-row-positive td {
  background: var(--green-100);
}

.report-table-row-warning td {
  background: var(--orange-100);
}

.report-table-row-negative td {
  background: var(--red-100);
}

/* Total row: bolden every cell (td + nested span override the base
   \`.report-table tbody td\` regular weight and \`.report-table-position\`
   semibold weight). */
.report-table tbody tr.report-table-row-total td,
.report-table tbody tr.report-table-row-total td span {
  font-weight: var(--font-semibold);
}

.report-table tbody tr.report-table-row-total td {
  border-top: 2px solid var(--grey-500);
  background: var(--grey-50);
}

/* Heading row: group-label spanning every column. Uses the same visual
   treatment as the standalone \`.report-heading\` block (uppercase, tight
   tracking, slate-900) with a distinct background so it stands apart
   from the zebra-striped data rows. */
.report-table tbody tr.report-table-row-heading td {
  padding: 0.625rem 0.75rem;
  background: var(--blue-50);
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* VALUE EMPHASIS */
.report-table .report-table-cell-numeric {
  color: var(--grey-600);
}

/* HEADING ─────────────────────────────────────────────────────────────────────── */
.report-heading {
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* TEXT ────────────────────────────────────────────────────────────────────────── */
/* Text is a single paragraph — rendered as \`<p class="report-text">\`. */
.report-text {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
  line-height: 1.6;
  width: 100%;
  margin: 0;
}

/* Sentiment tint. Specificity (0,2,0) beats the in-card
   \`.report-card .report-text\` color override (0,2,0) via order. */
.report-text.positive { color: var(--green-900); }
.report-text.warning  { color: var(--orange-900); }
.report-text.negative { color: var(--red-900); }

/* METRIC LIST ────────────────────────────────────────────────────────────────────────── */
.report-metric-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--grey-white);
  border: 0.0625rem solid var(--grey-300);
  border-radius: 0.5rem;
  width: 100%;
}

.report-metric-list-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-family: var(--font-family);
  font-size: var(--font-sm);
}

.report-metric-list-label {
  color: var(--grey-600);
  font-weight: var(--font-regular);
}

.report-metric-list-value {
  color: var(--slate-600);
  font-weight: var(--font-semibold);
}

.report-metric-list-value.positive { color: var(--green-900); }
.report-metric-list-value.warning  { color: var(--orange-900); }
.report-metric-list-value.negative { color: var(--red-900); }

/* STAT GRID ────────────────────────────────────────────────────────────────────────── */
.report-stat-grid {
  display: grid;
  gap: 1rem;
  width: 100%;
}

.report-stat-grid-item {
  display: flex;
  flex-direction: column;
  padding: 0.875rem 1rem;
  background: var(--grey-10);
  border-bottom: 1px solid var(--grey-300);
  border-radius: 0.5rem;
  gap: 0.25rem;
  min-width: 0;
}

.report-stat-grid-label {
  font-family: var(--font-family);
  font-size: var(--label-sm-size);
  font-weight: var(--font-semibold);
  color: var(--grey-600);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.report-stat-grid-value {
  font-family: var(--font-family);
  font-size: var(--font-xl);
  font-weight: var(--font-bold);
  color: var(--slate-600);
  line-height: 1.2;
}

.report-stat-grid-value.positive { color: var(--green-900); }
.report-stat-grid-value.warning  { color: var(--orange-900); }
.report-stat-grid-value.negative { color: var(--red-900); }

.report-stat-grid-sub {
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-regular);
  color: var(--grey-600);
}

.report-stat-grid-badge {
  align-self: flex-start;
  padding: 2px 8px;
  margin-top: 0.25rem;
  border-radius: 4px;
  background: var(--blue-100);
  color: var(--blue-900);
  font-family: var(--font-family);
  font-size: var(--font-xs);
  font-weight: var(--font-semibold);
}

/* WATERFALL CHART ────────────────────────────────────────────────────────────────────────── */
.report-waterfall-wrap {
  position: relative;
  width: 100%;
}

.report-waterfall {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  font-family: var(--font-family);
}

.report-waterfall-gridline {
  stroke: var(--grey-100);
  stroke-width: 1;
}

.report-waterfall-baseline {
  stroke: var(--grey-300);
  stroke-width: 1;
}

.report-waterfall-connector {
  stroke: var(--grey-300);
  stroke-width: 1;
  stroke-dasharray: 4, 3;
}

.report-waterfall-tick {
  fill: var(--grey-500);
  font-size: 10px;
}

.report-waterfall-label {
  fill: var(--grey-600);
  font-size: 11px;
  font-weight: var(--font-semibold);
}

.report-waterfall-label-secondary {
  font-weight: var(--font-regular);
}

.report-waterfall-bar-total            { fill: var(--blue-900); }
.report-waterfall-bar-captured-saving  { fill: var(--green-900); }
.report-waterfall-bar-avoided-exposure { fill: var(--grey-500); }

.report-waterfall-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  margin: 8px 0 0;
  font-size: var(--font-xs);
  color: var(--grey-600);
}
.report-waterfall-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.report-waterfall-legend-swatch {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
}
.report-waterfall-legend-swatch-total            { background: var(--blue-900); }
.report-waterfall-legend-swatch-captured-saving  { background: var(--green-900); }
.report-waterfall-legend-swatch-avoided-exposure { background: var(--grey-500); }

.report-waterfall-value {
  font-size: 11px;
  font-weight: var(--font-bold);
}
.report-waterfall-value-total            { fill: var(--blue-900); }
.report-waterfall-value-captured-saving  { fill: var(--green-900); }
.report-waterfall-value-avoided-exposure { fill: var(--grey-600); }

/* PIE CHART ────────────────────────────────────────────────────────────────────────── */
.report-pie-wrap {
  position: relative;
  width: 100%;
}

.report-pie {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  font-family: var(--font-family);
}

.report-pie-slice {
  /* Fill colours are applied per-element from the JS palette. */
}

.report-pie-leader {
  stroke: var(--grey-400);
  stroke-width: 1;
  fill: none;
}

.report-pie-label {
  fill: var(--slate-900);
  font-size: 11px;
  font-weight: var(--font-semibold);
}

.report-pie-value {
  fill: var(--grey-600);
  font-size: 10px;
  font-weight: var(--font-regular);
}

.report-pie-empty {
  text-align: center;
  color: var(--grey-500);
  font-size: var(--font-sm);
  margin: 24px 0;
}

/* VEGA-LITE CHART ────────────────────────────────────────────────────────────────────────── */
.report-vega-chart {
  width: 100%;
}

.report-vega-chart-svg {
  display: flex;
  justify-content: center;
}

.report-vega-chart-svg svg {
  max-width: 100%;
  height: auto;
}
  
/* Added by Oasis. Embedded under the project header card, the report spans the
   frame's full width: the page around it supplies the gutters. */
.container { min-width: 0; padding: 0 0 1.5rem 0; }
/* The side nav's entries: left aligned, one line each, cut with an ellipsis. */
.report-sidenav-btn > span:first-child { flex: 1; min-width: 0; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
</style>
</head>
<body>
<div class="container"><div class="report-header-container"><div class="report-summary-container"><div class="report-summary-card report-summary-card-positive wide"><div class="label label-positive">Recommendation</div><div class="report-tag report-tag-positive">Usage analysis v3</div><div class="card-title-container"><div class="card-title"><span class="truncate">Renew 10,400 licenses</span></div><div class="card-subtitle"><span class="card-subtitle">8,300 E5 and 2,100 E3, down from 12,400</span></div></div><div class="card-data-container"><div class="card-data-row"><span class="card-data-row-label">Saving at current prices</span><span class="card-data-row-value">$1.9M a year</span></div><div class="card-data-row"><span class="card-data-row-label">Net after the vendor&apos;s response</span><span class="card-data-row-value">$1.6M to $1.7M</span></div><div class="card-data-row"><span class="card-data-row-label">Next-term cost</span><span class="card-data-row-value">$6.6M against $8.5M today</span></div></div></div><div class="report-summary-card report-summary-card-warning wide"><div class="label label-warning">Deadline</div><div class="card-title-container"><div class="card-title"><span class="truncate">Notice by 31 Dec 2026</span></div><div class="card-subtitle"><span class="card-subtitle">62 days from this report; renews 31 Mar 2027</span></div></div><div class="card-description"><p>Owner reviews on <strong>1,150 seats</strong> are due 21 Nov. Seats under review are kept in the count until the owners answer. Business owner: End-user technology.</p></div></div><div class="report-summary-card report-summary-card-positive narrow"><div class="label label-positive">Price lever</div><div class="card-title-container"><div class="card-title"><span class="truncate">$1.0M to $1.1M more</span></div><div class="card-subtitle"><span class="card-subtitle">E5 at $48 against $57; see the playbook</span></div></div></div></div><div class="report-utility-nav-container"><button class="report-utility-nav-btn active" data-tab-id="usage">Usage analysis</button></div></div><div class="report-container-row"><div class="report-sidenav-container"><div class="report-tab-sidenav" data-tab-id="usage"><button class="report-sidenav-btn" data-target="recommendation"><span>Recommendation</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="quantity"><span>How the quantity was set</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="edition-mix"><span>Edition mix</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="range"><span>What is kept, and the range</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="asks"><span>What to ask for</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="vendor-response"><span>Vendor response and the net saving</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="after-signing"><span>After signing</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="evidence"><span>Evidence</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button></div></div><div class="report-container"><div class="report-tab-content" data-tab-id="usage"><div class="report-section" id="recommendation"><div class="report-section-header-column"><div class="report-section-subheader-row"><p class="report-section-title">Recommendation</p><p class="report-section-stat positive">10,400 licenses</p></div><div class="card-description"><p>Microsoft 365 renewal: how much to buy and at which edition. Prepared 30 Oct 2026, usage to 29 Oct 2026.</p></div></div><div class="report-section-card-container"><div class="report-card callout"><div class="card-title-row"><span class="card-title">Basis</span></div><div class="card-description"><p>Based on usage to 29 Oct 2026; <strong>94% of users matched</strong> to the contract. Seats under owner review (1,150) are kept in the count. Since v2 (28 Oct): <strong>14 more seats inactive</strong> (2,404 current staff with no sign-in in 90 days); no change to the recommendation.</p></div></div></div><div class="report-section-card-container"><p class="report-text">Renew <strong>10,400 licenses</strong>, down from 12,400: 8,300 E5 and 2,100 E3. At current prices that is $6.6M a year against $8.5M today, a saving of <strong>$1.9M</strong>; net after the vendor&apos;s expected response, <strong>$1.6M to $1.7M</strong>. The price lever adds $1.0M on top (see the playbook), for a target of $2.9M a year.</p></div><div class="report-section-card-container"><div class="report-stat-grid" style="grid-template-columns: repeat(4, minmax(0, 1fr));"><div class="report-stat-grid-item"><span class="report-stat-grid-label">Licenses next term</span><span class="report-stat-grid-value">10,400</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Saving a year</span><span class="report-stat-grid-value positive">$1.9M</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Days to notice</span><span class="report-stat-grid-value warning">62</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Owner reviews due</span><span class="report-stat-grid-value">21 Nov</span></div></div></div></div><div class="report-section" id="quantity"><div class="report-section-subheader-row"><p class="report-section-title">How the quantity was set</p></div><div class="report-section-card-container"><p class="report-text">From licenses bought to licenses needed: what was never assigned and what departed staff still hold goes now; what the function owners confirmed as not needed goes at renewal; the headcount plan adds back the hires. Seats inactive but <strong>still under owner review stay in the count</strong>.</p></div><div class="report-section-card-container"><div class="report-table-wrapper"><p class="report-table-title">From licenses bought to licenses needed</p><table class="report-table"><colgroup><col><col style="width: 20%"><col></colgroup><thead><tr><th><span>Step</span></th><th class="report-table-cell-numeric"><span>Licenses</span></th><th><span>Source</span></th></tr></thead><tbody><tr class="report-table-row-total"><td><span class="report-table-position">Bought</span></td><td class="report-table-cell-numeric"><span>12,400</span></td><td><span>Enterprise Agreement, 12 Sep 2026 extraction</span></td></tr><tr><td><span class="report-table-position">Never assigned</span></td><td class="report-table-cell-numeric"><span>−550</span></td><td><span>M365 admin center, 25 Oct 2026</span></td></tr><tr><td><span class="report-table-position">Held by departed staff</span></td><td class="report-table-cell-numeric"><span>−560</span></td><td><span>Workday headcount, 20 Oct 2026</span></td></tr><tr><td><span class="report-table-position">Inactive and confirmed not needed by the owner</span></td><td class="report-table-cell-numeric"><span>−1,240</span></td><td><span>Owner reviews confirmed 24 Oct (three functions)</span></td></tr><tr><td><span class="report-table-position">Planned hires</span></td><td class="report-table-cell-numeric"><span>+350</span></td><td><span>Headcount plan to 31 Mar 2028</span></td></tr><tr class="report-table-row-focus"><td><span class="report-table-position">Needed next term</span></td><td class="report-table-cell-numeric"><span>10,400</span></td><td><span>8,300 E5 and 2,100 E3</span></td></tr></tbody></table></div></div></div><div class="report-section" id="edition-mix"><div class="report-section-subheader-row"><p class="report-section-title">Edition mix</p><p class="report-section-stat">2,100 fit E3</p></div><div class="report-section-card-container"><p class="report-text"><strong>2,100 active users</strong> used none of the E5-only features in the 90 days to 29 Oct 2026 (Defender and Purview, Teams Phone, audio conferencing, Power BI Pro, advanced eDiscovery). They fit E3 at $36 a user a month instead of $57. The remaining 6,800 used at least one E5-only feature.</p></div><div class="report-section-card-container"><div class="report-table-wrapper"><p class="report-table-title">By function</p><table class="report-table"><thead><tr><th><span>Function</span></th><th class="report-table-cell-numeric"><span>Active users</span></th><th class="report-table-cell-numeric"><span>Fit E3</span></th><th class="report-table-cell-numeric"><span>Keep E5</span></th><th class="report-table-cell-numeric"><span>Awaiting review</span></th><th><span>Review status</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Client services</span></td><td class="report-table-cell-numeric"><span>2,360</span></td><td class="report-table-cell-numeric"><span>900</span></td><td class="report-table-cell-numeric"><span>1,460</span></td><td class="report-table-cell-numeric"><span>510</span></td><td><span class="warning">Due 21 Nov</span></td></tr><tr><td><span class="report-table-position">Advisory and wealth</span></td><td class="report-table-cell-numeric"><span>2,050</span></td><td class="report-table-cell-numeric"><span>300</span></td><td class="report-table-cell-numeric"><span>1,750</span></td><td class="report-table-cell-numeric"><span>0</span></td><td><span class="positive">Confirmed 24 Oct</span></td></tr><tr><td><span class="report-table-position">Technology</span></td><td class="report-table-cell-numeric"><span>2,300</span></td><td class="report-table-cell-numeric"><span>100</span></td><td class="report-table-cell-numeric"><span>2,200</span></td><td class="report-table-cell-numeric"><span>0</span></td><td><span class="positive">Confirmed 24 Oct</span></td></tr><tr><td><span class="report-table-position">Operations</span></td><td class="report-table-cell-numeric"><span>1,500</span></td><td class="report-table-cell-numeric"><span>600</span></td><td class="report-table-cell-numeric"><span>900</span></td><td class="report-table-cell-numeric"><span>640</span></td><td><span class="warning">Due 21 Nov</span></td></tr><tr><td><span class="report-table-position">Corporate functions</span></td><td class="report-table-cell-numeric"><span>690</span></td><td class="report-table-cell-numeric"><span>200</span></td><td class="report-table-cell-numeric"><span>490</span></td><td class="report-table-cell-numeric"><span>0</span></td><td><span class="positive">Confirmed 24 Oct</span></td></tr><tr class="report-table-row-total"><td><span class="report-table-position">Total</span></td><td class="report-table-cell-numeric"><span>8,900</span></td><td class="report-table-cell-numeric"><span>2,100</span></td><td class="report-table-cell-numeric"><span>6,800</span></td><td class="report-table-cell-numeric"><span>1,150</span></td><td><span></span></td></tr></tbody></table></div></div><div class="report-section-card-container"><p class="report-text">The 8,300 E5 is the 6,800 active E5 users, plus the 1,150 seats under review, plus the 350 planned hires.</p></div></div><div class="report-section" id="range"><div class="report-section-subheader-row"><p class="report-section-title">What is kept, and the range</p></div><div class="report-section-card-container"><div class="report-metric-list"><div class="report-metric-list-row"><span class="report-metric-list-label">All 1,150 reviewed seats released</span><span class="report-metric-list-value">7,150 E5</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">As recommended</span><span class="report-metric-list-value positive">8,300 E5</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">All kept, plus 300 hired ahead of plan</span><span class="report-metric-list-value">8,600 E5</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Each 100 seats released</span><span class="report-metric-list-value">$68K a year</span></div></div></div><div class="report-section-card-container"><p class="report-text">The E3 count (2,100) does not move with the reviews: those users are active, only their edition changes. The two outstanding reviews (Client services 510, Operations 640) decide where in the range the renewal lands.</p></div></div><div class="report-section" id="asks"><div class="report-section-subheader-row"><p class="report-section-title">What to ask for</p></div><div class="report-section-card-container"><div class="report-card banner negative"><div class="card-title-row"><span class="card-title negative">Reduce the quantity to 10,400 at renewal</span><span class="report-tag report-tag-negative">Quantity</span></div><div class="card-description"><p>8,300 E5 and 2,100 E3, with the reviewed seats settled before the order.</p></div></div><div class="report-card banner negative"><div class="card-title-row"><span class="card-title negative">A right to reduce during the term</span><span class="report-tag report-tag-negative">Terms</span></div><div class="card-description"><p>The contract review found no right to reduce and no price protection. Ask for 10% a year without penalty.</p></div></div><div class="report-card banner warning"><div class="card-title-row"><span class="card-title warning">An E3 mix</span><span class="report-tag report-tag-warning">Edition</span></div><div class="card-description"><p>2,100 users at $36 instead of $57, with the right to move users between editions at the anniversary.</p></div></div><div class="report-card banner warning"><div class="card-title-row"><span class="card-title warning">Price E5 at the peer target</span><span class="report-tag report-tag-warning">Price</span></div><div class="card-description"><p>$48.00 against $57.00 today; the peer median is $49.50 over 23 agreements.</p></div></div><div class="report-card banner warning"><div class="card-title-row"><span class="card-title warning">Protection against the announced list rise</span><span class="report-tag report-tag-warning">Terms</span></div><div class="card-description"><p>Microsoft raises list prices 5% from 1 Apr 2027, the day after the renewal. Fix the price for the term.</p></div></div></div></div><div class="report-section" id="vendor-response"><div class="report-section-subheader-row"><p class="report-section-title">Vendor response and the net saving</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col><col style="width: 18%"><col style="width: 22%"></colgroup><thead><tr><th><span>Lever</span></th><th class="report-table-cell-numeric"><span>Gross</span></th><th class="report-table-cell-numeric"><span>Net after response</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Right-size: quantity and edition</span></td><td class="report-table-cell-numeric"><span>$1.9M</span></td><td class="report-table-cell-numeric"><span>$1.6M to $1.7M</span></td></tr><tr><td><span class="report-table-position">Price: E5 at $48</span></td><td class="report-table-cell-numeric"><span>$1.1M</span></td><td class="report-table-cell-numeric"><span>$1.0M</span></td></tr><tr class="report-table-row-total"><td><span class="report-table-position">Total</span></td><td class="report-table-cell-numeric"><span>$3.0M</span></td><td class="report-table-cell-numeric"><span class="positive">$2.5M to $2.7M</span></td></tr></tbody></table></div><div class="report-section-card-container"><div class="report-description-list"><span class="label">Expected vendor moves</span><div class="report-description-list-content"><div class="card-title"><span class="truncate">Re-pricing of the retained E5:</span></div><div class="card-description"><p>The per-user discount shrinks as the quantity falls; part of the right-size saving comes back as price.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">Loss of a discount tier:</span></div><div class="card-description"><p>Below 10,000 E5 the agreement drops a tier; the E3 mix keeps the total above 10,000 licenses.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">Bundling Copilot:</span></div><div class="card-description"><p>Expect an offer to fold Copilot into the renewal to hold the total; the 460 inactive Copilot seats say no.</p></div></div></div></div><div class="report-section-card-container"><p class="report-text">Figures are illustrative; the playbook carries the negotiation plan and the rounds.</p></div></div><div class="report-section" id="after-signing"><div class="report-section-subheader-row"><p class="report-section-title">After signing</p></div><div class="report-section-card-container"><p class="report-text">Check the first invoice, due 30 Jun 2027, against the signed quantities and unit prices.</p></div><div class="report-section-card-container"><p class="report-text">Re-run this analysis 90 days after the renewal date. The owner reviews become the baseline for the next term&apos;s reduction right.</p></div></div><div class="report-section" id="evidence"><div class="report-section-subheader-row"><p class="report-section-title">Evidence</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 30%"><col style="width: 20%"><col></colgroup><thead><tr><th><span>Source</span></th><th><span>As of</span></th><th><span>Supports</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Microsoft Entra sign-ins</span></td><td><span>29 Oct 2026, daily</span></td><td><span>Last sign-in and days inactive per user</span></td></tr><tr><td><span class="report-table-position">Microsoft 365 admin center usage reports</span></td><td><span>25 Oct 2026</span></td><td><span>Licenses assigned, E5 feature use per user (94% matched to the contract)</span></td></tr><tr><td><span class="report-table-position">Workday headcount</span></td><td><span>20 Oct 2026</span></td><td><span>Employment status, departures, planned hires</span></td></tr><tr><td><span class="report-table-position">Coupa AP</span></td><td><span>24 Oct 2026</span></td><td><span>Invoices paid; unit price $57 a user a month</span></td></tr><tr><td><span class="report-table-position">Enterprise Agreement extraction</span></td><td><span>12 Sep 2026</span></td><td><span>Quantities, term, no right to reduce, no price protection</span></td></tr><tr><td><span class="report-table-position">Market data and playbook</span></td><td><span>30 Oct 2026</span></td><td><span>Peer median $49.50 over 23 agreements; target $48.00</span></td></tr></tbody></table></div></div></div></div></div></div>
<script>
  const tabButtons = document.querySelectorAll(".report-utility-nav-btn");
  const header = document.querySelector(".report-header-container");

  // The sidenav sticks flush beneath the header (see styles.css). The header's
  // height varies with its content and shrinks as it collapses, so publish its
  // live height and keep the sidenav in sync — ResizeObserver fires through the
  // collapse transition too, so the sidenav tracks the header frame by frame.
  function publishHeaderHeight() {
    if (!header) return;
    document.documentElement.style.setProperty(
      "--report-header-height",
      header.offsetHeight + "px"
    );
  }

  if (header && "ResizeObserver" in window) {
    new ResizeObserver(publishHeaderHeight).observe(header);
  }
  publishHeaderHeight();

  // Measure the header's expanded and compact heights so the collapse
  // thresholds adapt to the report (summary-card content varies the height).
  // Toggling with the transition suppressed makes the probe instant and
  // invisible. Re-measured on resize / once webfonts settle, since both shift
  // the height.
  let expandedHeaderH = 0;
  let compactHeaderH = 0;
  function measureHeaderHeights() {
    if (!header) return;
    const wasCompact = header.classList.contains("is-compact");
    header.classList.add("is-anim-suppressed");
    header.classList.remove("is-compact");
    expandedHeaderH = header.offsetHeight;
    header.classList.add("is-compact");
    compactHeaderH = header.offsetHeight;
    header.classList.toggle("is-compact", wasCompact);
    header.classList.remove("is-anim-suppressed");
  }
  measureHeaderHeights();
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(measureHeaderHeights);
  }

  // Header collapse controller. The header is expanded at the very top of the
  // report and compact once you've scrolled down into the content. Run on
  // every scroll and after programmatic scrolls (nav-item clicks, tab
  // switches) so both paths behave identically.
  //
  // Driven by scrollY with hysteresis: expand near the very top, collapse once
  // you've scrolled past the header. The catch is that collapsing shrinks the
  // header by \`delta\`, and browser scroll anchoring then shifts scrollY by that
  // same delta to keep your place — so the gap between the two thresholds must
  // exceed \`delta\`, otherwise the post-toggle anchoring shift lands past the
  // opposite threshold and the header flickers. We collapse \`delta\` + margin
  // down and expand within \`margin\` of the top, giving a band of delta + margin.
  const HEADER_MARGIN_PX = 32;
  function updateHeaderCompact() {
    if (!header) return;
    const delta = Math.max(0, expandedHeaderH - compactHeaderH);
    const y = window.pageYOffset;
    if (y <= HEADER_MARGIN_PX) {
      header.classList.remove("is-compact");
    } else if (y >= delta + 2 * HEADER_MARGIN_PX) {
      header.classList.add("is-compact");
    }
  }

  // Scroll a section flush beneath the header (used by nav-item clicks and tab
  // switches). The offset depends on the header height, but landing the scroll
  // *also* changes whether the header is collapsed — and an animated collapse
  // reflows content under the new scroll position, which browser scroll
  // anchoring then chases, drifting us onto the previous section. So: jump,
  // settle the header into its destination state with the collapse animation
  // suppressed (one synchronous reflow, nothing for anchoring to track), then
  // re-pin against the now-final header height. Animation is restored next
  // frame so manual scrolling keeps its smooth collapse.
  function scrollSectionToTop(target) {
    if (!target) return;
    const pin = () =>
      Math.max(0, target.getBoundingClientRect().top + window.pageYOffset - getHeaderOffset());
    window.scrollTo({ top: pin(), behavior: "auto" });
    if (header) header.classList.add("is-anim-suppressed");
    updateHeaderCompact();
    window.scrollTo({ top: pin(), behavior: "auto" });
    if (header) {
      requestAnimationFrame(() => header.classList.remove("is-anim-suppressed"));
    }
  }

  function getHeaderOffset() {
    return header ? header.offsetHeight : 0;
  }

  function updateActiveSidenav() {
    const activePane = document.querySelector('.report-tab-content:not([hidden])');
    if (!activePane) return;

    const sections = activePane.querySelectorAll('.report-section');
    if (!sections.length) return;

    let activeId = sections[0].id;
    const headerOffset = getHeaderOffset() + 24;

    sections.forEach(section => {
      const rect = section.getBoundingClientRect();
      if (rect.top <= headerOffset) {
        activeId = section.id;
      }
    });

    document
      .querySelectorAll('.report-tab-sidenav:not([hidden]) .report-sidenav-btn')
      .forEach(button => {
        button.classList.toggle('active', button.dataset.target === activeId);
      });
  }

  function onScroll() {
    updateHeaderCompact();
    updateActiveSidenav();
  }

  function activateTab(tabId) {
    tabButtons.forEach(button => button.classList.toggle("active", button.dataset.tabId === tabId));

    document.querySelectorAll(".report-tab-sidenav").forEach(element => {
      element.hidden = element.dataset.tabId !== tabId;
    });

    document.querySelectorAll(".report-tab-content").forEach(element => {
      element.hidden = element.dataset.tabId !== tabId;
    });

    document.querySelectorAll(".report-sidenav-btn").forEach(button => button.classList.remove("active"));

    const firstSide = document.querySelector('.report-tab-sidenav:not([hidden]) .report-sidenav-btn');
    if (firstSide) firstSide.classList.add('active');

    const firstSection = document.querySelector('.report-tab-content:not([hidden]) .report-section');
    scrollSectionToTop(firstSection);

    updateActiveSidenav();
  }

  tabButtons.forEach(button => {
    button.addEventListener("click", () => activateTab(button.dataset.tabId));
  });

  const firstExpander = document.querySelector(".report-expander");
  if (firstExpander) firstExpander.setAttribute("open", "");

  const firstSideOnLoad = document.querySelector('.report-tab-sidenav:not([hidden]) .report-sidenav-btn');
  if (firstSideOnLoad) firstSideOnLoad.classList.add('active');

  document.querySelectorAll(".report-sidenav-btn").forEach(button => {
    button.addEventListener("click", () => {
      const target = document.getElementById(button.dataset.target);
      if (!target) return;

      document.querySelectorAll('.report-tab-sidenav:not([hidden]) .report-sidenav-btn').forEach(sideButton => {
        sideButton.classList.remove('active');
      });
      button.classList.add('active');

      scrollSectionToTop(target);
      updateActiveSidenav();
    });
  });

  updateHeaderCompact();
  updateActiveSidenav();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
    measureHeaderHeights();
    onScroll();
  });

    // Fallback colgroup for tables whose schema didn't declare column
    // widths. If the server-side renderer already emitted a <colgroup>
    // (because at least one column in the JSON had a \`width\`), respect
    // it and bail — don't strip and replace.
  document.querySelectorAll('.report-table').forEach((table) => {
    if (table.querySelector(':scope > colgroup')) return;

    const firstRow = table.querySelector('thead tr, tbody tr');
    if (!firstRow) return;

    const columnCount = firstRow.children.length;

    const colgroup = document.createElement('colgroup');

    let widths = [];

    if (columnCount === 2) {
      widths = ['13.75rem', 'auto']; // 220px / rest
    } else if (columnCount === 3) {
      widths = ['13.75rem', '13.125rem', 'auto']; // 220 / 210 / rest
    } else if (columnCount === 4) {
      widths = ['13.75rem', '13.125rem', '13.125rem', 'auto']; // 220 / 210 / 210 / rest
    } else if (columnCount === 5) {
      widths = ['13.75rem', '13.125rem', '12rem', '12rem', '10rem'];
    } else {
      // fallback: first two anchored, rest auto
      widths = Array.from({ length: columnCount }, (_, i) => {
        if (i === 0) return '13.75rem';
        if (i === 1) return '13.125rem';
        return 'auto';
      });
    }

    widths.forEach((width) => {
      const col = document.createElement('col');
      if (width !== 'auto') {
        col.style.width = width;
      }
      colgroup.appendChild(col);
    });

    table.insertBefore(colgroup, table.firstChild);
  });
</script>
</body>
</html>

`;

export const PLAYBOOK_V1 = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Report</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap" rel="stylesheet">
  <style>
/* GLOBAL ────────────────────────────────────────────────────────────────────────── */
:root {
  --container-min-width: 1080px;

  --report-header-expanded-offset: 17.125rem;

  /* FONT FAMILY */
  --font-family: "Inter", sans-serif;

  /* FONT WEIGHTS */
  --font-regular: 400;
  --font-semibold: 600;
  --font-bold: 700;

  /* FONT SIZES */
  --font-xs: 0.625rem;
  --font-sm: 0.75rem;
  --font-md: 0.875rem;
  --font-default: 1rem;
  --font-lg: 1.25rem;
  --font-xl: 1.5rem;

  /* LABEL */
  --label-sm-size: 0.625rem;
  --label-md-size: 0.75rem;

  /* SLATE */
  --slate-900: #141E2E;
  --slate-600: #2F466B;

  /* TEAL */
  --teal-900: #00B2FE;
  --teal-300: #75D6FF;

  /* GREY */
  --grey-900: #404040;
  --grey-600: #666666;
  --grey-500: #999999;
  --grey-400: #BFBFBF;
  --grey-300: #D9D9D9;
  --grey-100: #F0F0F0;
  --grey-50: #F5F5F5;
  --grey-10: #FAFAFA;
  --grey-white: #FFFFFF;

  /* BLUE */
  --blue-900: #006ACC;
  --blue-600: #1890FF;
  --blue-300: #99CEFF;
  --blue-100: #E5F3FF;
  --blue-50: #F0F8FF;


  /* GREEN */
  --green-900: #2FB801;
  --green-600: #5AD81D;
  --green-500: #77E043;
  --green-300: #C1F2A9;
  --green-100: #E6FADC;
  --green-50: #F2FCED;

  /* RED */
  --red-900: #FD3559;
  --red-600: #FF758E;
  --red-300: #FEA8B7;
  --red-100: #FFE6EA;
  --red-50: #FFF0F2;
  --red-10: #FFF7F9;

  /* ORANGE */
  --orange-900: #FF8400;
  --orange-600: #FF981F;
  --orange-300: #FFCA99;
  --orange-100: #FFE8CC;
  --orange-50: #FFF4EB;

  /* MAGENTA */
  --magenta-900: #EA00FF;

  /* SPECIAL */
  --slate-alt-900: #101525;
  --green-alt-900: #124002;
  --green-alt-700: #315724;
  --orange-alt-900: #591E00;
  --orange-alt-700: #663600;
  --red-alt-900: #4D000E;
  --red-alt-700: #660012;

  /* GRADIENT */
  --pink-blue: linear-gradient(90deg, #77ADFF 31.25%, #FF3FEC 100%);
}

body {
  margin: 0;
  background: var(--grey-white);
  font-family: var(--font-family);
  display: flex;
  justify-content: center;
}

.container {
  box-sizing: border-box;
  width: 100%;
  min-width: var(--container-min-width);
  padding: 0 1.5rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

p {
  margin: 0;
}

/* REPORT CONTAINER ────────────────────────────────────────────────────────────────────────── */
.report-container-row {
  display: flex;
  align-items: flex-start;
  background: var(--grey-white);
  gap: 16px;
}

.report-container {
  display: flex;
  flex-direction: column;
  background: var(--grey-white);
  flex: 1 1 0;
  min-width: 0;
  padding-bottom: 80vh;
}

/* REPORT HEADER ────────────────────────────────────────────────────────────────────────── */
.report-header-container {
  display: flex;
  flex-direction: column;
  padding: 0 0 1.25rem 0;
  position: sticky;
  top: 0;
  z-index: 1000;
  gap: 1rem;
  background: var(--grey-white);
  transition:
    padding 0.32s ease,
    gap 0.32s ease,
    box-shadow 0.32s ease;
  will-change: padding, gap;
}


/* REPORT SUMMARY CARDS ────────────────────────────────────────────────────────────────────────── */
.report-summary-container {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  background: var(--grey-white);
  transition: gap 0.22s ease;
}

.report-summary-card {
  --card-bg: var(--grey-10);
  --card-border: var(--grey-300);

  background: var(--card-bg);
  border-bottom: 1px solid var(--card-border);

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem 1rem 0.75rem 1rem;
  gap: 0.5rem;
  border-radius: 0.5rem;
  min-width: 0;
  transition:
    padding 0.32s ease,
    gap 0.32s ease,
    border-radius 0.32s ease,
    background-color 0.32s ease,
    border-color 0.32s ease;
}

.report-summary-card.wide {
  flex: 5;
}

.report-summary-card.narrow {
  flex: 2.5;
}

.report-summary-card-positive {
  --card-bg: var(--green-50);
  --card-border: var(--green-500);
}

.report-summary-card-warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-summary-card-negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

/* Collapse instantly (no animation) while a nav-item click / tab switch
   re-pins the scroll, so the header height settles in one synchronous reflow
   that browser scroll anchoring can't chase off the target. Restored next
   frame, so manual scrolling keeps its smooth collapse. */
.report-header-container.is-anim-suppressed,
.report-header-container.is-anim-suppressed * {
  transition: none !important;
}

.report-header-container.is-compact {
  padding: 0 0 1.25rem 0;
  gap: 0.75rem;
}

.report-header-container.is-compact .report-summary-container {
  gap: 0.5rem;
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.report-header-container.is-compact .report-summary-card {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  gap: 0.75rem;
  border-radius: 0.5rem;
}

.report-header-container.is-compact .report-summary-card .card-description,
.report-header-container.is-compact .report-summary-card .card-data-container {
  opacity: 0;
  transform: translateY(-0.25rem);
  pointer-events: none;
  display: none;
}

.report-header-container.is-compact .report-summary-card .label {
  flex: 0 0 auto;
  white-space: nowrap;
}

.report-header-container.is-compact .report-summary-card .report-tag {
  flex: 0 0 auto;
  min-width: auto;
  white-space: nowrap;
}

.report-header-container.is-compact .report-summary-card .card-title-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  width: auto;
  min-width: 0;
  flex: 1 1 auto;
}

.report-header-container.is-compact .report-summary-card .card-subtitle {
  opacity: 0;
  transform: translateY(-0.25rem);
  pointer-events: none;
  display: none;
}

.report-header-container.is-compact .report-summary-card .card-title {
  font-size: var(--font-md);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.report-header-container.is-compact .report-summary-card.narrow .card-title-container {
  justify-content: flex-end;
}

.card-title-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  gap: 0.25rem;
  transition: gap 0.22s ease;
}

.card-title,
.card-subtitle,
.card-description {
  width: 100%;
  min-width: 0;
}

.card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-lg);
  color: var(--slate-600);
  width: 100%;
  transition: font-size 0.22s ease, color 0.22s ease;
}

.report-summary-card-positive .card-title {
  color: var(--green-900);
}

.report-summary-card-warning .card-title {
  color: var(--orange-900);
}

.report-summary-card-negative .card-title {
  color: var(--red-900);
}

.card-subtitle {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  transition: opacity 0.18s ease, transform 0.18s ease, color 0.22s ease;
}

.card-description {
  width: 100%;
  min-width: 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  line-height: 1.6;
  color: var(--grey-600);
  transition: opacity 0.18s ease, transform 0.18s ease, color 0.22s ease;
}

.card-data-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-width: 0;
  gap: 0.5rem;
}

.card-data-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
}

.card-data-row-label {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.card-data-row-value {
  font-family: var(--font-family);
  font-weight: var(--font-bold);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.report-summary-card-positive .card-data-row-value {
  color: var(--green-900);
}

.report-summary-card-warning .card-data-row-value {
  color: var(--orange-900);
}

.report-summary-card-negative .card-data-row-value {
  color: var(--red-900);
}

.truncate {
  display: block;
  width: 100%;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* REPORT SECTION ────────────────────────────────────────────────────────────────────────── */
.report-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
  padding: 16px;
  margin: 0 0 20px 0;
  border-radius: 8px;
  border-bottom: 1px solid var(--grey-300);
  background: var(--grey-10);
}

.report-section-header-column {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  gap: 4px;
}

.report-section-card-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}

.report-section-card-container.row {
  flex-direction: row;
  justify-content: space-between;
}

/* CARD GRID — CSS-grid layout. \`grid-template-columns\` is set inline by the
   CardGrid component (via the \`columns\` prop; defaults to items.length). */
.report-card-grid {
  display: grid;
  gap: 1rem;
  width: 100%;
}

/* BASE CARD — shared layout for section / banner / callout cards */
.report-card {
  --card-bg: var(--grey-white);
  --card-border: var(--grey-300);
  background: var(--card-bg);
  border-bottom: 1px solid var(--card-border);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem 1rem 0.75rem 1rem;
  gap: 0.5rem;
  border-radius: 0.5rem;
  width: 100%;
  box-sizing: border-box;
}

.report-card .card-title-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.report-card .card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

/* Inside a card-grid, titles ellipsize to fit fixed-width grid cells. */
.report-card-grid .card-title {
  display: block;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.report-card .card-title-stat {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--green-900);
}

.report-card .card-description,
.report-card .report-text {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  line-height: 1.6;
  color: var(--grey-500);
}

/* BANNER modifier — tighter gap, severity color variants */
.report-card.banner {
  gap: 0.325rem;
}

.report-card.banner.positive {
  --card-bg: var(--green-50);
  --card-border: var(--green-500);
}

.report-card.banner.warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-card.banner.negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

.report-card.banner .card-title.positive {
  color: var(--green-900);
}

.report-card.banner .card-title.warning {
  color: var(--orange-900);
}

.report-card.banner .card-title.negative {
  color: var(--red-900);
}

.report-card.banner .card-description {
  color: var(--grey-600);
}

/* CALLOUT modifier — teal accent, smaller title */
.report-card.callout {
  --card-border: var(--teal-300);
  gap: 0.325rem;
}

.report-card.callout.warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-card.callout.negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

.report-card.callout .card-title {
  font-size: var(--font-sm);
  color: var(--teal-900);
}

.report-card.callout .card-title.warning {
  color: var(--orange-900);
}

.report-card.callout .card-title.negative {
  color: var(--red-900);
}

/* BLOCKQUOTE — verbatim quoted text (e.g. suggested contract language) */
.report-blockquote {
  margin: 0;
  padding: 0.5rem 0.7rem;
  background: var(--grey-50);
  border-left: 2px solid var(--grey-300);
  border-radius: 0 4px 4px 0;
  color: var(--grey-600);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: var(--font-xs);
  line-height: 1.6;
  white-space: pre-wrap;
}

.report-section-subheader {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 4px;
  align-self: stretch;
  padding: 0 2px;
}

.report-section-subheader-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  align-self: stretch;
  padding: 0 2px;
}

.report-section-title {
  display: flex;
  align-items: center;
  align-self: stretch;
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

.report-section-description {
  display: flex;
  align-items: center;
  max-width: 900px;
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.report-section-stat {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

.report-section-stat.positive {
  color: var(--green-900);
}

.report-section-stat.warning {
  color: var(--orange-900);
}

.report-section-stat.negative {
  color: var(--red-900);
}

/* REPORT EXPANDERS ────────────────────────────────────────────────────────────────────────── */
.report-expander {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  font-family: var(--font-family);
  border-bottom: 0.0625rem solid var(--grey-300);
  border-radius: 0.375rem;
  overflow: hidden;
  background: var(--grey-white);
}

/* HEADER BASE */
.report-expander summary {
  list-style: none;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-self: stretch;
  align-items: center;
  padding: 0.5rem 1rem;
  background: var(--grey-white);
  transition: background 0.2s ease;
}

/* HOVER ONLY WHEN CLOSED */
.report-expander:not([open]) summary:hover {
  background: var(--blue-600);
}

/* TEXT + ICON WHITE ON HOVER */
.report-expander:not([open]) summary:hover .report-title,
.report-expander:not([open]) summary:hover .report-subtitle,
.report-expander:not([open]) summary:hover .report-icon {
  color: var(--grey-white);
}

/* BORDER CHANGE */
.report-expander:not([open]):hover {
  border-bottom: 0.0625rem solid var(--blue-600);
}

/* OPEN STATE */
.report-expander[open] summary {
  background: var(--blue-600);
}

/* TEXT + ICON WHITE WHEN OPEN */
.report-expander[open] .report-title,
.report-expander[open] .report-subtitle,
.report-expander[open] .report-icon {
  color: var(--grey-white);
}

/* TEXT BLOCK */
.report-expander-text {
  display: flex;
  flex-direction: column;
}

/* TYPOGRAPHY */
.report-title {
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-bold);
  font-size: var(--font-md);
  color: var(--blue-600);
}

.report-subtitle {
  margin: 0.125rem 0 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

/* ICON */
.report-icon {
  display: flex;
  width: 0.875rem;
  height: 0.875rem;
  flex: 0 0 1rem;
  color: var(--blue-600);
  transition: transform 0.25s ease, color 0.2s ease;
}

.report-icon svg {
  width: 100%;
  height: 100%;
  display: flex;
}

.report-icon svg path {
  fill: currentColor;
}

.report-expander[open] .report-icon {
  transform: rotate(180deg);
}

/* CONTENT */
.report-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  padding: 0.75rem 1rem;
  background: var(--grey-white);
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-regular);
  line-height: 1.5;
  color: var(--grey-600);
}

/* ANIMATION */
.report-expander .report-content {
  transform-origin: top;
  transform: scaleY(0.95);
  opacity: 0;
  min-height: 200px;
  overflow: hidden;
  transition:
    transform 0.2s ease,
    opacity 0.2s ease,
    max-height 0.25s ease;
}

.report-expander[open] .report-content {
  transform: scaleY(1);
  opacity: 1;
  max-height: 31.25rem;
}

/* REMOVE DEFAULT ARROW */
.report-expander summary {
  list-style: none;
}

.report-expander summary::-webkit-details-marker {
  display: none;
}

.report-expander summary::marker {
  content: "";
}

/* REPORT TAGS ────────────────────────────────────────────────────────────────────────── */
.report-tag {
  display: flex;
  padding: 4px 10px 4px 10px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  min-width: 68px;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  color: var(--grey-white);
}

.report-tag-positive {
  background: var(--green-900);
  color: var(--grey-white);
}

.report-tag-warning {
  background: var(--orange-900);
  color: var(--grey-white);
}

.report-tag-negative {
  background: var(--red-900);
  color: var(--grey-white);
}

.report-tag-alt-positive {
  background: var(--green-100);
  color: var(--green-900);
}

.report-tag-alt-warning {
  background: var(--orange-100);
  color: var(--orange-900);
}

.report-tag-alt-negative {
  background: var(--red-100);
  color: var(--red-900);
}

/* LABELS ────────────────────────────────────────────────────────────────────────── */
.label {
  font-family: var(--font-family);
  font-size: var(--label-sm-size);
  font-weight: var(--font-semibold);
  color: var(--teal-900);
  text-transform: uppercase;
}

.label-positive {
  color: var(--green-alt-700);
}

.label-warning {
  color: var(--orange-alt-700);
}

.label-negative {
  color: var(--red-alt-900);
}

/* REPORT UTILITY NAV ────────────────────────────────────────────────────────────────────────── */
.report-utility-nav-container {
  display: flex;
  padding: 4px;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  border-radius: 6px;
  background: var(--blue-100);
}

/* Inactive */
.report-utility-nav-btn {
  display: flex;
  padding: 0.25rem 1rem;
  justify-content: center;
  align-items: center;
  border-radius: 0.25rem;
  border: none;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  color: var(--blue-600);
  cursor: pointer;

  background: var(--blue-100); /* softer inactive bg */
  color: var(--blue-600);

  box-shadow: none;
  transition: background 0.2s ease, color 0.2s ease;
}

/* HOVER (inactive only) */
.report-utility-nav-btn:hover {
  background: var(--grey-white);
}

/* ACTIVE */
.report-utility-nav-btn.active {
  background: var(--grey-white);
  color: var(--blue-600);
  box-shadow: 0 0.0625rem 0.25rem 0 var(--blue-300);
}

/* prevent hover from overriding active */
.report-utility-nav-btn.active:hover {
  background: var(--grey-white);
}

.report-header-container.is-compact .report-utility-nav-container {
  padding: 0.25rem;
}

.report-header-container.is-compact .report-utility-nav-btn {
  padding: 0.375rem 0.875rem;
}

/* REPORT SIDENAV NAV ────────────────────────────────────────────────────────────────────────── */
.report-sidenav-container {
  display: flex;
  padding-right: 16px;
  flex-direction: column;
  align-items: flex-start;
  align-self: flex-start;
  border-right: 1px solid var(--grey-100);
  gap: 4px;
  position: relative;
}

/* Stick the sidenav flush beneath the sticky header so its top items are
   never hidden behind it. The header's height is dynamic — summary-card
   content varies and the header collapses on scroll — so the runtime JS
   publishes its live border-box height as --report-header-height and the
   sidenav tracks it exactly (through the collapse transition too). The
   expanded-offset token is only the pre-JS fallback. */
.report-container-row .report-sidenav-container {
  position: sticky;
  top: var(--report-header-height, var(--report-header-expanded-offset));
}

/* BASE = inactive */
.report-sidenav-btn {
  display: flex;
  width: 240px;
  padding: 0.5rem 0.5rem 0.5rem 0.625rem;
  justify-content: space-between;
  align-items: center;
  border-radius: 0.375rem;
  border: none;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);

  color: var(--grey-500);
  cursor: pointer;

  background: var(--grey-white);
  transition: background 0.2s ease, color 0.2s ease;
}

/* HOVER (inactive only) */
.report-sidenav-btn:not(.active):hover {
  background: var(--grey-10);
}

/* ACTIVE */
.report-sidenav-btn.active {
  background: var(--grey-10);
  color: var(--slate-600);
}

/* keep active stable */
.report-sidenav-btn.active:hover {
  background: var(--grey-10);
}


/* CHEVRON (reuse report-icon) */
.report-sidenav-btn .report-icon {
  margin-left: auto;
  transform: rotate(0deg); /* DOWN */
  transition: transform 0.2s ease;
}

/* ACTIVE → RIGHT */
.report-sidenav-btn.active .report-icon {
  transform: rotate(-90deg);
}

/* SIZE */
.report-sidenav-btn .report-icon {
  width: 0.625rem;
  height: 0.625rem;

  margin-left: auto;
  display: flex;
  align-items: center;

  /* COLOR (inherits from button) */
  color: inherit;

  /* DEFAULT → DOWN */
  transform: rotate(0deg);
  transition: transform 0.2s ease;
}

/* ACTIVE → RIGHT */
.report-sidenav-btn.active .report-icon {
  transform: rotate(-90deg);
}

/* BANNER BOX — now uses .report-card.banner (see BASE CARD above) */

/* SUPPORTING ARGUMENTS BOX ────────────────────────────────────────────────────────────────────────── */
.report-description-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  width: 100%;
  margin: .5rem 0 0 0;
}

.report-description-list-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
}

.report-description-list .label {
  font-size: var(--label-md-size);
}

.report-description-list .card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--slate-600);
}

.report-description-list .card-description {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-500);
}

/* TABLE ────────────────────────────────────────────────────────────────────────── */
.report-table-wrapper {
  width: 100%;
}

.report-table-title {
  margin: 0 0 0.25rem 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--label-sm-size);
  color: var(--slate-900);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  background: var(--grey-white);
  margin: 0.5rem 0;
}

.report-table th,
.report-table td {
  overflow-wrap: anywhere;
}

/* HEADER */
.report-table thead th {
  padding:  0.5rem 0.75rem;
  text-align: left;
  background: var(--grey-100);

  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--slate-600);
}

/* BODY */
.report-table tbody td {
  padding:  0.5rem 0.75rem;

  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

/* NUMERIC */
.report-table-cell-numeric {
  text-align: left;
  white-space: nowrap;
}

/* POSITION */
.report-table-position {
  font-weight: var(--font-semibold);
  color: var(--slate-600);
}

/* CELL / COLUMN SENTIMENT — applies to spans in both <th> and <td>. */
.report-table .positive { color: var(--green-900); }
.report-table .warning  { color: var(--orange-900); }
.report-table .negative { color: var(--red-900); }

/* TWO-LINE CELL — value on top, muted sub underneath. */
.report-table-cell-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.report-table-cell-sub {
  font-size: var(--font-xs);
  font-weight: var(--font-regular);
  color: var(--grey-500);
}

/* PILL CELL — renders the cell value as a rounded badge. */
.report-table-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  background: var(--grey-100);
  color: var(--grey-600);
}
.report-table-pill-positive { background: var(--green-100); color: var(--green-900); }
.report-table-pill-warning  { background: var(--orange-100); color: var(--orange-900); }
.report-table-pill-negative { background: var(--red-100); color: var(--red-900); }

/* ROW VARIANTS — zebra stripes apply unless a row has its own
   background treatment (focus variant, or row sentiment). */
.report-table tbody tr:nth-child(odd):not(.report-table-row-focus):not(.report-table-row-positive):not(.report-table-row-warning):not(.report-table-row-negative):not(.report-table-row-heading):not(.report-table-row-total) td {
  background: var(--grey-white);
}

.report-table tbody tr:nth-child(even):not(.report-table-row-focus):not(.report-table-row-positive):not(.report-table-row-warning):not(.report-table-row-negative):not(.report-table-row-heading):not(.report-table-row-total) td {
  background: var(--grey-10);
}

.report-table-row-focus td {
  background: var(--blue-100);
}

.report-table-row-positive td {
  background: var(--green-100);
}

.report-table-row-warning td {
  background: var(--orange-100);
}

.report-table-row-negative td {
  background: var(--red-100);
}

/* Total row: bolden every cell (td + nested span override the base
   \`.report-table tbody td\` regular weight and \`.report-table-position\`
   semibold weight). */
.report-table tbody tr.report-table-row-total td,
.report-table tbody tr.report-table-row-total td span {
  font-weight: var(--font-semibold);
}

.report-table tbody tr.report-table-row-total td {
  border-top: 2px solid var(--grey-500);
  background: var(--grey-50);
}

/* Heading row: group-label spanning every column. Uses the same visual
   treatment as the standalone \`.report-heading\` block (uppercase, tight
   tracking, slate-900) with a distinct background so it stands apart
   from the zebra-striped data rows. */
.report-table tbody tr.report-table-row-heading td {
  padding: 0.625rem 0.75rem;
  background: var(--blue-50);
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* VALUE EMPHASIS */
.report-table .report-table-cell-numeric {
  color: var(--grey-600);
}

/* HEADING ─────────────────────────────────────────────────────────────────────── */
.report-heading {
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* TEXT ────────────────────────────────────────────────────────────────────────── */
/* Text is a single paragraph — rendered as \`<p class="report-text">\`. */
.report-text {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
  line-height: 1.6;
  width: 100%;
  margin: 0;
}

/* Sentiment tint. Specificity (0,2,0) beats the in-card
   \`.report-card .report-text\` color override (0,2,0) via order. */
.report-text.positive { color: var(--green-900); }
.report-text.warning  { color: var(--orange-900); }
.report-text.negative { color: var(--red-900); }

/* METRIC LIST ────────────────────────────────────────────────────────────────────────── */
.report-metric-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--grey-white);
  border: 0.0625rem solid var(--grey-300);
  border-radius: 0.5rem;
  width: 100%;
}

.report-metric-list-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-family: var(--font-family);
  font-size: var(--font-sm);
}

.report-metric-list-label {
  color: var(--grey-600);
  font-weight: var(--font-regular);
}

.report-metric-list-value {
  color: var(--slate-600);
  font-weight: var(--font-semibold);
}

.report-metric-list-value.positive { color: var(--green-900); }
.report-metric-list-value.warning  { color: var(--orange-900); }
.report-metric-list-value.negative { color: var(--red-900); }

/* STAT GRID ────────────────────────────────────────────────────────────────────────── */
.report-stat-grid {
  display: grid;
  gap: 1rem;
  width: 100%;
}

.report-stat-grid-item {
  display: flex;
  flex-direction: column;
  padding: 0.875rem 1rem;
  background: var(--grey-10);
  border-bottom: 1px solid var(--grey-300);
  border-radius: 0.5rem;
  gap: 0.25rem;
  min-width: 0;
}

.report-stat-grid-label {
  font-family: var(--font-family);
  font-size: var(--label-sm-size);
  font-weight: var(--font-semibold);
  color: var(--grey-600);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.report-stat-grid-value {
  font-family: var(--font-family);
  font-size: var(--font-xl);
  font-weight: var(--font-bold);
  color: var(--slate-600);
  line-height: 1.2;
}

.report-stat-grid-value.positive { color: var(--green-900); }
.report-stat-grid-value.warning  { color: var(--orange-900); }
.report-stat-grid-value.negative { color: var(--red-900); }

.report-stat-grid-sub {
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-regular);
  color: var(--grey-600);
}

.report-stat-grid-badge {
  align-self: flex-start;
  padding: 2px 8px;
  margin-top: 0.25rem;
  border-radius: 4px;
  background: var(--blue-100);
  color: var(--blue-900);
  font-family: var(--font-family);
  font-size: var(--font-xs);
  font-weight: var(--font-semibold);
}

/* WATERFALL CHART ────────────────────────────────────────────────────────────────────────── */
.report-waterfall-wrap {
  position: relative;
  width: 100%;
}

.report-waterfall {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  font-family: var(--font-family);
}

.report-waterfall-gridline {
  stroke: var(--grey-100);
  stroke-width: 1;
}

.report-waterfall-baseline {
  stroke: var(--grey-300);
  stroke-width: 1;
}

.report-waterfall-connector {
  stroke: var(--grey-300);
  stroke-width: 1;
  stroke-dasharray: 4, 3;
}

.report-waterfall-tick {
  fill: var(--grey-500);
  font-size: 10px;
}

.report-waterfall-label {
  fill: var(--grey-600);
  font-size: 11px;
  font-weight: var(--font-semibold);
}

.report-waterfall-label-secondary {
  font-weight: var(--font-regular);
}

.report-waterfall-bar-total            { fill: var(--blue-900); }
.report-waterfall-bar-captured-saving  { fill: var(--green-900); }
.report-waterfall-bar-avoided-exposure { fill: var(--grey-500); }

.report-waterfall-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  margin: 8px 0 0;
  font-size: var(--font-xs);
  color: var(--grey-600);
}
.report-waterfall-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.report-waterfall-legend-swatch {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
}
.report-waterfall-legend-swatch-total            { background: var(--blue-900); }
.report-waterfall-legend-swatch-captured-saving  { background: var(--green-900); }
.report-waterfall-legend-swatch-avoided-exposure { background: var(--grey-500); }

.report-waterfall-value {
  font-size: 11px;
  font-weight: var(--font-bold);
}
.report-waterfall-value-total            { fill: var(--blue-900); }
.report-waterfall-value-captured-saving  { fill: var(--green-900); }
.report-waterfall-value-avoided-exposure { fill: var(--grey-600); }

/* PIE CHART ────────────────────────────────────────────────────────────────────────── */
.report-pie-wrap {
  position: relative;
  width: 100%;
}

.report-pie {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  font-family: var(--font-family);
}

.report-pie-slice {
  /* Fill colours are applied per-element from the JS palette. */
}

.report-pie-leader {
  stroke: var(--grey-400);
  stroke-width: 1;
  fill: none;
}

.report-pie-label {
  fill: var(--slate-900);
  font-size: 11px;
  font-weight: var(--font-semibold);
}

.report-pie-value {
  fill: var(--grey-600);
  font-size: 10px;
  font-weight: var(--font-regular);
}

.report-pie-empty {
  text-align: center;
  color: var(--grey-500);
  font-size: var(--font-sm);
  margin: 24px 0;
}

/* VEGA-LITE CHART ────────────────────────────────────────────────────────────────────────── */
.report-vega-chart {
  width: 100%;
}

.report-vega-chart-svg {
  display: flex;
  justify-content: center;
}

.report-vega-chart-svg svg {
  max-width: 100%;
  height: auto;
}
  
/* Added by Oasis. Embedded under the project header card, the report spans the
   frame's full width: the page around it supplies the gutters. */
.container { min-width: 0; padding: 0 0 1.5rem 0; }
/* The side nav's entries: left aligned, one line each, cut with an ellipsis. */
.report-sidenav-btn > span:first-child { flex: 1; min-width: 0; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
</style>
</head>
<body>
<div class="container"><div class="report-header-container"><div class="report-summary-container"><div class="report-summary-card report-summary-card-positive wide"><div class="label label-positive">Target outcome</div><div class="report-tag report-tag-positive">Playbook v1</div><div class="card-title-container"><div class="card-title"><span class="truncate">$2.9M a year target</span></div><div class="card-subtitle"><span class="card-subtitle">Right-size and re-price the Microsoft EA at the 31 Mar 2027 renewal</span></div></div><div class="card-data-container"><div class="card-data-row"><span class="card-data-row-label">Quantity and edition, from the usage analysis</span><span class="card-data-row-value">$1.9M a year</span></div><div class="card-data-row"><span class="card-data-row-label">Price, at the peer target</span><span class="card-data-row-value">$1.0M a year</span></div><div class="card-data-row"><span class="card-data-row-label">Snapshot&apos;s rough estimate</span><span class="card-data-row-value">$2.4M to $3.4M</span></div></div></div><div class="report-summary-card report-summary-card-warning wide"><div class="label label-warning">Round 1</div><div class="card-title-container"><div class="card-title"><span class="truncate">In progress</span></div><div class="card-subtitle"><span class="card-subtitle">Opening position sent 29 Oct 2026; vendor response expected 12 Nov</span></div></div><div class="card-description"><p>Notice by <strong>31 Dec 2026</strong>. Owner reviews due 21 Nov settle the quantity before round 2.</p></div></div></div><div class="report-utility-nav-container"><button class="report-utility-nav-btn active" data-tab-id="execute">How to execute</button><button class="report-utility-nav-btn" data-tab-id="usage">Usage analysis</button><button class="report-utility-nav-btn" data-tab-id="about">About the deal</button></div></div><div class="report-container-row"><div class="report-sidenav-container"><div class="report-tab-sidenav" data-tab-id="execute"><button class="report-sidenav-btn" data-target="moves"><span>Negotiation moves</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="scenarios"><span>Estimated savings against today</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="asks"><span>Recommended commercial ask package</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="protections"><span>Protections to secure before signature</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button></div><div class="report-tab-sidenav" data-tab-id="usage" hidden=""><button class="report-sidenav-btn" data-target="recommendation"><span>Recommendation</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="quantity"><span>How the quantity was set</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="edition-mix"><span>Edition mix</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="range"><span>What is kept, and the range</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="asks"><span>What to ask for</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="vendor-response"><span>Vendor response and the net saving</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="after-signing"><span>After signing</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="evidence"><span>Evidence</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button></div><div class="report-tab-sidenav" data-tab-id="about" hidden=""><button class="report-sidenav-btn" data-target="deal"><span>The deal today</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="benchmark"><span>Market benchmark</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="risks"><span>Risks</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button></div></div><div class="report-container"><div class="report-tab-content" data-tab-id="execute"><div class="report-section" id="moves"><div class="report-section-subheader"><p class="report-section-title">Negotiation moves</p><p class="report-section-description">In the order to play them: the quantity is settled on evidence before any price is discussed.</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 24%"><col style="width: 38%"><col></colgroup><thead><tr><th><span>Move</span></th><th><span>Why it works</span></th><th><span>What to do</span></th></tr></thead><tbody><tr><td><span class="report-table-position">1. Settle the quantity first</span></td><td><span>The usage analysis makes 10,400 licenses a matter of record: 550 never assigned, 560 held by departed staff, 1,240 confirmed not needed, 350 hires. Microsoft&apos;s own usage reports show the same.</span></td><td><span>Table the reconciliation from bought to needed as the order, before any discussion of price. Do not let the count be traded for a discount.</span></td></tr><tr><td><span class="report-table-position">2. Release the empty seats unconditionally</span></td><td><span>1,110 seats are not in anyone&apos;s hands; nothing about them is negotiable and conceding them costs Microsoft nothing to accept.</span></td><td><span>Take the never-assigned and departed seats off the order in round 1 and move the conversation to the remaining 2,100 inactive ones.</span></td></tr><tr><td><span class="report-table-position">3. Hold the E5 count in the range</span></td><td><span>The owner reviews due 21 Nov settle 1,150 seats; each 100 released is $68K a year at today&apos;s price.</span></td><td><span>Quote the E5 count as 7,150 to 8,600 until the reviews land, then fix it in round 2. Do not round up to close early.</span></td></tr><tr><td><span class="report-table-position">4. Price the smaller order at the peer target</span></td><td><span>23 comparable agreements put the median at $49.50 and the lower quartile at $46.80; $57.00 sits above the upper quartile.</span></td><td><span>Ask $46.80 as the opening position with the benchmark on the table; settle at $48.00; walk at $49.50 only with the reduction right.</span></td></tr><tr><td><span class="report-table-position">5. Keep the E3 mix as currency</span></td><td><span>2,100 users use no E5-only feature; moving them is $0.5M a year on its own, and Microsoft would rather keep them on E5 at a lower price.</span></td><td><span>Hold the E3 move in reserve: trade it back for a better E5 price only if the discount tier or the price moves below target.</span></td></tr><tr><td><span class="report-table-position">6. Make MCA-E the credible alternative</span></td><td><span>Leaving Microsoft 365 is not credible in the window; moving the licensing vehicle is, and an EA renewal is worth more to the field than a shrinking MCA-E.</span></td><td><span>Request the MCA-E price comparison (in progress) and reference it in round 2 without naming it as the plan.</span></td></tr><tr><td><span class="report-table-position">7. Keep Copilot out of the EA</span></td><td><span>460 of 1,000 Copilot seats are inactive; bundling it protects Microsoft&apos;s total, not ours.</span></td><td><span>Decline any bundle. If Copilot stays, re-size it to 600 in a separate order with its own usage clause.</span></td></tr><tr><td><span class="report-table-position">8. Hold the term until late</span></td><td><span>A three-year commitment is the strongest concession left and should buy price protection, not be assumed.</span></td><td><span>Negotiate the first-year economics on a one-year basis, then ask what the three-year term earns: the price lock and the reduction right.</span></td></tr></tbody></table></div><div class="report-section-card-container"><div class="report-table-wrapper"><p class="report-table-title">Rounds</p><table class="report-table"><colgroup><col style="width: 24%"><col style="width: 26%"><col></colgroup><thead><tr><th><span>Round</span></th><th><span>When</span></th><th><span>Goal</span></th></tr></thead><tbody><tr class="report-table-row-focus"><td><span class="report-table-position">Round 1: opening position</span></td><td><span>Sent 29 Oct 2026; response expected 12 Nov</span></td><td><span>The quantity of 10,400 on the table with the evidence; $46.80 and the reduction right asked. No Copilot.</span></td></tr><tr><td><span class="report-table-position">Round 2: counter</span></td><td><span>After the owner reviews, from 21 Nov</span></td><td><span>Fix the E5 count within the range; trade the E3 mix against price if the discount tier is raised.</span></td></tr><tr><td><span class="report-table-position">Round 3: close</span></td><td><span>Before the notice date, 31 Dec 2026</span></td><td><span>Sign at or below $48.00 with the reduction right and the price lock; send notice on the current agreement if Microsoft holds at $57.00.</span></td></tr></tbody></table></div></div></div><div class="report-section" id="scenarios"><div class="report-section-subheader-row"><p class="report-section-title">Estimated savings against today</p><p class="report-section-stat positive">$2.9M target</p></div><div class="report-section-card-container"><div class="report-table-wrapper"><p class="report-table-title">10,400 licenses (8,300 E5 and 2,100 E3) against 12,400 E5 at $57.00, $8.5M a year</p><table class="report-table"><colgroup><col><col style="width: 20%"><col style="width: 16%"><col style="width: 22%"></colgroup><thead><tr><th><span>Position</span></th><th class="report-table-cell-numeric"><span>E5 / E3 a user a month</span></th><th class="report-table-cell-numeric"><span>Annual cost</span></th><th class="report-table-cell-numeric"><span>Annual saving</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Opening ask</span></td><td class="report-table-cell-numeric"><span>$46.80 / $30.00</span></td><td class="report-table-cell-numeric"><span>$5.4M</span></td><td class="report-table-cell-numeric"><span>$3.1M (36%)</span></td></tr><tr class="report-table-row-focus"><td><span class="report-table-position">Target</span></td><td class="report-table-cell-numeric"><span>$48.00 / $32.00</span></td><td class="report-table-cell-numeric"><span>$5.6M</span></td><td class="report-table-cell-numeric"><span class="positive">$2.9M (34%)</span></td></tr><tr><td><span class="report-table-position">Fallback</span></td><td class="report-table-cell-numeric"><span>$49.50 / $34.00</span></td><td class="report-table-cell-numeric"><span>$5.8M</span></td><td class="report-table-cell-numeric"><span>$2.7M (32%)</span></td></tr><tr><td><span class="report-table-position">Quantity only, today&apos;s prices</span></td><td class="report-table-cell-numeric"><span>$57.00 / $36.00</span></td><td class="report-table-cell-numeric"><span>$6.6M</span></td><td class="report-table-cell-numeric"><span>$1.9M (22%)</span></td></tr></tbody></table></div></div><div class="report-section-card-container"><div class="report-description-list"><span class="label">Supporting arguments</span><div class="report-description-list-content"><div class="card-title"><span class="truncate">The quantity is evidenced seat by seat:</span></div><div class="card-description"><p>Tenant activity to 24 Oct 2026 and Workday headcount reconcile 12,400 bought to 10,400 needed, with 94% of users matched to the contract. The reduction is a fact to record, not a position to defend.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">The price is above the peer market:</span></div><div class="card-description"><p>Over 23 comparable agreements of 8,000 to 15,000 E5 seats signed in the last 18 months, the median is $49.50 and the lower quartile $46.80. At $57.00 the agreement sits above the upper quartile of $53.20.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">Microsoft has the same data:</span></div><div class="card-description"><p>The Microsoft 365 admin center usage reports the account team can see show the inactive seats; there is no version of the numbers to dispute.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">The timing favors us:</span></div><div class="card-description"><p>The notice date falls in Microsoft&apos;s fiscal Q2 and the term ends in Q3, when field teams carry quota pressure on renewals and a signed EA counts more than a shrinking MCA-E.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">A lower unit price is not the goal on its own:</span></div><div class="card-description"><p>The saving comes from the smaller order first; a discount offered against keeping 12,400 licenses is worth less than the target and should be declined.</p></div></div></div></div></div><div class="report-section" id="asks"><div class="report-section-subheader-row"><p class="report-section-title">Recommended commercial ask package</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 20%"><col style="width: 32%"><col style="width: 32%"><col></colgroup><thead><tr><th><span>Commercial ask</span></th><th><span>Recommended position</span></th><th><span>Why it matters</span></th><th><span>Likelihood</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Quantity at renewal</span></td><td><span>10,400 licenses: 8,300 E5 and 2,100 E3, fixed from the usage analysis.</span></td><td><span>The whole of the $1.9M usage lever.</span></td><td><span>High</span></td></tr><tr><td><span class="report-table-position">Unit price</span></td><td><span>E5 $48.00 and E3 $32.00; opening $46.80 / $30.00; fallback $49.50 / $34.00.</span></td><td><span>The $1.0M price lever; the fallback keeps $0.8M of it.</span></td><td><span>Medium to high</span></td></tr><tr><td><span class="report-table-position">Right to reduce</span></td><td><span>10% a year during the term without penalty or repricing of the remainder.</span></td><td><span>The agreement allows true-ups only today; the seats confirmed inactive after signing would otherwise stay paid for three years.</span></td><td><span>Medium</span></td></tr><tr><td><span class="report-table-position">Price lock</span></td><td><span>The negotiated price fixed for the term, through the 1 Apr 2027 list rise, with 0% escalation.</span></td><td><span>Microsoft has announced a 5% list increase the day after the term ends.</span></td><td><span>High</span></td></tr><tr><td><span class="report-table-position">Renewal cap</span></td><td><span>Increase at the next renewal capped at 3%; 5% as the fallback.</span></td><td><span>Prevents the saving being recovered at the 2030 renewal.</span></td><td><span>Medium to high</span></td></tr><tr><td><span class="report-table-position">Add-ons priced with the EA</span></td><td><span>Premier support ($0.6M a year) priced in the same negotiation, separately removable.</span></td><td><span>It renews on the same date and would otherwise be priced after the leverage is spent.</span></td><td><span>High</span></td></tr><tr><td><span class="report-table-position">Copilot kept separate</span></td><td><span>Out of the EA; if retained, 600 seats in a separate order with a usage clause.</span></td><td><span>460 of 1,000 seats are inactive; a bundle protects Microsoft&apos;s total.</span></td><td><span>High</span></td></tr><tr><td><span class="report-table-position">SKU and entitlement protection</span></td><td><span>Negotiated economics and material functionality preserved across successor SKUs.</span></td><td><span>Protects against repackaging during the term.</span></td><td><span>Medium</span></td></tr></tbody></table></div></div><div class="report-section" id="protections"><div class="report-section-subheader-row"><p class="report-section-title">Protections to secure before signature</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 22%"><col style="width: 36%"><col></colgroup><thead><tr><th><span>Protection</span></th><th><span>Current risk</span></th><th><span>Recommended structure</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Term and in-term pricing</span></td><td><span>No price protection: nothing caps the price at the anniversary and the 5% list rise applies from 1 Apr 2027.</span></td><td><span>The price fixed for the three-year term with 0% escalation, stated in the Customer Price Sheet.</span></td></tr><tr><td><span class="report-table-position">Quantity flexibility</span></td><td><span>True-ups only: a seat kept at the renewal is paid for three years whatever its use.</span></td><td><span>A 10% annual true-down at each anniversary, without repricing the remaining licenses.</span></td></tr><tr><td><span class="report-table-position">Renewal protection</span></td><td><span>The saving could be recovered through the 2030 renewal price.</span></td><td><span>A renewal increase cap of 3%, 5% as the fallback; no automatic renewal of the quantity.</span></td></tr><tr><td><span class="report-table-position">Edition mix</span></td><td><span>The 2,100 E3 users could be moved back to E5 by a future bundle or feature change.</span></td><td><span>The E3 entitlement and its price stated per user, with the right to move users between editions at the anniversary.</span></td></tr><tr><td><span class="report-table-position">Add-ons</span></td><td><span>Premier support renews on the same date under a separate agreement, after the EA is signed.</span></td><td><span>Priced in the EA negotiation; separately removable without changing the software economics.</span></td></tr><tr><td><span class="report-table-position">Contract precedence</span></td><td><span>The Customer Price Sheet and the amendments referenced in the enrollment have not been reviewed.</span></td><td><span>The executed enrollment and price sheet control over online terms; every referenced amendment listed and reviewed before signature.</span></td></tr></tbody></table></div></div></div><div class="report-tab-content" data-tab-id="usage" hidden=""><div class="report-section" id="recommendation"><div class="report-section-header-column"><div class="report-section-subheader-row"><p class="report-section-title">Recommendation</p><p class="report-section-stat positive">10,400 licenses</p></div><div class="card-description"><p>Microsoft 365 renewal: how much to buy and at which edition. Prepared 28 Oct 2026, usage to 24 Oct 2026.</p></div></div><div class="report-section-card-container"><div class="report-card callout"><div class="card-title-row"><span class="card-title">Basis</span></div><div class="card-description"><p>Based on usage to 24 Oct 2026; <strong>94% of users matched</strong> to the contract. Seats under owner review (1,150) are kept in the count.</p></div></div></div><div class="report-section-card-container"><p class="report-text">Renew <strong>10,400 licenses</strong>, down from 12,400: 8,300 E5 and 2,100 E3. At current prices that is $6.6M a year against $8.5M today, a saving of <strong>$1.9M</strong>; net after the vendor&apos;s expected response, <strong>$1.6M to $1.7M</strong>. The price lever adds $1.0M on top (see the playbook), for a target of $2.9M a year.</p></div><div class="report-section-card-container"><div class="report-stat-grid" style="grid-template-columns: repeat(4, minmax(0, 1fr));"><div class="report-stat-grid-item"><span class="report-stat-grid-label">Licenses next term</span><span class="report-stat-grid-value">10,400</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Saving a year</span><span class="report-stat-grid-value positive">$1.9M</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Days to notice</span><span class="report-stat-grid-value warning">62</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Owner reviews due</span><span class="report-stat-grid-value">21 Nov</span></div></div></div></div><div class="report-section" id="quantity"><div class="report-section-subheader-row"><p class="report-section-title">How the quantity was set</p></div><div class="report-section-card-container"><p class="report-text">From licenses bought to licenses needed: what was never assigned and what departed staff still hold goes now; what the function owners confirmed as not needed goes at renewal; the headcount plan adds back the hires. Seats inactive but <strong>still under owner review stay in the count</strong>.</p></div><div class="report-section-card-container"><div class="report-table-wrapper"><p class="report-table-title">From licenses bought to licenses needed</p><table class="report-table"><colgroup><col><col style="width: 20%"><col></colgroup><thead><tr><th><span>Step</span></th><th class="report-table-cell-numeric"><span>Licenses</span></th><th><span>Source</span></th></tr></thead><tbody><tr class="report-table-row-total"><td><span class="report-table-position">Bought</span></td><td class="report-table-cell-numeric"><span>12,400</span></td><td><span>Enterprise Agreement, 12 Sep 2026 extraction</span></td></tr><tr><td><span class="report-table-position">Never assigned</span></td><td class="report-table-cell-numeric"><span>−550</span></td><td><span>M365 admin center, 25 Oct 2026</span></td></tr><tr><td><span class="report-table-position">Held by departed staff</span></td><td class="report-table-cell-numeric"><span>−560</span></td><td><span>Workday headcount, 20 Oct 2026</span></td></tr><tr><td><span class="report-table-position">Inactive and confirmed not needed by the owner</span></td><td class="report-table-cell-numeric"><span>−1,240</span></td><td><span>Owner reviews confirmed 24 Oct (three functions)</span></td></tr><tr><td><span class="report-table-position">Planned hires</span></td><td class="report-table-cell-numeric"><span>+350</span></td><td><span>Headcount plan to 31 Mar 2028</span></td></tr><tr class="report-table-row-focus"><td><span class="report-table-position">Needed next term</span></td><td class="report-table-cell-numeric"><span>10,400</span></td><td><span>8,300 E5 and 2,100 E3</span></td></tr></tbody></table></div></div></div><div class="report-section" id="edition-mix"><div class="report-section-subheader-row"><p class="report-section-title">Edition mix</p><p class="report-section-stat">2,100 fit E3</p></div><div class="report-section-card-container"><p class="report-text"><strong>2,100 active users</strong> used none of the E5-only features in the 90 days to 24 Oct 2026 (Defender and Purview, Teams Phone, audio conferencing, Power BI Pro, advanced eDiscovery). They fit E3 at $36 a user a month instead of $57. The remaining 6,800 used at least one E5-only feature.</p></div><div class="report-section-card-container"><div class="report-table-wrapper"><p class="report-table-title">By function</p><table class="report-table"><thead><tr><th><span>Function</span></th><th class="report-table-cell-numeric"><span>Active users</span></th><th class="report-table-cell-numeric"><span>Fit E3</span></th><th class="report-table-cell-numeric"><span>Keep E5</span></th><th class="report-table-cell-numeric"><span>Awaiting review</span></th><th><span>Review status</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Client services</span></td><td class="report-table-cell-numeric"><span>2,360</span></td><td class="report-table-cell-numeric"><span>900</span></td><td class="report-table-cell-numeric"><span>1,460</span></td><td class="report-table-cell-numeric"><span>510</span></td><td><span class="warning">Due 21 Nov</span></td></tr><tr><td><span class="report-table-position">Advisory and wealth</span></td><td class="report-table-cell-numeric"><span>2,050</span></td><td class="report-table-cell-numeric"><span>300</span></td><td class="report-table-cell-numeric"><span>1,750</span></td><td class="report-table-cell-numeric"><span>0</span></td><td><span class="positive">Confirmed 24 Oct</span></td></tr><tr><td><span class="report-table-position">Technology</span></td><td class="report-table-cell-numeric"><span>2,300</span></td><td class="report-table-cell-numeric"><span>100</span></td><td class="report-table-cell-numeric"><span>2,200</span></td><td class="report-table-cell-numeric"><span>0</span></td><td><span class="positive">Confirmed 24 Oct</span></td></tr><tr><td><span class="report-table-position">Operations</span></td><td class="report-table-cell-numeric"><span>1,500</span></td><td class="report-table-cell-numeric"><span>600</span></td><td class="report-table-cell-numeric"><span>900</span></td><td class="report-table-cell-numeric"><span>640</span></td><td><span class="warning">Due 21 Nov</span></td></tr><tr><td><span class="report-table-position">Corporate functions</span></td><td class="report-table-cell-numeric"><span>690</span></td><td class="report-table-cell-numeric"><span>200</span></td><td class="report-table-cell-numeric"><span>490</span></td><td class="report-table-cell-numeric"><span>0</span></td><td><span class="positive">Confirmed 24 Oct</span></td></tr><tr class="report-table-row-total"><td><span class="report-table-position">Total</span></td><td class="report-table-cell-numeric"><span>8,900</span></td><td class="report-table-cell-numeric"><span>2,100</span></td><td class="report-table-cell-numeric"><span>6,800</span></td><td class="report-table-cell-numeric"><span>1,150</span></td><td><span></span></td></tr></tbody></table></div></div><div class="report-section-card-container"><p class="report-text">The 8,300 E5 is the 6,800 active E5 users, plus the 1,150 seats under review, plus the 350 planned hires.</p></div></div><div class="report-section" id="range"><div class="report-section-subheader-row"><p class="report-section-title">What is kept, and the range</p></div><div class="report-section-card-container"><div class="report-metric-list"><div class="report-metric-list-row"><span class="report-metric-list-label">All 1,150 reviewed seats released</span><span class="report-metric-list-value">7,150 E5</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">As recommended</span><span class="report-metric-list-value positive">8,300 E5</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">All kept, plus 300 hired ahead of plan</span><span class="report-metric-list-value">8,600 E5</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Each 100 seats released</span><span class="report-metric-list-value">$68K a year</span></div></div></div><div class="report-section-card-container"><p class="report-text">The E3 count (2,100) does not move with the reviews: those users are active, only their edition changes. The two outstanding reviews (Client services 510, Operations 640) decide where in the range the renewal lands.</p></div></div><div class="report-section" id="asks"><div class="report-section-subheader-row"><p class="report-section-title">What to ask for</p></div><div class="report-section-card-container"><div class="report-card banner negative"><div class="card-title-row"><span class="card-title negative">Reduce the quantity to 10,400 at renewal</span><span class="report-tag report-tag-negative">Quantity</span></div><div class="card-description"><p>8,300 E5 and 2,100 E3, with the reviewed seats settled before the order.</p></div></div><div class="report-card banner negative"><div class="card-title-row"><span class="card-title negative">A right to reduce during the term</span><span class="report-tag report-tag-negative">Terms</span></div><div class="card-description"><p>The contract review found no right to reduce and no price protection. Ask for 10% a year without penalty.</p></div></div><div class="report-card banner warning"><div class="card-title-row"><span class="card-title warning">An E3 mix</span><span class="report-tag report-tag-warning">Edition</span></div><div class="card-description"><p>2,100 users at $36 instead of $57, with the right to move users between editions at the anniversary.</p></div></div><div class="report-card banner warning"><div class="card-title-row"><span class="card-title warning">Price E5 at the peer target</span><span class="report-tag report-tag-warning">Price</span></div><div class="card-description"><p>$48.00 against $57.00 today; the peer median is $49.50 over 23 agreements.</p></div></div><div class="report-card banner warning"><div class="card-title-row"><span class="card-title warning">Protection against the announced list rise</span><span class="report-tag report-tag-warning">Terms</span></div><div class="card-description"><p>Microsoft raises list prices 5% from 1 Apr 2027, the day after the renewal. Fix the price for the term.</p></div></div></div></div><div class="report-section" id="vendor-response"><div class="report-section-subheader-row"><p class="report-section-title">Vendor response and the net saving</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col><col style="width: 18%"><col style="width: 22%"></colgroup><thead><tr><th><span>Lever</span></th><th class="report-table-cell-numeric"><span>Gross</span></th><th class="report-table-cell-numeric"><span>Net after response</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Right-size: quantity and edition</span></td><td class="report-table-cell-numeric"><span>$1.9M</span></td><td class="report-table-cell-numeric"><span>$1.6M to $1.7M</span></td></tr><tr><td><span class="report-table-position">Price: E5 at $48</span></td><td class="report-table-cell-numeric"><span>$1.1M</span></td><td class="report-table-cell-numeric"><span>$1.0M</span></td></tr><tr class="report-table-row-total"><td><span class="report-table-position">Total</span></td><td class="report-table-cell-numeric"><span>$3.0M</span></td><td class="report-table-cell-numeric"><span class="positive">$2.5M to $2.7M</span></td></tr></tbody></table></div><div class="report-section-card-container"><div class="report-description-list"><span class="label">Expected vendor moves</span><div class="report-description-list-content"><div class="card-title"><span class="truncate">Re-pricing of the retained E5:</span></div><div class="card-description"><p>The per-user discount shrinks as the quantity falls; part of the right-size saving comes back as price.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">Loss of a discount tier:</span></div><div class="card-description"><p>Below 10,000 E5 the agreement drops a tier; the E3 mix keeps the total above 10,000 licenses.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">Bundling Copilot:</span></div><div class="card-description"><p>Expect an offer to fold Copilot into the renewal to hold the total; the 460 inactive Copilot seats say no.</p></div></div></div></div><div class="report-section-card-container"><p class="report-text">Figures are illustrative; the playbook carries the negotiation plan and the rounds.</p></div></div><div class="report-section" id="after-signing"><div class="report-section-subheader-row"><p class="report-section-title">After signing</p></div><div class="report-section-card-container"><p class="report-text">Check the first invoice, due 30 Jun 2027, against the signed quantities and unit prices.</p></div><div class="report-section-card-container"><p class="report-text">Re-run this analysis 90 days after the renewal date. The owner reviews become the baseline for the next term&apos;s reduction right.</p></div></div><div class="report-section" id="evidence"><div class="report-section-subheader-row"><p class="report-section-title">Evidence</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 30%"><col style="width: 20%"><col></colgroup><thead><tr><th><span>Source</span></th><th><span>As of</span></th><th><span>Supports</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Microsoft Entra sign-ins</span></td><td><span>29 Oct 2026, daily</span></td><td><span>Last sign-in and days inactive per user</span></td></tr><tr><td><span class="report-table-position">Microsoft 365 admin center usage reports</span></td><td><span>25 Oct 2026</span></td><td><span>Licenses assigned, E5 feature use per user (94% matched to the contract)</span></td></tr><tr><td><span class="report-table-position">Workday headcount</span></td><td><span>20 Oct 2026</span></td><td><span>Employment status, departures, planned hires</span></td></tr><tr><td><span class="report-table-position">Coupa AP</span></td><td><span>24 Oct 2026</span></td><td><span>Invoices paid; unit price $57 a user a month</span></td></tr><tr><td><span class="report-table-position">Enterprise Agreement extraction</span></td><td><span>12 Sep 2026</span></td><td><span>Quantities, term, no right to reduce, no price protection</span></td></tr><tr><td><span class="report-table-position">Market data and playbook</span></td><td><span>28 Oct 2026</span></td><td><span>Peer median $49.50 over 23 agreements; target $48.00</span></td></tr></tbody></table></div></div></div><div class="report-tab-content" data-tab-id="about" hidden=""><div class="report-section" id="deal"><div class="report-section-subheader-row"><p class="report-section-title">The deal today</p></div><div class="report-section-card-container"><div class="report-stat-grid" style="grid-template-columns: repeat(6, minmax(0, 1fr));"><div class="report-stat-grid-item"><span class="report-stat-grid-label">Licenses</span><span class="report-stat-grid-value">12,400</span><span class="report-stat-grid-sub">Microsoft 365 E5</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Unit price</span><span class="report-stat-grid-value negative">$57.00</span><span class="report-stat-grid-sub">A user a month; $684 a year</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Annual cost</span><span class="report-stat-grid-value">$8.5M</span><span class="report-stat-grid-sub">Plus $0.6M premier support</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Total contract value</span><span class="report-stat-grid-value">$25.5M</span><span class="report-stat-grid-sub">1 Apr 2024 to 31 Mar 2027</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Notice by</span><span class="report-stat-grid-value warning">31 Dec 2026</span><span class="report-stat-grid-sub">90 days before the term ends</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Utilization</span><span class="report-stat-grid-value warning">72%</span><span class="report-stat-grid-sub">8,900 of 12,400 active in 90 days</span></div></div></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 28%"><col></colgroup><thead><tr><th><span>Item</span></th><th><span>Detail</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Supplier</span></td><td><span>Microsoft</span></td></tr><tr><td><span class="report-table-position">Agreement</span></td><td><span>Enterprise Agreement, Microsoft 365 E5 and add-ons</span></td></tr><tr><td><span class="report-table-position">Deal type</span></td><td><span>Renewal, year 3 of 3</span></td></tr><tr><td><span class="report-table-position">Term</span></td><td><span>1 Apr 2024 to 31 Mar 2027</span></td></tr><tr><td><span class="report-table-position">Reduction right</span></td><td><span>None: true-ups only during the term</span></td></tr><tr><td><span class="report-table-position">Price protection</span></td><td><span>None: a 5% list rise is announced from 1 Apr 2027</span></td></tr><tr><td><span class="report-table-position">Negotiation owner</span></td><td><span>Rachel Sato</span></td></tr></tbody></table></div></div><div class="report-section" id="benchmark"><div class="report-section-subheader-row"><p class="report-section-title">Market benchmark</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col><col style="width: 30%"></colgroup><thead><tr><th><span>Peer position</span></th><th class="report-table-cell-numeric"><span>E5 a user a month</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Lower quartile</span></td><td class="report-table-cell-numeric"><span>$46.80</span></td></tr><tr><td><span class="report-table-position">Median</span></td><td class="report-table-cell-numeric"><span>$49.50</span></td></tr><tr><td><span class="report-table-position">Upper quartile</span></td><td class="report-table-cell-numeric"><span>$53.20</span></td></tr><tr><td><span class="report-table-position">You today</span></td><td class="report-table-cell-numeric"><span class="negative">$57.00</span></td></tr><tr class="report-table-row-focus"><td><span class="report-table-position">Target</span></td><td class="report-table-cell-numeric"><span class="positive">$48.00</span></td></tr></tbody></table></div><div class="report-section-card-container"><p class="report-text">23 agreements of 8,000 to 15,000 E5 seats signed in the last 18 months, financial services and professional services.</p></div></div><div class="report-section" id="risks"><div class="report-section-subheader-row"><p class="report-section-title">Risks</p></div><div class="report-section-card-container"><div class="report-card banner negative"><div class="card-title-row"><span class="card-title negative">Microsoft re-prices the retained E5</span><span class="report-tag report-tag-negative">High</span></div><div class="card-description"><p>If the price is held to recover the quantity, the net lands at $2.5M to $2.7M instead of $2.9M; the fallback position already allows for it.</p></div></div><div class="report-card banner warning"><div class="card-title-row"><span class="card-title warning">Owner reviews slip past 21 Nov</span><span class="report-tag report-tag-warning">Medium</span></div><div class="card-description"><p>Escalation to the business owner on 14 Nov, then the CIO office; the E5 range covers both outcomes.</p></div></div><div class="report-card banner warning"><div class="card-title-row"><span class="card-title warning">Discount tier lost below 10,000 E5</span><span class="report-tag report-tag-warning">Medium</span></div><div class="card-description"><p>The E3 mix keeps the total above 10,000 licenses; confirm the tier counts E3.</p></div></div><div class="report-card banner positive"><div class="card-title-row"><span class="card-title positive">Copilot bundled to protect the total</span><span class="report-tag report-tag-positive">Low</span></div><div class="card-description"><p>460 of 1,000 Copilot seats are inactive; re-size to 600 in a separate order or decline the bundle.</p></div></div></div></div></div></div></div></div>
<script>
  const tabButtons = document.querySelectorAll(".report-utility-nav-btn");
  const header = document.querySelector(".report-header-container");

  // The sidenav sticks flush beneath the header (see styles.css). The header's
  // height varies with its content and shrinks as it collapses, so publish its
  // live height and keep the sidenav in sync — ResizeObserver fires through the
  // collapse transition too, so the sidenav tracks the header frame by frame.
  function publishHeaderHeight() {
    if (!header) return;
    document.documentElement.style.setProperty(
      "--report-header-height",
      header.offsetHeight + "px"
    );
  }

  if (header && "ResizeObserver" in window) {
    new ResizeObserver(publishHeaderHeight).observe(header);
  }
  publishHeaderHeight();

  // Measure the header's expanded and compact heights so the collapse
  // thresholds adapt to the report (summary-card content varies the height).
  // Toggling with the transition suppressed makes the probe instant and
  // invisible. Re-measured on resize / once webfonts settle, since both shift
  // the height.
  let expandedHeaderH = 0;
  let compactHeaderH = 0;
  function measureHeaderHeights() {
    if (!header) return;
    const wasCompact = header.classList.contains("is-compact");
    header.classList.add("is-anim-suppressed");
    header.classList.remove("is-compact");
    expandedHeaderH = header.offsetHeight;
    header.classList.add("is-compact");
    compactHeaderH = header.offsetHeight;
    header.classList.toggle("is-compact", wasCompact);
    header.classList.remove("is-anim-suppressed");
  }
  measureHeaderHeights();
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(measureHeaderHeights);
  }

  // Header collapse controller. The header is expanded at the very top of the
  // report and compact once you've scrolled down into the content. Run on
  // every scroll and after programmatic scrolls (nav-item clicks, tab
  // switches) so both paths behave identically.
  //
  // Driven by scrollY with hysteresis: expand near the very top, collapse once
  // you've scrolled past the header. The catch is that collapsing shrinks the
  // header by \`delta\`, and browser scroll anchoring then shifts scrollY by that
  // same delta to keep your place — so the gap between the two thresholds must
  // exceed \`delta\`, otherwise the post-toggle anchoring shift lands past the
  // opposite threshold and the header flickers. We collapse \`delta\` + margin
  // down and expand within \`margin\` of the top, giving a band of delta + margin.
  const HEADER_MARGIN_PX = 32;
  function updateHeaderCompact() {
    if (!header) return;
    const delta = Math.max(0, expandedHeaderH - compactHeaderH);
    const y = window.pageYOffset;
    if (y <= HEADER_MARGIN_PX) {
      header.classList.remove("is-compact");
    } else if (y >= delta + 2 * HEADER_MARGIN_PX) {
      header.classList.add("is-compact");
    }
  }

  // Scroll a section flush beneath the header (used by nav-item clicks and tab
  // switches). The offset depends on the header height, but landing the scroll
  // *also* changes whether the header is collapsed — and an animated collapse
  // reflows content under the new scroll position, which browser scroll
  // anchoring then chases, drifting us onto the previous section. So: jump,
  // settle the header into its destination state with the collapse animation
  // suppressed (one synchronous reflow, nothing for anchoring to track), then
  // re-pin against the now-final header height. Animation is restored next
  // frame so manual scrolling keeps its smooth collapse.
  function scrollSectionToTop(target) {
    if (!target) return;
    const pin = () =>
      Math.max(0, target.getBoundingClientRect().top + window.pageYOffset - getHeaderOffset());
    window.scrollTo({ top: pin(), behavior: "auto" });
    if (header) header.classList.add("is-anim-suppressed");
    updateHeaderCompact();
    window.scrollTo({ top: pin(), behavior: "auto" });
    if (header) {
      requestAnimationFrame(() => header.classList.remove("is-anim-suppressed"));
    }
  }

  function getHeaderOffset() {
    return header ? header.offsetHeight : 0;
  }

  function updateActiveSidenav() {
    const activePane = document.querySelector('.report-tab-content:not([hidden])');
    if (!activePane) return;

    const sections = activePane.querySelectorAll('.report-section');
    if (!sections.length) return;

    let activeId = sections[0].id;
    const headerOffset = getHeaderOffset() + 24;

    sections.forEach(section => {
      const rect = section.getBoundingClientRect();
      if (rect.top <= headerOffset) {
        activeId = section.id;
      }
    });

    document
      .querySelectorAll('.report-tab-sidenav:not([hidden]) .report-sidenav-btn')
      .forEach(button => {
        button.classList.toggle('active', button.dataset.target === activeId);
      });
  }

  function onScroll() {
    updateHeaderCompact();
    updateActiveSidenav();
  }

  function activateTab(tabId) {
    tabButtons.forEach(button => button.classList.toggle("active", button.dataset.tabId === tabId));

    document.querySelectorAll(".report-tab-sidenav").forEach(element => {
      element.hidden = element.dataset.tabId !== tabId;
    });

    document.querySelectorAll(".report-tab-content").forEach(element => {
      element.hidden = element.dataset.tabId !== tabId;
    });

    document.querySelectorAll(".report-sidenav-btn").forEach(button => button.classList.remove("active"));

    const firstSide = document.querySelector('.report-tab-sidenav:not([hidden]) .report-sidenav-btn');
    if (firstSide) firstSide.classList.add('active');

    const firstSection = document.querySelector('.report-tab-content:not([hidden]) .report-section');
    scrollSectionToTop(firstSection);

    updateActiveSidenav();
  }

  tabButtons.forEach(button => {
    button.addEventListener("click", () => activateTab(button.dataset.tabId));
  });

  const firstExpander = document.querySelector(".report-expander");
  if (firstExpander) firstExpander.setAttribute("open", "");

  const firstSideOnLoad = document.querySelector('.report-tab-sidenav:not([hidden]) .report-sidenav-btn');
  if (firstSideOnLoad) firstSideOnLoad.classList.add('active');

  document.querySelectorAll(".report-sidenav-btn").forEach(button => {
    button.addEventListener("click", () => {
      const target = document.getElementById(button.dataset.target);
      if (!target) return;

      document.querySelectorAll('.report-tab-sidenav:not([hidden]) .report-sidenav-btn').forEach(sideButton => {
        sideButton.classList.remove('active');
      });
      button.classList.add('active');

      scrollSectionToTop(target);
      updateActiveSidenav();
    });
  });

  updateHeaderCompact();
  updateActiveSidenav();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
    measureHeaderHeights();
    onScroll();
  });

    // Fallback colgroup for tables whose schema didn't declare column
    // widths. If the server-side renderer already emitted a <colgroup>
    // (because at least one column in the JSON had a \`width\`), respect
    // it and bail — don't strip and replace.
  document.querySelectorAll('.report-table').forEach((table) => {
    if (table.querySelector(':scope > colgroup')) return;

    const firstRow = table.querySelector('thead tr, tbody tr');
    if (!firstRow) return;

    const columnCount = firstRow.children.length;

    const colgroup = document.createElement('colgroup');

    let widths = [];

    if (columnCount === 2) {
      widths = ['13.75rem', 'auto']; // 220px / rest
    } else if (columnCount === 3) {
      widths = ['13.75rem', '13.125rem', 'auto']; // 220 / 210 / rest
    } else if (columnCount === 4) {
      widths = ['13.75rem', '13.125rem', '13.125rem', 'auto']; // 220 / 210 / 210 / rest
    } else if (columnCount === 5) {
      widths = ['13.75rem', '13.125rem', '12rem', '12rem', '10rem'];
    } else {
      // fallback: first two anchored, rest auto
      widths = Array.from({ length: columnCount }, (_, i) => {
        if (i === 0) return '13.75rem';
        if (i === 1) return '13.125rem';
        return 'auto';
      });
    }

    widths.forEach((width) => {
      const col = document.createElement('col');
      if (width !== 'auto') {
        col.style.width = width;
      }
      colgroup.appendChild(col);
    });

    table.insertBefore(colgroup, table.firstChild);
  });
</script>
</body>
</html>

`;

export const SNAPSHOT_V1 = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Report</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap" rel="stylesheet">
  <style>
/* GLOBAL ────────────────────────────────────────────────────────────────────────── */
:root {
  --container-min-width: 1080px;

  --report-header-expanded-offset: 17.125rem;

  /* FONT FAMILY */
  --font-family: "Inter", sans-serif;

  /* FONT WEIGHTS */
  --font-regular: 400;
  --font-semibold: 600;
  --font-bold: 700;

  /* FONT SIZES */
  --font-xs: 0.625rem;
  --font-sm: 0.75rem;
  --font-md: 0.875rem;
  --font-default: 1rem;
  --font-lg: 1.25rem;
  --font-xl: 1.5rem;

  /* LABEL */
  --label-sm-size: 0.625rem;
  --label-md-size: 0.75rem;

  /* SLATE */
  --slate-900: #141E2E;
  --slate-600: #2F466B;

  /* TEAL */
  --teal-900: #00B2FE;
  --teal-300: #75D6FF;

  /* GREY */
  --grey-900: #404040;
  --grey-600: #666666;
  --grey-500: #999999;
  --grey-400: #BFBFBF;
  --grey-300: #D9D9D9;
  --grey-100: #F0F0F0;
  --grey-50: #F5F5F5;
  --grey-10: #FAFAFA;
  --grey-white: #FFFFFF;

  /* BLUE */
  --blue-900: #006ACC;
  --blue-600: #1890FF;
  --blue-300: #99CEFF;
  --blue-100: #E5F3FF;
  --blue-50: #F0F8FF;


  /* GREEN */
  --green-900: #2FB801;
  --green-600: #5AD81D;
  --green-500: #77E043;
  --green-300: #C1F2A9;
  --green-100: #E6FADC;
  --green-50: #F2FCED;

  /* RED */
  --red-900: #FD3559;
  --red-600: #FF758E;
  --red-300: #FEA8B7;
  --red-100: #FFE6EA;
  --red-50: #FFF0F2;
  --red-10: #FFF7F9;

  /* ORANGE */
  --orange-900: #FF8400;
  --orange-600: #FF981F;
  --orange-300: #FFCA99;
  --orange-100: #FFE8CC;
  --orange-50: #FFF4EB;

  /* MAGENTA */
  --magenta-900: #EA00FF;

  /* SPECIAL */
  --slate-alt-900: #101525;
  --green-alt-900: #124002;
  --green-alt-700: #315724;
  --orange-alt-900: #591E00;
  --orange-alt-700: #663600;
  --red-alt-900: #4D000E;
  --red-alt-700: #660012;

  /* GRADIENT */
  --pink-blue: linear-gradient(90deg, #77ADFF 31.25%, #FF3FEC 100%);
}

body {
  margin: 0;
  background: var(--grey-white);
  font-family: var(--font-family);
  display: flex;
  justify-content: center;
}

.container {
  box-sizing: border-box;
  width: 100%;
  min-width: var(--container-min-width);
  padding: 0 1.5rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

p {
  margin: 0;
}

/* REPORT CONTAINER ────────────────────────────────────────────────────────────────────────── */
.report-container-row {
  display: flex;
  align-items: flex-start;
  background: var(--grey-white);
  gap: 16px;
}

.report-container {
  display: flex;
  flex-direction: column;
  background: var(--grey-white);
  flex: 1 1 0;
  min-width: 0;
  padding-bottom: 80vh;
}

/* REPORT HEADER ────────────────────────────────────────────────────────────────────────── */
.report-header-container {
  display: flex;
  flex-direction: column;
  padding: 0 0 1.25rem 0;
  position: sticky;
  top: 0;
  z-index: 1000;
  gap: 1rem;
  background: var(--grey-white);
  transition:
    padding 0.32s ease,
    gap 0.32s ease,
    box-shadow 0.32s ease;
  will-change: padding, gap;
}


/* REPORT SUMMARY CARDS ────────────────────────────────────────────────────────────────────────── */
.report-summary-container {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  background: var(--grey-white);
  transition: gap 0.22s ease;
}

.report-summary-card {
  --card-bg: var(--grey-10);
  --card-border: var(--grey-300);

  background: var(--card-bg);
  border-bottom: 1px solid var(--card-border);

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem 1rem 0.75rem 1rem;
  gap: 0.5rem;
  border-radius: 0.5rem;
  min-width: 0;
  transition:
    padding 0.32s ease,
    gap 0.32s ease,
    border-radius 0.32s ease,
    background-color 0.32s ease,
    border-color 0.32s ease;
}

.report-summary-card.wide {
  flex: 5;
}

.report-summary-card.narrow {
  flex: 2.5;
}

.report-summary-card-positive {
  --card-bg: var(--green-50);
  --card-border: var(--green-500);
}

.report-summary-card-warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-summary-card-negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

/* Collapse instantly (no animation) while a nav-item click / tab switch
   re-pins the scroll, so the header height settles in one synchronous reflow
   that browser scroll anchoring can't chase off the target. Restored next
   frame, so manual scrolling keeps its smooth collapse. */
.report-header-container.is-anim-suppressed,
.report-header-container.is-anim-suppressed * {
  transition: none !important;
}

.report-header-container.is-compact {
  padding: 0 0 1.25rem 0;
  gap: 0.75rem;
}

.report-header-container.is-compact .report-summary-container {
  gap: 0.5rem;
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.report-header-container.is-compact .report-summary-card {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  gap: 0.75rem;
  border-radius: 0.5rem;
}

.report-header-container.is-compact .report-summary-card .card-description,
.report-header-container.is-compact .report-summary-card .card-data-container {
  opacity: 0;
  transform: translateY(-0.25rem);
  pointer-events: none;
  display: none;
}

.report-header-container.is-compact .report-summary-card .label {
  flex: 0 0 auto;
  white-space: nowrap;
}

.report-header-container.is-compact .report-summary-card .report-tag {
  flex: 0 0 auto;
  min-width: auto;
  white-space: nowrap;
}

.report-header-container.is-compact .report-summary-card .card-title-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  width: auto;
  min-width: 0;
  flex: 1 1 auto;
}

.report-header-container.is-compact .report-summary-card .card-subtitle {
  opacity: 0;
  transform: translateY(-0.25rem);
  pointer-events: none;
  display: none;
}

.report-header-container.is-compact .report-summary-card .card-title {
  font-size: var(--font-md);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.report-header-container.is-compact .report-summary-card.narrow .card-title-container {
  justify-content: flex-end;
}

.card-title-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  gap: 0.25rem;
  transition: gap 0.22s ease;
}

.card-title,
.card-subtitle,
.card-description {
  width: 100%;
  min-width: 0;
}

.card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-lg);
  color: var(--slate-600);
  width: 100%;
  transition: font-size 0.22s ease, color 0.22s ease;
}

.report-summary-card-positive .card-title {
  color: var(--green-900);
}

.report-summary-card-warning .card-title {
  color: var(--orange-900);
}

.report-summary-card-negative .card-title {
  color: var(--red-900);
}

.card-subtitle {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  transition: opacity 0.18s ease, transform 0.18s ease, color 0.22s ease;
}

.card-description {
  width: 100%;
  min-width: 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  line-height: 1.6;
  color: var(--grey-600);
  transition: opacity 0.18s ease, transform 0.18s ease, color 0.22s ease;
}

.card-data-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-width: 0;
  gap: 0.5rem;
}

.card-data-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
}

.card-data-row-label {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.card-data-row-value {
  font-family: var(--font-family);
  font-weight: var(--font-bold);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.report-summary-card-positive .card-data-row-value {
  color: var(--green-900);
}

.report-summary-card-warning .card-data-row-value {
  color: var(--orange-900);
}

.report-summary-card-negative .card-data-row-value {
  color: var(--red-900);
}

.truncate {
  display: block;
  width: 100%;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* REPORT SECTION ────────────────────────────────────────────────────────────────────────── */
.report-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
  padding: 16px;
  margin: 0 0 20px 0;
  border-radius: 8px;
  border-bottom: 1px solid var(--grey-300);
  background: var(--grey-10);
}

.report-section-header-column {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  gap: 4px;
}

.report-section-card-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}

.report-section-card-container.row {
  flex-direction: row;
  justify-content: space-between;
}

/* CARD GRID — CSS-grid layout. \`grid-template-columns\` is set inline by the
   CardGrid component (via the \`columns\` prop; defaults to items.length). */
.report-card-grid {
  display: grid;
  gap: 1rem;
  width: 100%;
}

/* BASE CARD — shared layout for section / banner / callout cards */
.report-card {
  --card-bg: var(--grey-white);
  --card-border: var(--grey-300);
  background: var(--card-bg);
  border-bottom: 1px solid var(--card-border);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem 1rem 0.75rem 1rem;
  gap: 0.5rem;
  border-radius: 0.5rem;
  width: 100%;
  box-sizing: border-box;
}

.report-card .card-title-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.report-card .card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

/* Inside a card-grid, titles ellipsize to fit fixed-width grid cells. */
.report-card-grid .card-title {
  display: block;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.report-card .card-title-stat {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--green-900);
}

.report-card .card-description,
.report-card .report-text {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  line-height: 1.6;
  color: var(--grey-500);
}

/* BANNER modifier — tighter gap, severity color variants */
.report-card.banner {
  gap: 0.325rem;
}

.report-card.banner.positive {
  --card-bg: var(--green-50);
  --card-border: var(--green-500);
}

.report-card.banner.warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-card.banner.negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

.report-card.banner .card-title.positive {
  color: var(--green-900);
}

.report-card.banner .card-title.warning {
  color: var(--orange-900);
}

.report-card.banner .card-title.negative {
  color: var(--red-900);
}

.report-card.banner .card-description {
  color: var(--grey-600);
}

/* CALLOUT modifier — teal accent, smaller title */
.report-card.callout {
  --card-border: var(--teal-300);
  gap: 0.325rem;
}

.report-card.callout.warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-card.callout.negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

.report-card.callout .card-title {
  font-size: var(--font-sm);
  color: var(--teal-900);
}

.report-card.callout .card-title.warning {
  color: var(--orange-900);
}

.report-card.callout .card-title.negative {
  color: var(--red-900);
}

/* BLOCKQUOTE — verbatim quoted text (e.g. suggested contract language) */
.report-blockquote {
  margin: 0;
  padding: 0.5rem 0.7rem;
  background: var(--grey-50);
  border-left: 2px solid var(--grey-300);
  border-radius: 0 4px 4px 0;
  color: var(--grey-600);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: var(--font-xs);
  line-height: 1.6;
  white-space: pre-wrap;
}

.report-section-subheader {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 4px;
  align-self: stretch;
  padding: 0 2px;
}

.report-section-subheader-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  align-self: stretch;
  padding: 0 2px;
}

.report-section-title {
  display: flex;
  align-items: center;
  align-self: stretch;
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

.report-section-description {
  display: flex;
  align-items: center;
  max-width: 900px;
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.report-section-stat {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

.report-section-stat.positive {
  color: var(--green-900);
}

.report-section-stat.warning {
  color: var(--orange-900);
}

.report-section-stat.negative {
  color: var(--red-900);
}

/* REPORT EXPANDERS ────────────────────────────────────────────────────────────────────────── */
.report-expander {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  font-family: var(--font-family);
  border-bottom: 0.0625rem solid var(--grey-300);
  border-radius: 0.375rem;
  overflow: hidden;
  background: var(--grey-white);
}

/* HEADER BASE */
.report-expander summary {
  list-style: none;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-self: stretch;
  align-items: center;
  padding: 0.5rem 1rem;
  background: var(--grey-white);
  transition: background 0.2s ease;
}

/* HOVER ONLY WHEN CLOSED */
.report-expander:not([open]) summary:hover {
  background: var(--blue-600);
}

/* TEXT + ICON WHITE ON HOVER */
.report-expander:not([open]) summary:hover .report-title,
.report-expander:not([open]) summary:hover .report-subtitle,
.report-expander:not([open]) summary:hover .report-icon {
  color: var(--grey-white);
}

/* BORDER CHANGE */
.report-expander:not([open]):hover {
  border-bottom: 0.0625rem solid var(--blue-600);
}

/* OPEN STATE */
.report-expander[open] summary {
  background: var(--blue-600);
}

/* TEXT + ICON WHITE WHEN OPEN */
.report-expander[open] .report-title,
.report-expander[open] .report-subtitle,
.report-expander[open] .report-icon {
  color: var(--grey-white);
}

/* TEXT BLOCK */
.report-expander-text {
  display: flex;
  flex-direction: column;
}

/* TYPOGRAPHY */
.report-title {
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-bold);
  font-size: var(--font-md);
  color: var(--blue-600);
}

.report-subtitle {
  margin: 0.125rem 0 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

/* ICON */
.report-icon {
  display: flex;
  width: 0.875rem;
  height: 0.875rem;
  flex: 0 0 1rem;
  color: var(--blue-600);
  transition: transform 0.25s ease, color 0.2s ease;
}

.report-icon svg {
  width: 100%;
  height: 100%;
  display: flex;
}

.report-icon svg path {
  fill: currentColor;
}

.report-expander[open] .report-icon {
  transform: rotate(180deg);
}

/* CONTENT */
.report-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  padding: 0.75rem 1rem;
  background: var(--grey-white);
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-regular);
  line-height: 1.5;
  color: var(--grey-600);
}

/* ANIMATION */
.report-expander .report-content {
  transform-origin: top;
  transform: scaleY(0.95);
  opacity: 0;
  min-height: 200px;
  overflow: hidden;
  transition:
    transform 0.2s ease,
    opacity 0.2s ease,
    max-height 0.25s ease;
}

.report-expander[open] .report-content {
  transform: scaleY(1);
  opacity: 1;
  max-height: 31.25rem;
}

/* REMOVE DEFAULT ARROW */
.report-expander summary {
  list-style: none;
}

.report-expander summary::-webkit-details-marker {
  display: none;
}

.report-expander summary::marker {
  content: "";
}

/* REPORT TAGS ────────────────────────────────────────────────────────────────────────── */
.report-tag {
  display: flex;
  padding: 4px 10px 4px 10px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  min-width: 68px;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  color: var(--grey-white);
}

.report-tag-positive {
  background: var(--green-900);
  color: var(--grey-white);
}

.report-tag-warning {
  background: var(--orange-900);
  color: var(--grey-white);
}

.report-tag-negative {
  background: var(--red-900);
  color: var(--grey-white);
}

.report-tag-alt-positive {
  background: var(--green-100);
  color: var(--green-900);
}

.report-tag-alt-warning {
  background: var(--orange-100);
  color: var(--orange-900);
}

.report-tag-alt-negative {
  background: var(--red-100);
  color: var(--red-900);
}

/* LABELS ────────────────────────────────────────────────────────────────────────── */
.label {
  font-family: var(--font-family);
  font-size: var(--label-sm-size);
  font-weight: var(--font-semibold);
  color: var(--teal-900);
  text-transform: uppercase;
}

.label-positive {
  color: var(--green-alt-700);
}

.label-warning {
  color: var(--orange-alt-700);
}

.label-negative {
  color: var(--red-alt-900);
}

/* REPORT UTILITY NAV ────────────────────────────────────────────────────────────────────────── */
.report-utility-nav-container {
  display: flex;
  padding: 4px;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  border-radius: 6px;
  background: var(--blue-100);
}

/* Inactive */
.report-utility-nav-btn {
  display: flex;
  padding: 0.25rem 1rem;
  justify-content: center;
  align-items: center;
  border-radius: 0.25rem;
  border: none;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  color: var(--blue-600);
  cursor: pointer;

  background: var(--blue-100); /* softer inactive bg */
  color: var(--blue-600);

  box-shadow: none;
  transition: background 0.2s ease, color 0.2s ease;
}

/* HOVER (inactive only) */
.report-utility-nav-btn:hover {
  background: var(--grey-white);
}

/* ACTIVE */
.report-utility-nav-btn.active {
  background: var(--grey-white);
  color: var(--blue-600);
  box-shadow: 0 0.0625rem 0.25rem 0 var(--blue-300);
}

/* prevent hover from overriding active */
.report-utility-nav-btn.active:hover {
  background: var(--grey-white);
}

.report-header-container.is-compact .report-utility-nav-container {
  padding: 0.25rem;
}

.report-header-container.is-compact .report-utility-nav-btn {
  padding: 0.375rem 0.875rem;
}

/* REPORT SIDENAV NAV ────────────────────────────────────────────────────────────────────────── */
.report-sidenav-container {
  display: flex;
  padding-right: 16px;
  flex-direction: column;
  align-items: flex-start;
  align-self: flex-start;
  border-right: 1px solid var(--grey-100);
  gap: 4px;
  position: relative;
}

/* Stick the sidenav flush beneath the sticky header so its top items are
   never hidden behind it. The header's height is dynamic — summary-card
   content varies and the header collapses on scroll — so the runtime JS
   publishes its live border-box height as --report-header-height and the
   sidenav tracks it exactly (through the collapse transition too). The
   expanded-offset token is only the pre-JS fallback. */
.report-container-row .report-sidenav-container {
  position: sticky;
  top: var(--report-header-height, var(--report-header-expanded-offset));
}

/* BASE = inactive */
.report-sidenav-btn {
  display: flex;
  width: 240px;
  padding: 0.5rem 0.5rem 0.5rem 0.625rem;
  justify-content: space-between;
  align-items: center;
  border-radius: 0.375rem;
  border: none;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);

  color: var(--grey-500);
  cursor: pointer;

  background: var(--grey-white);
  transition: background 0.2s ease, color 0.2s ease;
}

/* HOVER (inactive only) */
.report-sidenav-btn:not(.active):hover {
  background: var(--grey-10);
}

/* ACTIVE */
.report-sidenav-btn.active {
  background: var(--grey-10);
  color: var(--slate-600);
}

/* keep active stable */
.report-sidenav-btn.active:hover {
  background: var(--grey-10);
}


/* CHEVRON (reuse report-icon) */
.report-sidenav-btn .report-icon {
  margin-left: auto;
  transform: rotate(0deg); /* DOWN */
  transition: transform 0.2s ease;
}

/* ACTIVE → RIGHT */
.report-sidenav-btn.active .report-icon {
  transform: rotate(-90deg);
}

/* SIZE */
.report-sidenav-btn .report-icon {
  width: 0.625rem;
  height: 0.625rem;

  margin-left: auto;
  display: flex;
  align-items: center;

  /* COLOR (inherits from button) */
  color: inherit;

  /* DEFAULT → DOWN */
  transform: rotate(0deg);
  transition: transform 0.2s ease;
}

/* ACTIVE → RIGHT */
.report-sidenav-btn.active .report-icon {
  transform: rotate(-90deg);
}

/* BANNER BOX — now uses .report-card.banner (see BASE CARD above) */

/* SUPPORTING ARGUMENTS BOX ────────────────────────────────────────────────────────────────────────── */
.report-description-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  width: 100%;
  margin: .5rem 0 0 0;
}

.report-description-list-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
}

.report-description-list .label {
  font-size: var(--label-md-size);
}

.report-description-list .card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--slate-600);
}

.report-description-list .card-description {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-500);
}

/* TABLE ────────────────────────────────────────────────────────────────────────── */
.report-table-wrapper {
  width: 100%;
}

.report-table-title {
  margin: 0 0 0.25rem 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--label-sm-size);
  color: var(--slate-900);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  background: var(--grey-white);
  margin: 0.5rem 0;
}

.report-table th,
.report-table td {
  overflow-wrap: anywhere;
}

/* HEADER */
.report-table thead th {
  padding:  0.5rem 0.75rem;
  text-align: left;
  background: var(--grey-100);

  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--slate-600);
}

/* BODY */
.report-table tbody td {
  padding:  0.5rem 0.75rem;

  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

/* NUMERIC */
.report-table-cell-numeric {
  text-align: left;
  white-space: nowrap;
}

/* POSITION */
.report-table-position {
  font-weight: var(--font-semibold);
  color: var(--slate-600);
}

/* CELL / COLUMN SENTIMENT — applies to spans in both <th> and <td>. */
.report-table .positive { color: var(--green-900); }
.report-table .warning  { color: var(--orange-900); }
.report-table .negative { color: var(--red-900); }

/* TWO-LINE CELL — value on top, muted sub underneath. */
.report-table-cell-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.report-table-cell-sub {
  font-size: var(--font-xs);
  font-weight: var(--font-regular);
  color: var(--grey-500);
}

/* PILL CELL — renders the cell value as a rounded badge. */
.report-table-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  background: var(--grey-100);
  color: var(--grey-600);
}
.report-table-pill-positive { background: var(--green-100); color: var(--green-900); }
.report-table-pill-warning  { background: var(--orange-100); color: var(--orange-900); }
.report-table-pill-negative { background: var(--red-100); color: var(--red-900); }

/* ROW VARIANTS — zebra stripes apply unless a row has its own
   background treatment (focus variant, or row sentiment). */
.report-table tbody tr:nth-child(odd):not(.report-table-row-focus):not(.report-table-row-positive):not(.report-table-row-warning):not(.report-table-row-negative):not(.report-table-row-heading):not(.report-table-row-total) td {
  background: var(--grey-white);
}

.report-table tbody tr:nth-child(even):not(.report-table-row-focus):not(.report-table-row-positive):not(.report-table-row-warning):not(.report-table-row-negative):not(.report-table-row-heading):not(.report-table-row-total) td {
  background: var(--grey-10);
}

.report-table-row-focus td {
  background: var(--blue-100);
}

.report-table-row-positive td {
  background: var(--green-100);
}

.report-table-row-warning td {
  background: var(--orange-100);
}

.report-table-row-negative td {
  background: var(--red-100);
}

/* Total row: bolden every cell (td + nested span override the base
   \`.report-table tbody td\` regular weight and \`.report-table-position\`
   semibold weight). */
.report-table tbody tr.report-table-row-total td,
.report-table tbody tr.report-table-row-total td span {
  font-weight: var(--font-semibold);
}

.report-table tbody tr.report-table-row-total td {
  border-top: 2px solid var(--grey-500);
  background: var(--grey-50);
}

/* Heading row: group-label spanning every column. Uses the same visual
   treatment as the standalone \`.report-heading\` block (uppercase, tight
   tracking, slate-900) with a distinct background so it stands apart
   from the zebra-striped data rows. */
.report-table tbody tr.report-table-row-heading td {
  padding: 0.625rem 0.75rem;
  background: var(--blue-50);
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* VALUE EMPHASIS */
.report-table .report-table-cell-numeric {
  color: var(--grey-600);
}

/* HEADING ─────────────────────────────────────────────────────────────────────── */
.report-heading {
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* TEXT ────────────────────────────────────────────────────────────────────────── */
/* Text is a single paragraph — rendered as \`<p class="report-text">\`. */
.report-text {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
  line-height: 1.6;
  width: 100%;
  margin: 0;
}

/* Sentiment tint. Specificity (0,2,0) beats the in-card
   \`.report-card .report-text\` color override (0,2,0) via order. */
.report-text.positive { color: var(--green-900); }
.report-text.warning  { color: var(--orange-900); }
.report-text.negative { color: var(--red-900); }

/* METRIC LIST ────────────────────────────────────────────────────────────────────────── */
.report-metric-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--grey-white);
  border: 0.0625rem solid var(--grey-300);
  border-radius: 0.5rem;
  width: 100%;
}

.report-metric-list-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-family: var(--font-family);
  font-size: var(--font-sm);
}

.report-metric-list-label {
  color: var(--grey-600);
  font-weight: var(--font-regular);
}

.report-metric-list-value {
  color: var(--slate-600);
  font-weight: var(--font-semibold);
}

.report-metric-list-value.positive { color: var(--green-900); }
.report-metric-list-value.warning  { color: var(--orange-900); }
.report-metric-list-value.negative { color: var(--red-900); }

/* STAT GRID ────────────────────────────────────────────────────────────────────────── */
.report-stat-grid {
  display: grid;
  gap: 1rem;
  width: 100%;
}

.report-stat-grid-item {
  display: flex;
  flex-direction: column;
  padding: 0.875rem 1rem;
  background: var(--grey-10);
  border-bottom: 1px solid var(--grey-300);
  border-radius: 0.5rem;
  gap: 0.25rem;
  min-width: 0;
}

.report-stat-grid-label {
  font-family: var(--font-family);
  font-size: var(--label-sm-size);
  font-weight: var(--font-semibold);
  color: var(--grey-600);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.report-stat-grid-value {
  font-family: var(--font-family);
  font-size: var(--font-xl);
  font-weight: var(--font-bold);
  color: var(--slate-600);
  line-height: 1.2;
}

.report-stat-grid-value.positive { color: var(--green-900); }
.report-stat-grid-value.warning  { color: var(--orange-900); }
.report-stat-grid-value.negative { color: var(--red-900); }

.report-stat-grid-sub {
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-regular);
  color: var(--grey-600);
}

.report-stat-grid-badge {
  align-self: flex-start;
  padding: 2px 8px;
  margin-top: 0.25rem;
  border-radius: 4px;
  background: var(--blue-100);
  color: var(--blue-900);
  font-family: var(--font-family);
  font-size: var(--font-xs);
  font-weight: var(--font-semibold);
}

/* WATERFALL CHART ────────────────────────────────────────────────────────────────────────── */
.report-waterfall-wrap {
  position: relative;
  width: 100%;
}

.report-waterfall {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  font-family: var(--font-family);
}

.report-waterfall-gridline {
  stroke: var(--grey-100);
  stroke-width: 1;
}

.report-waterfall-baseline {
  stroke: var(--grey-300);
  stroke-width: 1;
}

.report-waterfall-connector {
  stroke: var(--grey-300);
  stroke-width: 1;
  stroke-dasharray: 4, 3;
}

.report-waterfall-tick {
  fill: var(--grey-500);
  font-size: 10px;
}

.report-waterfall-label {
  fill: var(--grey-600);
  font-size: 11px;
  font-weight: var(--font-semibold);
}

.report-waterfall-label-secondary {
  font-weight: var(--font-regular);
}

.report-waterfall-bar-total            { fill: var(--blue-900); }
.report-waterfall-bar-captured-saving  { fill: var(--green-900); }
.report-waterfall-bar-avoided-exposure { fill: var(--grey-500); }

.report-waterfall-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  margin: 8px 0 0;
  font-size: var(--font-xs);
  color: var(--grey-600);
}
.report-waterfall-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.report-waterfall-legend-swatch {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
}
.report-waterfall-legend-swatch-total            { background: var(--blue-900); }
.report-waterfall-legend-swatch-captured-saving  { background: var(--green-900); }
.report-waterfall-legend-swatch-avoided-exposure { background: var(--grey-500); }

.report-waterfall-value {
  font-size: 11px;
  font-weight: var(--font-bold);
}
.report-waterfall-value-total            { fill: var(--blue-900); }
.report-waterfall-value-captured-saving  { fill: var(--green-900); }
.report-waterfall-value-avoided-exposure { fill: var(--grey-600); }

/* PIE CHART ────────────────────────────────────────────────────────────────────────── */
.report-pie-wrap {
  position: relative;
  width: 100%;
}

.report-pie {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  font-family: var(--font-family);
}

.report-pie-slice {
  /* Fill colours are applied per-element from the JS palette. */
}

.report-pie-leader {
  stroke: var(--grey-400);
  stroke-width: 1;
  fill: none;
}

.report-pie-label {
  fill: var(--slate-900);
  font-size: 11px;
  font-weight: var(--font-semibold);
}

.report-pie-value {
  fill: var(--grey-600);
  font-size: 10px;
  font-weight: var(--font-regular);
}

.report-pie-empty {
  text-align: center;
  color: var(--grey-500);
  font-size: var(--font-sm);
  margin: 24px 0;
}

/* VEGA-LITE CHART ────────────────────────────────────────────────────────────────────────── */
.report-vega-chart {
  width: 100%;
}

.report-vega-chart-svg {
  display: flex;
  justify-content: center;
}

.report-vega-chart-svg svg {
  max-width: 100%;
  height: auto;
}
  
/* Added by Oasis. Embedded under the project header card, the report spans the
   frame's full width: the page around it supplies the gutters. */
.container { min-width: 0; padding: 0 0 1.5rem 0; }
/* The side nav's entries: left aligned, one line each, cut with an ellipsis. */
.report-sidenav-btn > span:first-child { flex: 1; min-width: 0; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
</style>
</head>
<body>
<div class="container"><div class="report-header-container"><div class="report-summary-container"><div class="report-summary-card report-summary-card-positive wide"><div class="label label-positive">Recommendation</div><div class="report-tag report-tag-positive">Renegotiate before the 31 Dec notice</div><div class="card-title-container"><div class="card-title"><span class="truncate">Microsoft Enterprise Agreement</span></div><div class="card-subtitle"><span class="card-subtitle">Opportunity snapshot · Renewal · Notice by 31 Dec 2026, term ends 31 Mar 2027</span></div></div><div class="card-description"><p>The renewal carries a large, well-evidenced savings opportunity. <strong>A third of the E5 seats paid for show no activity in 90 days</strong> and the unit price sits above the peer median. Letting the agreement renew as is locks the current quantity and price in for three more years, with no right to reduce and a 5% list rise announced for April.</p></div></div><div class="report-summary-card report-summary-card-positive narrow"><div class="label label-positive">Opportunity at a glance</div><div class="report-tag report-tag-positive">High opportunity</div><div class="card-title-container"><div class="card-title"><span class="truncate">$2.4M to $3.4M</span></div><div class="card-subtitle"><span class="card-subtitle">Potential annual savings</span></div></div><div class="card-data-container"><div class="card-data-row"><span class="card-data-row-label">Share of annual value</span><span class="card-data-row-value">28% to 40%</span></div><div class="card-data-row"><span class="card-data-row-label">Annual contract value</span><span class="card-data-row-value">$8.5M</span></div><div class="card-data-row"><span class="card-data-row-label">Contract term</span><span class="card-data-row-value">1 Apr 2024 to 31 Mar 2027</span></div></div></div><div class="report-summary-card report-summary-card-warning narrow"><div class="label label-warning">Priority</div><div class="report-tag report-tag-warning">High priority</div><div class="card-title-container"><div class="card-title"><span class="truncate">Act in the next 90 days</span></div><div class="card-subtitle"><span class="card-subtitle">Large upside, a hard notice date and a contract that forbids reducing later</span></div></div><div class="card-data-container"><div class="card-data-row"><span class="card-data-row-label">Financial opportunity</span><span class="card-data-row-value">Strong</span></div><div class="card-data-row"><span class="card-data-row-label">Negotiation leverage</span><span class="card-data-row-value">Moderate</span></div><div class="card-data-row"><span class="card-data-row-label">Decision urgency</span><span class="card-data-row-value">High</span></div><div class="card-data-row"><span class="card-data-row-label">Contractual risk</span><span class="card-data-row-value">High</span></div></div></div></div><div class="report-utility-nav-container"><button class="report-utility-nav-btn active" data-tab-id="snapshot">Opportunity snapshot</button></div></div><div class="report-container-row"><div class="report-sidenav-container"><div class="report-tab-sidenav" data-tab-id="snapshot"><button class="report-sidenav-btn" data-target="about"><span>About the deal</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="glance"><span>Opportunity at a glance</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="findings"><span>Key findings</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="assessment"><span>Assessment and recommendation</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button></div></div><div class="report-container"><div class="report-tab-content" data-tab-id="snapshot"><div class="report-section" id="about"><div class="report-section-subheader"><p class="report-section-title">About the deal</p><p class="report-section-description">Prepared 28 Sep 2026 from the connected sources; a rough estimate, not an analysis.</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 28%"><col></colgroup><thead><tr><th><span>Item</span></th><th><span>Detail</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Supplier</span></td><td><span>Microsoft</span></td></tr><tr><td><span class="report-table-position">Agreement</span></td><td><span>Enterprise Agreement, Microsoft 365 E5 and add-ons</span></td></tr><tr><td><span class="report-table-position">Deal type</span></td><td><span>Renewal, year 3 of 3</span></td></tr><tr><td><span class="report-table-position">Annual contract value</span></td><td><span>$8.5M, plus $0.6M of premier support under a separate agreement</span></td></tr><tr><td><span class="report-table-position">Licenses</span></td><td><span>12,400 E5 at $57.00 a user a month ($684 a year)</span></td></tr><tr><td><span class="report-table-position">Term</span></td><td><span>1 Apr 2024 to 31 Mar 2027</span></td></tr><tr><td><span class="report-table-position">Notice date</span></td><td><span>31 Dec 2026; 90 days before the term ends</span></td></tr><tr><td><span class="report-table-position">Negotiation owner</span></td><td><span>Rachel Sato</span></td></tr></tbody></table></div></div><div class="report-section" id="glance"><div class="report-section-subheader-row"><p class="report-section-title">Opportunity at a glance</p><p class="report-section-stat positive">$2.4M to $3.4M · 28% to 40%</p></div><div class="report-section-card-container"><div class="report-stat-grid" style="grid-template-columns: repeat(4, minmax(0, 1fr));"><div class="report-stat-grid-item"><span class="report-stat-grid-label">Potential savings</span><span class="report-stat-grid-value positive">$2.4M to $3.4M</span><span class="report-stat-grid-sub">Per year</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Savings opportunity</span><span class="report-stat-grid-value">28% to 40%</span><span class="report-stat-grid-sub">Of annual value</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Seats without activity</span><span class="report-stat-grid-value warning">3,440</span><span class="report-stat-grid-sub">Of 12,400 in the last 90 days</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Opportunity level</span><span class="report-stat-grid-value">High</span><span class="report-stat-grid-sub">Quantity, edition and price</span></div></div></div><div class="report-section-card-container"><div class="report-table-wrapper"><p class="report-table-title">Where the estimate comes from</p><table class="report-table"><colgroup><col><col style="width: 20%"><col></colgroup><thead><tr><th><span>Lever</span></th><th class="report-table-cell-numeric"><span>Estimate</span></th><th><span>Basis</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Right-size the quantity and edition</span></td><td class="report-table-cell-numeric"><span>$1.5M to $2.3M</span></td><td><span>3,440 seats inactive or unassigned at $684 a year; how many release, and how many fit E3, is for the usage analysis to confirm</span></td></tr><tr><td><span class="report-table-position">Price at the peer median or better</span></td><td class="report-table-cell-numeric"><span>$0.9M to $1.1M</span></td><td><span>23 comparable agreements, median $49.50, target $48.00</span></td></tr><tr class="report-table-row-total"><td><span class="report-table-position">Total</span></td><td class="report-table-cell-numeric"><span class="positive">$2.4M to $3.4M</span></td><td><span>Before the vendor&apos;s response; midpoint $2.9M</span></td></tr></tbody></table></div></div></div><div class="report-section" id="findings"><div class="report-section-subheader-row"><p class="report-section-title">Key findings</p></div><div class="report-section-card-container"><div class="report-card-grid" style="grid-template-columns: repeat(2, minmax(0, 1fr));"><div class="report-card"><div class="card-title-row"><span class="card-title">Usage and right-sizing</span><span class="card-title-stat">Strong opportunity</span></div><div class="report-description-list"><div class="report-description-list-content"><div class="card-title"><span class="truncate">A third of the seats show no activity:</span></div><div class="card-description"><p>The Microsoft 365 usage export of 25 Sep shows 8,960 of 12,400 E5 licenses active in the last 90 days. The 3,440 others are assigned to staff with no sign-in, held by departed staff, or never assigned.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">About 1,100 seats are not in anyone&apos;s hands:</span></div><div class="card-description"><p>550 licenses were never assigned and about 550 belong to people Workday lists as departed. These release without a conversation with the business.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">Edition fit is unknown and worth checking:</span></div><div class="card-description"><p>E5 costs $21 a month more than E3. Only a usage analysis of the E5-only features can say how many active users need it; the estimate assumes none move.</p></div></div></div></div><div class="report-card"><div class="card-title-row"><span class="card-title">Pricing and commercials</span><span class="card-title-stat">Above market</span></div><div class="report-description-list"><div class="report-description-list-content"><div class="card-title"><span class="truncate">The E5 unit price is above the peer median:</span></div><div class="card-description"><p>Coupa shows $57.00 a user a month. The median of 23 comparable agreements is $49.50 and the best in the set sits at $46.00; the target is $48.00.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">A list price rise is announced for April:</span></div><div class="card-description"><p>Microsoft has announced a 5% list increase from 1 Apr 2027, the day after the term ends. Renewing without a negotiated price inherits it.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">Add-ons ride on the same renewal:</span></div><div class="card-description"><p>$0.6M of premier support renews on the same date under a separate agreement and should be priced with the EA, not after it.</p></div></div></div></div><div class="report-card"><div class="card-title-row"><span class="card-title">Negotiation leverage</span><span class="card-title-stat">Moderate</span></div><div class="report-description-list"><div class="report-description-list-content"><div class="card-title"><span class="truncate">The quantity ask is backed by data:</span></div><div class="card-description"><p>Seat-level activity from the tenant and headcount from Workday make the reduction a matter of record, not of opinion. Microsoft&apos;s own usage reports show the same picture.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">The alternative path is MCA-E, not a change of vendor:</span></div><div class="card-description"><p>Moving the workloads off Microsoft 365 is not credible in the window. The lever is the licensing vehicle and the quantity, and Microsoft prefers an EA renewal to a shrinking MCA-E.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">Fiscal timing helps:</span></div><div class="card-description"><p>The notice date falls in Microsoft&apos;s Q2 and the term ends in Q3, when field teams carry quota pressure on renewals.</p></div></div></div></div><div class="report-card"><div class="card-title-row"><span class="card-title">Contract structure and risk</span><span class="card-title-stat">Requires attention</span></div><div class="report-description-list"><div class="report-description-list-content"><div class="card-title"><span class="truncate">No right to reduce during the term:</span></div><div class="card-description"><p>The agreement allows true-ups only. Any seat kept at the renewal is paid for three years, whatever its use.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">No price protection:</span></div><div class="card-description"><p>Nothing caps the renewal price or the increase at the next anniversary. The announced 5% list rise applies unless the renewal fixes a price.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">Supporting terms are unverified:</span></div><div class="card-description"><p>The extraction covers the enrollment and the order; the Customer Price Sheet and the amendments referenced in it have not been reviewed.</p></div></div></div></div><div class="report-card"><div class="card-title-row"><span class="card-title">Timing and decision window</span><span class="card-title-stat">High urgency</span></div><div class="report-description-list"><div class="report-description-list-content"><div class="card-title"><span class="truncate">The notice date is 94 days away:</span></div><div class="card-description"><p>Written notice is due by 31 Dec 2026 for any change to quantity or vehicle at the 31 Mar 2027 renewal. After it, the agreement renews on the current terms.</p></div></div><div class="report-description-list-content"><div class="card-title"><span class="truncate">A usage analysis and a playbook fit before it:</span></div><div class="card-description"><p>The usage analysis is delivered within five working days of the request and the playbook before round 1. Owner confirmation of inactive seats is the long pole and should start in October.</p></div></div></div></div></div></div></div><div class="report-section" id="assessment"><div class="report-section-subheader-row"><p class="report-section-title">Assessment and recommendation</p><p class="report-section-stat warning">High priority</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 24%"><col style="width: 18%"><col></colgroup><thead><tr><th><span>Factor</span></th><th><span>Assessment</span></th><th><span>Rationale</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Financial opportunity</span></td><td><span class="positive">Strong</span></td><td><span>$2.4M to $3.4M a year on an $8.5M agreement; the quantity lever alone is evidenced seat by seat.</span></td></tr><tr><td><span class="report-table-position">Negotiation leverage</span></td><td><span>Moderate</span></td><td><span>Usage data makes the reduction a matter of record and MCA-E is a credible vehicle; leaving Microsoft is not.</span></td></tr><tr><td><span class="report-table-position">Decision urgency</span></td><td><span class="warning">High</span></td><td><span>Notice by 31 Dec 2026; owner confirmations and a playbook must land before it.</span></td></tr><tr><td><span class="report-table-position">Contractual risk</span></td><td><span class="negative">High</span></td><td><span>No right to reduce and no price protection: renewing as is locks quantity and price for three years.</span></td></tr></tbody></table></div><div class="report-section-card-container"><div class="report-card callout"><div class="card-title-row"><span class="card-title">If you accept</span></div><div class="card-description"><p>A <strong>usage analysis</strong> within five working days (per-user activity, E5 feature use by function, licenses bought to licenses needed, with the function owners asked to confirm inactive seats), a <strong>negotiation playbook</strong> before round 1, and a <strong>savings report</strong> after signing. Dismissing records the reason so the proposal does not come back; snoozing brings it back on the date you choose.</p></div></div></div></div><div class="report-section" id="sources"><div class="report-section-subheader-row"><p class="report-section-title">Sources</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 35%"><col style="width: 20%"><col></colgroup><thead><tr><th><span>Source</span></th><th><span>As of</span></th><th><span>Used for</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Microsoft 365 admin center usage export</span></td><td><span>25 Sep 2026</span></td><td><span>Licenses assigned and last activity per user</span></td></tr><tr><td><span class="report-table-position">Workday</span></td><td><span>20 Sep 2026</span></td><td><span>Departed staff still holding a license</span></td></tr><tr><td><span class="report-table-position">Coupa AP</span></td><td><span>20 Sep 2026</span></td><td><span>Unit price and annual cost</span></td></tr><tr><td><span class="report-table-position">Enterprise Agreement extraction</span></td><td><span>12 Sep 2026</span></td><td><span>Term, notice date, reduction and price terms</span></td></tr><tr><td><span class="report-table-position">Market data</span></td><td><span>28 Sep 2026</span></td><td><span>Peer median and target price</span></td></tr></tbody></table></div></div></div></div></div></div>
<script>
  const tabButtons = document.querySelectorAll(".report-utility-nav-btn");
  const header = document.querySelector(".report-header-container");

  // The sidenav sticks flush beneath the header (see styles.css). The header's
  // height varies with its content and shrinks as it collapses, so publish its
  // live height and keep the sidenav in sync — ResizeObserver fires through the
  // collapse transition too, so the sidenav tracks the header frame by frame.
  function publishHeaderHeight() {
    if (!header) return;
    document.documentElement.style.setProperty(
      "--report-header-height",
      header.offsetHeight + "px"
    );
  }

  if (header && "ResizeObserver" in window) {
    new ResizeObserver(publishHeaderHeight).observe(header);
  }
  publishHeaderHeight();

  // Measure the header's expanded and compact heights so the collapse
  // thresholds adapt to the report (summary-card content varies the height).
  // Toggling with the transition suppressed makes the probe instant and
  // invisible. Re-measured on resize / once webfonts settle, since both shift
  // the height.
  let expandedHeaderH = 0;
  let compactHeaderH = 0;
  function measureHeaderHeights() {
    if (!header) return;
    const wasCompact = header.classList.contains("is-compact");
    header.classList.add("is-anim-suppressed");
    header.classList.remove("is-compact");
    expandedHeaderH = header.offsetHeight;
    header.classList.add("is-compact");
    compactHeaderH = header.offsetHeight;
    header.classList.toggle("is-compact", wasCompact);
    header.classList.remove("is-anim-suppressed");
  }
  measureHeaderHeights();
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(measureHeaderHeights);
  }

  // Header collapse controller. The header is expanded at the very top of the
  // report and compact once you've scrolled down into the content. Run on
  // every scroll and after programmatic scrolls (nav-item clicks, tab
  // switches) so both paths behave identically.
  //
  // Driven by scrollY with hysteresis: expand near the very top, collapse once
  // you've scrolled past the header. The catch is that collapsing shrinks the
  // header by \`delta\`, and browser scroll anchoring then shifts scrollY by that
  // same delta to keep your place — so the gap between the two thresholds must
  // exceed \`delta\`, otherwise the post-toggle anchoring shift lands past the
  // opposite threshold and the header flickers. We collapse \`delta\` + margin
  // down and expand within \`margin\` of the top, giving a band of delta + margin.
  const HEADER_MARGIN_PX = 32;
  function updateHeaderCompact() {
    if (!header) return;
    const delta = Math.max(0, expandedHeaderH - compactHeaderH);
    const y = window.pageYOffset;
    if (y <= HEADER_MARGIN_PX) {
      header.classList.remove("is-compact");
    } else if (y >= delta + 2 * HEADER_MARGIN_PX) {
      header.classList.add("is-compact");
    }
  }

  // Scroll a section flush beneath the header (used by nav-item clicks and tab
  // switches). The offset depends on the header height, but landing the scroll
  // *also* changes whether the header is collapsed — and an animated collapse
  // reflows content under the new scroll position, which browser scroll
  // anchoring then chases, drifting us onto the previous section. So: jump,
  // settle the header into its destination state with the collapse animation
  // suppressed (one synchronous reflow, nothing for anchoring to track), then
  // re-pin against the now-final header height. Animation is restored next
  // frame so manual scrolling keeps its smooth collapse.
  function scrollSectionToTop(target) {
    if (!target) return;
    const pin = () =>
      Math.max(0, target.getBoundingClientRect().top + window.pageYOffset - getHeaderOffset());
    window.scrollTo({ top: pin(), behavior: "auto" });
    if (header) header.classList.add("is-anim-suppressed");
    updateHeaderCompact();
    window.scrollTo({ top: pin(), behavior: "auto" });
    if (header) {
      requestAnimationFrame(() => header.classList.remove("is-anim-suppressed"));
    }
  }

  function getHeaderOffset() {
    return header ? header.offsetHeight : 0;
  }

  function updateActiveSidenav() {
    const activePane = document.querySelector('.report-tab-content:not([hidden])');
    if (!activePane) return;

    const sections = activePane.querySelectorAll('.report-section');
    if (!sections.length) return;

    let activeId = sections[0].id;
    const headerOffset = getHeaderOffset() + 24;

    sections.forEach(section => {
      const rect = section.getBoundingClientRect();
      if (rect.top <= headerOffset) {
        activeId = section.id;
      }
    });

    document
      .querySelectorAll('.report-tab-sidenav:not([hidden]) .report-sidenav-btn')
      .forEach(button => {
        button.classList.toggle('active', button.dataset.target === activeId);
      });
  }

  function onScroll() {
    updateHeaderCompact();
    updateActiveSidenav();
  }

  function activateTab(tabId) {
    tabButtons.forEach(button => button.classList.toggle("active", button.dataset.tabId === tabId));

    document.querySelectorAll(".report-tab-sidenav").forEach(element => {
      element.hidden = element.dataset.tabId !== tabId;
    });

    document.querySelectorAll(".report-tab-content").forEach(element => {
      element.hidden = element.dataset.tabId !== tabId;
    });

    document.querySelectorAll(".report-sidenav-btn").forEach(button => button.classList.remove("active"));

    const firstSide = document.querySelector('.report-tab-sidenav:not([hidden]) .report-sidenav-btn');
    if (firstSide) firstSide.classList.add('active');

    const firstSection = document.querySelector('.report-tab-content:not([hidden]) .report-section');
    scrollSectionToTop(firstSection);

    updateActiveSidenav();
  }

  tabButtons.forEach(button => {
    button.addEventListener("click", () => activateTab(button.dataset.tabId));
  });

  const firstExpander = document.querySelector(".report-expander");
  if (firstExpander) firstExpander.setAttribute("open", "");

  const firstSideOnLoad = document.querySelector('.report-tab-sidenav:not([hidden]) .report-sidenav-btn');
  if (firstSideOnLoad) firstSideOnLoad.classList.add('active');

  document.querySelectorAll(".report-sidenav-btn").forEach(button => {
    button.addEventListener("click", () => {
      const target = document.getElementById(button.dataset.target);
      if (!target) return;

      document.querySelectorAll('.report-tab-sidenav:not([hidden]) .report-sidenav-btn').forEach(sideButton => {
        sideButton.classList.remove('active');
      });
      button.classList.add('active');

      scrollSectionToTop(target);
      updateActiveSidenav();
    });
  });

  updateHeaderCompact();
  updateActiveSidenav();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
    measureHeaderHeights();
    onScroll();
  });

    // Fallback colgroup for tables whose schema didn't declare column
    // widths. If the server-side renderer already emitted a <colgroup>
    // (because at least one column in the JSON had a \`width\`), respect
    // it and bail — don't strip and replace.
  document.querySelectorAll('.report-table').forEach((table) => {
    if (table.querySelector(':scope > colgroup')) return;

    const firstRow = table.querySelector('thead tr, tbody tr');
    if (!firstRow) return;

    const columnCount = firstRow.children.length;

    const colgroup = document.createElement('colgroup');

    let widths = [];

    if (columnCount === 2) {
      widths = ['13.75rem', 'auto']; // 220px / rest
    } else if (columnCount === 3) {
      widths = ['13.75rem', '13.125rem', 'auto']; // 220 / 210 / rest
    } else if (columnCount === 4) {
      widths = ['13.75rem', '13.125rem', '13.125rem', 'auto']; // 220 / 210 / 210 / rest
    } else if (columnCount === 5) {
      widths = ['13.75rem', '13.125rem', '12rem', '12rem', '10rem'];
    } else {
      // fallback: first two anchored, rest auto
      widths = Array.from({ length: columnCount }, (_, i) => {
        if (i === 0) return '13.75rem';
        if (i === 1) return '13.125rem';
        return 'auto';
      });
    }

    widths.forEach((width) => {
      const col = document.createElement('col');
      if (width !== 'auto') {
        col.style.width = width;
      }
      colgroup.appendChild(col);
    });

    table.insertBefore(colgroup, table.firstChild);
  });
</script>
</body>
</html>

`;

export const SNAPSHOT_SNOWFLAKE_V1 = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Report</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap" rel="stylesheet">
  <style>
/* GLOBAL ────────────────────────────────────────────────────────────────────────── */
:root {
  --container-min-width: 1080px;

  --report-header-expanded-offset: 17.125rem;

  /* FONT FAMILY */
  --font-family: "Inter", sans-serif;

  /* FONT WEIGHTS */
  --font-regular: 400;
  --font-semibold: 600;
  --font-bold: 700;

  /* FONT SIZES */
  --font-xs: 0.625rem;
  --font-sm: 0.75rem;
  --font-md: 0.875rem;
  --font-default: 1rem;
  --font-lg: 1.25rem;
  --font-xl: 1.5rem;

  /* LABEL */
  --label-sm-size: 0.625rem;
  --label-md-size: 0.75rem;

  /* SLATE */
  --slate-900: #141E2E;
  --slate-600: #2F466B;

  /* TEAL */
  --teal-900: #00B2FE;
  --teal-300: #75D6FF;

  /* GREY */
  --grey-900: #404040;
  --grey-600: #666666;
  --grey-500: #999999;
  --grey-400: #BFBFBF;
  --grey-300: #D9D9D9;
  --grey-100: #F0F0F0;
  --grey-50: #F5F5F5;
  --grey-10: #FAFAFA;
  --grey-white: #FFFFFF;

  /* BLUE */
  --blue-900: #006ACC;
  --blue-600: #1890FF;
  --blue-300: #99CEFF;
  --blue-100: #E5F3FF;
  --blue-50: #F0F8FF;


  /* GREEN */
  --green-900: #2FB801;
  --green-600: #5AD81D;
  --green-500: #77E043;
  --green-300: #C1F2A9;
  --green-100: #E6FADC;
  --green-50: #F2FCED;

  /* RED */
  --red-900: #FD3559;
  --red-600: #FF758E;
  --red-300: #FEA8B7;
  --red-100: #FFE6EA;
  --red-50: #FFF0F2;
  --red-10: #FFF7F9;

  /* ORANGE */
  --orange-900: #FF8400;
  --orange-600: #FF981F;
  --orange-300: #FFCA99;
  --orange-100: #FFE8CC;
  --orange-50: #FFF4EB;

  /* MAGENTA */
  --magenta-900: #EA00FF;

  /* SPECIAL */
  --slate-alt-900: #101525;
  --green-alt-900: #124002;
  --green-alt-700: #315724;
  --orange-alt-900: #591E00;
  --orange-alt-700: #663600;
  --red-alt-900: #4D000E;
  --red-alt-700: #660012;

  /* GRADIENT */
  --pink-blue: linear-gradient(90deg, #77ADFF 31.25%, #FF3FEC 100%);
}

body {
  margin: 0;
  background: var(--grey-white);
  font-family: var(--font-family);
  display: flex;
  justify-content: center;
}

.container {
  box-sizing: border-box;
  width: 100%;
  min-width: var(--container-min-width);
  padding: 0 1.5rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

p {
  margin: 0;
}

/* REPORT CONTAINER ────────────────────────────────────────────────────────────────────────── */
.report-container-row {
  display: flex;
  align-items: flex-start;
  background: var(--grey-white);
  gap: 16px;
}

.report-container {
  display: flex;
  flex-direction: column;
  background: var(--grey-white);
  flex: 1 1 0;
  min-width: 0;
  padding-bottom: 80vh;
}

/* REPORT HEADER ────────────────────────────────────────────────────────────────────────── */
.report-header-container {
  display: flex;
  flex-direction: column;
  padding: 0 0 1.25rem 0;
  position: sticky;
  top: 0;
  z-index: 1000;
  gap: 1rem;
  background: var(--grey-white);
  transition:
    padding 0.32s ease,
    gap 0.32s ease,
    box-shadow 0.32s ease;
  will-change: padding, gap;
}


/* REPORT SUMMARY CARDS ────────────────────────────────────────────────────────────────────────── */
.report-summary-container {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  background: var(--grey-white);
  transition: gap 0.22s ease;
}

.report-summary-card {
  --card-bg: var(--grey-10);
  --card-border: var(--grey-300);

  background: var(--card-bg);
  border-bottom: 1px solid var(--card-border);

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem 1rem 0.75rem 1rem;
  gap: 0.5rem;
  border-radius: 0.5rem;
  min-width: 0;
  transition:
    padding 0.32s ease,
    gap 0.32s ease,
    border-radius 0.32s ease,
    background-color 0.32s ease,
    border-color 0.32s ease;
}

.report-summary-card.wide {
  flex: 5;
}

.report-summary-card.narrow {
  flex: 2.5;
}

.report-summary-card-positive {
  --card-bg: var(--green-50);
  --card-border: var(--green-500);
}

.report-summary-card-warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-summary-card-negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

/* Collapse instantly (no animation) while a nav-item click / tab switch
   re-pins the scroll, so the header height settles in one synchronous reflow
   that browser scroll anchoring can't chase off the target. Restored next
   frame, so manual scrolling keeps its smooth collapse. */
.report-header-container.is-anim-suppressed,
.report-header-container.is-anim-suppressed * {
  transition: none !important;
}

.report-header-container.is-compact {
  padding: 0 0 1.25rem 0;
  gap: 0.75rem;
}

.report-header-container.is-compact .report-summary-container {
  gap: 0.5rem;
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.report-header-container.is-compact .report-summary-card {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  gap: 0.75rem;
  border-radius: 0.5rem;
}

.report-header-container.is-compact .report-summary-card .card-description,
.report-header-container.is-compact .report-summary-card .card-data-container {
  opacity: 0;
  transform: translateY(-0.25rem);
  pointer-events: none;
  display: none;
}

.report-header-container.is-compact .report-summary-card .label {
  flex: 0 0 auto;
  white-space: nowrap;
}

.report-header-container.is-compact .report-summary-card .report-tag {
  flex: 0 0 auto;
  min-width: auto;
  white-space: nowrap;
}

.report-header-container.is-compact .report-summary-card .card-title-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  width: auto;
  min-width: 0;
  flex: 1 1 auto;
}

.report-header-container.is-compact .report-summary-card .card-subtitle {
  opacity: 0;
  transform: translateY(-0.25rem);
  pointer-events: none;
  display: none;
}

.report-header-container.is-compact .report-summary-card .card-title {
  font-size: var(--font-md);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.report-header-container.is-compact .report-summary-card.narrow .card-title-container {
  justify-content: flex-end;
}

.card-title-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  gap: 0.25rem;
  transition: gap 0.22s ease;
}

.card-title,
.card-subtitle,
.card-description {
  width: 100%;
  min-width: 0;
}

.card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-lg);
  color: var(--slate-600);
  width: 100%;
  transition: font-size 0.22s ease, color 0.22s ease;
}

.report-summary-card-positive .card-title {
  color: var(--green-900);
}

.report-summary-card-warning .card-title {
  color: var(--orange-900);
}

.report-summary-card-negative .card-title {
  color: var(--red-900);
}

.card-subtitle {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  transition: opacity 0.18s ease, transform 0.18s ease, color 0.22s ease;
}

.card-description {
  width: 100%;
  min-width: 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  line-height: 1.6;
  color: var(--grey-600);
  transition: opacity 0.18s ease, transform 0.18s ease, color 0.22s ease;
}

.card-data-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-width: 0;
  gap: 0.5rem;
}

.card-data-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
}

.card-data-row-label {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.card-data-row-value {
  font-family: var(--font-family);
  font-weight: var(--font-bold);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.report-summary-card-positive .card-data-row-value {
  color: var(--green-900);
}

.report-summary-card-warning .card-data-row-value {
  color: var(--orange-900);
}

.report-summary-card-negative .card-data-row-value {
  color: var(--red-900);
}

.truncate {
  display: block;
  width: 100%;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* REPORT SECTION ────────────────────────────────────────────────────────────────────────── */
.report-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
  padding: 16px;
  margin: 0 0 20px 0;
  border-radius: 8px;
  border-bottom: 1px solid var(--grey-300);
  background: var(--grey-10);
}

.report-section-header-column {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  gap: 4px;
}

.report-section-card-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}

.report-section-card-container.row {
  flex-direction: row;
  justify-content: space-between;
}

/* CARD GRID — CSS-grid layout. \`grid-template-columns\` is set inline by the
   CardGrid component (via the \`columns\` prop; defaults to items.length). */
.report-card-grid {
  display: grid;
  gap: 1rem;
  width: 100%;
}

/* BASE CARD — shared layout for section / banner / callout cards */
.report-card {
  --card-bg: var(--grey-white);
  --card-border: var(--grey-300);
  background: var(--card-bg);
  border-bottom: 1px solid var(--card-border);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem 1rem 0.75rem 1rem;
  gap: 0.5rem;
  border-radius: 0.5rem;
  width: 100%;
  box-sizing: border-box;
}

.report-card .card-title-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.report-card .card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

/* Inside a card-grid, titles ellipsize to fit fixed-width grid cells. */
.report-card-grid .card-title {
  display: block;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.report-card .card-title-stat {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--green-900);
}

.report-card .card-description,
.report-card .report-text {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  line-height: 1.6;
  color: var(--grey-500);
}

/* BANNER modifier — tighter gap, severity color variants */
.report-card.banner {
  gap: 0.325rem;
}

.report-card.banner.positive {
  --card-bg: var(--green-50);
  --card-border: var(--green-500);
}

.report-card.banner.warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-card.banner.negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

.report-card.banner .card-title.positive {
  color: var(--green-900);
}

.report-card.banner .card-title.warning {
  color: var(--orange-900);
}

.report-card.banner .card-title.negative {
  color: var(--red-900);
}

.report-card.banner .card-description {
  color: var(--grey-600);
}

/* CALLOUT modifier — teal accent, smaller title */
.report-card.callout {
  --card-border: var(--teal-300);
  gap: 0.325rem;
}

.report-card.callout.warning {
  --card-bg: var(--orange-50);
  --card-border: var(--orange-300);
}

.report-card.callout.negative {
  --card-bg: var(--red-50);
  --card-border: var(--red-300);
}

.report-card.callout .card-title {
  font-size: var(--font-sm);
  color: var(--teal-900);
}

.report-card.callout .card-title.warning {
  color: var(--orange-900);
}

.report-card.callout .card-title.negative {
  color: var(--red-900);
}

/* BLOCKQUOTE — verbatim quoted text (e.g. suggested contract language) */
.report-blockquote {
  margin: 0;
  padding: 0.5rem 0.7rem;
  background: var(--grey-50);
  border-left: 2px solid var(--grey-300);
  border-radius: 0 4px 4px 0;
  color: var(--grey-600);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: var(--font-xs);
  line-height: 1.6;
  white-space: pre-wrap;
}

.report-section-subheader {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 4px;
  align-self: stretch;
  padding: 0 2px;
}

.report-section-subheader-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  align-self: stretch;
  padding: 0 2px;
}

.report-section-title {
  display: flex;
  align-items: center;
  align-self: stretch;
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

.report-section-description {
  display: flex;
  align-items: center;
  max-width: 900px;
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

.report-section-stat {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-md);
  color: var(--slate-600);
}

.report-section-stat.positive {
  color: var(--green-900);
}

.report-section-stat.warning {
  color: var(--orange-900);
}

.report-section-stat.negative {
  color: var(--red-900);
}

/* REPORT EXPANDERS ────────────────────────────────────────────────────────────────────────── */
.report-expander {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  font-family: var(--font-family);
  border-bottom: 0.0625rem solid var(--grey-300);
  border-radius: 0.375rem;
  overflow: hidden;
  background: var(--grey-white);
}

/* HEADER BASE */
.report-expander summary {
  list-style: none;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-self: stretch;
  align-items: center;
  padding: 0.5rem 1rem;
  background: var(--grey-white);
  transition: background 0.2s ease;
}

/* HOVER ONLY WHEN CLOSED */
.report-expander:not([open]) summary:hover {
  background: var(--blue-600);
}

/* TEXT + ICON WHITE ON HOVER */
.report-expander:not([open]) summary:hover .report-title,
.report-expander:not([open]) summary:hover .report-subtitle,
.report-expander:not([open]) summary:hover .report-icon {
  color: var(--grey-white);
}

/* BORDER CHANGE */
.report-expander:not([open]):hover {
  border-bottom: 0.0625rem solid var(--blue-600);
}

/* OPEN STATE */
.report-expander[open] summary {
  background: var(--blue-600);
}

/* TEXT + ICON WHITE WHEN OPEN */
.report-expander[open] .report-title,
.report-expander[open] .report-subtitle,
.report-expander[open] .report-icon {
  color: var(--grey-white);
}

/* TEXT BLOCK */
.report-expander-text {
  display: flex;
  flex-direction: column;
}

/* TYPOGRAPHY */
.report-title {
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-bold);
  font-size: var(--font-md);
  color: var(--blue-600);
}

.report-subtitle {
  margin: 0.125rem 0 0;
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

/* ICON */
.report-icon {
  display: flex;
  width: 0.875rem;
  height: 0.875rem;
  flex: 0 0 1rem;
  color: var(--blue-600);
  transition: transform 0.25s ease, color 0.2s ease;
}

.report-icon svg {
  width: 100%;
  height: 100%;
  display: flex;
}

.report-icon svg path {
  fill: currentColor;
}

.report-expander[open] .report-icon {
  transform: rotate(180deg);
}

/* CONTENT */
.report-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  padding: 0.75rem 1rem;
  background: var(--grey-white);
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-regular);
  line-height: 1.5;
  color: var(--grey-600);
}

/* ANIMATION */
.report-expander .report-content {
  transform-origin: top;
  transform: scaleY(0.95);
  opacity: 0;
  min-height: 200px;
  overflow: hidden;
  transition:
    transform 0.2s ease,
    opacity 0.2s ease,
    max-height 0.25s ease;
}

.report-expander[open] .report-content {
  transform: scaleY(1);
  opacity: 1;
  max-height: 31.25rem;
}

/* REMOVE DEFAULT ARROW */
.report-expander summary {
  list-style: none;
}

.report-expander summary::-webkit-details-marker {
  display: none;
}

.report-expander summary::marker {
  content: "";
}

/* REPORT TAGS ────────────────────────────────────────────────────────────────────────── */
.report-tag {
  display: flex;
  padding: 4px 10px 4px 10px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  min-width: 68px;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  color: var(--grey-white);
}

.report-tag-positive {
  background: var(--green-900);
  color: var(--grey-white);
}

.report-tag-warning {
  background: var(--orange-900);
  color: var(--grey-white);
}

.report-tag-negative {
  background: var(--red-900);
  color: var(--grey-white);
}

.report-tag-alt-positive {
  background: var(--green-100);
  color: var(--green-900);
}

.report-tag-alt-warning {
  background: var(--orange-100);
  color: var(--orange-900);
}

.report-tag-alt-negative {
  background: var(--red-100);
  color: var(--red-900);
}

/* LABELS ────────────────────────────────────────────────────────────────────────── */
.label {
  font-family: var(--font-family);
  font-size: var(--label-sm-size);
  font-weight: var(--font-semibold);
  color: var(--teal-900);
  text-transform: uppercase;
}

.label-positive {
  color: var(--green-alt-700);
}

.label-warning {
  color: var(--orange-alt-700);
}

.label-negative {
  color: var(--red-alt-900);
}

/* REPORT UTILITY NAV ────────────────────────────────────────────────────────────────────────── */
.report-utility-nav-container {
  display: flex;
  padding: 4px;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  border-radius: 6px;
  background: var(--blue-100);
}

/* Inactive */
.report-utility-nav-btn {
  display: flex;
  padding: 0.25rem 1rem;
  justify-content: center;
  align-items: center;
  border-radius: 0.25rem;
  border: none;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  color: var(--blue-600);
  cursor: pointer;

  background: var(--blue-100); /* softer inactive bg */
  color: var(--blue-600);

  box-shadow: none;
  transition: background 0.2s ease, color 0.2s ease;
}

/* HOVER (inactive only) */
.report-utility-nav-btn:hover {
  background: var(--grey-white);
}

/* ACTIVE */
.report-utility-nav-btn.active {
  background: var(--grey-white);
  color: var(--blue-600);
  box-shadow: 0 0.0625rem 0.25rem 0 var(--blue-300);
}

/* prevent hover from overriding active */
.report-utility-nav-btn.active:hover {
  background: var(--grey-white);
}

.report-header-container.is-compact .report-utility-nav-container {
  padding: 0.25rem;
}

.report-header-container.is-compact .report-utility-nav-btn {
  padding: 0.375rem 0.875rem;
}

/* REPORT SIDENAV NAV ────────────────────────────────────────────────────────────────────────── */
.report-sidenav-container {
  display: flex;
  padding-right: 16px;
  flex-direction: column;
  align-items: flex-start;
  align-self: flex-start;
  border-right: 1px solid var(--grey-100);
  gap: 4px;
  position: relative;
}

/* Stick the sidenav flush beneath the sticky header so its top items are
   never hidden behind it. The header's height is dynamic — summary-card
   content varies and the header collapses on scroll — so the runtime JS
   publishes its live border-box height as --report-header-height and the
   sidenav tracks it exactly (through the collapse transition too). The
   expanded-offset token is only the pre-JS fallback. */
.report-container-row .report-sidenav-container {
  position: sticky;
  top: var(--report-header-height, var(--report-header-expanded-offset));
}

/* BASE = inactive */
.report-sidenav-btn {
  display: flex;
  width: 240px;
  padding: 0.5rem 0.5rem 0.5rem 0.625rem;
  justify-content: space-between;
  align-items: center;
  border-radius: 0.375rem;
  border: none;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);

  color: var(--grey-500);
  cursor: pointer;

  background: var(--grey-white);
  transition: background 0.2s ease, color 0.2s ease;
}

/* HOVER (inactive only) */
.report-sidenav-btn:not(.active):hover {
  background: var(--grey-10);
}

/* ACTIVE */
.report-sidenav-btn.active {
  background: var(--grey-10);
  color: var(--slate-600);
}

/* keep active stable */
.report-sidenav-btn.active:hover {
  background: var(--grey-10);
}


/* CHEVRON (reuse report-icon) */
.report-sidenav-btn .report-icon {
  margin-left: auto;
  transform: rotate(0deg); /* DOWN */
  transition: transform 0.2s ease;
}

/* ACTIVE → RIGHT */
.report-sidenav-btn.active .report-icon {
  transform: rotate(-90deg);
}

/* SIZE */
.report-sidenav-btn .report-icon {
  width: 0.625rem;
  height: 0.625rem;

  margin-left: auto;
  display: flex;
  align-items: center;

  /* COLOR (inherits from button) */
  color: inherit;

  /* DEFAULT → DOWN */
  transform: rotate(0deg);
  transition: transform 0.2s ease;
}

/* ACTIVE → RIGHT */
.report-sidenav-btn.active .report-icon {
  transform: rotate(-90deg);
}

/* BANNER BOX — now uses .report-card.banner (see BASE CARD above) */

/* SUPPORTING ARGUMENTS BOX ────────────────────────────────────────────────────────────────────────── */
.report-description-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  width: 100%;
  margin: .5rem 0 0 0;
}

.report-description-list-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
}

.report-description-list .label {
  font-size: var(--label-md-size);
}

.report-description-list .card-title {
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--slate-600);
}

.report-description-list .card-description {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-500);
}

/* TABLE ────────────────────────────────────────────────────────────────────────── */
.report-table-wrapper {
  width: 100%;
}

.report-table-title {
  margin: 0 0 0.25rem 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--label-sm-size);
  color: var(--slate-900);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  background: var(--grey-white);
  margin: 0.5rem 0;
}

.report-table th,
.report-table td {
  overflow-wrap: anywhere;
}

/* HEADER */
.report-table thead th {
  padding:  0.5rem 0.75rem;
  text-align: left;
  background: var(--grey-100);

  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--slate-600);
}

/* BODY */
.report-table tbody td {
  padding:  0.5rem 0.75rem;

  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
}

/* NUMERIC */
.report-table-cell-numeric {
  text-align: left;
  white-space: nowrap;
}

/* POSITION */
.report-table-position {
  font-weight: var(--font-semibold);
  color: var(--slate-600);
}

/* CELL / COLUMN SENTIMENT — applies to spans in both <th> and <td>. */
.report-table .positive { color: var(--green-900); }
.report-table .warning  { color: var(--orange-900); }
.report-table .negative { color: var(--red-900); }

/* TWO-LINE CELL — value on top, muted sub underneath. */
.report-table-cell-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.report-table-cell-sub {
  font-size: var(--font-xs);
  font-weight: var(--font-regular);
  color: var(--grey-500);
}

/* PILL CELL — renders the cell value as a rounded badge. */
.report-table-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  background: var(--grey-100);
  color: var(--grey-600);
}
.report-table-pill-positive { background: var(--green-100); color: var(--green-900); }
.report-table-pill-warning  { background: var(--orange-100); color: var(--orange-900); }
.report-table-pill-negative { background: var(--red-100); color: var(--red-900); }

/* ROW VARIANTS — zebra stripes apply unless a row has its own
   background treatment (focus variant, or row sentiment). */
.report-table tbody tr:nth-child(odd):not(.report-table-row-focus):not(.report-table-row-positive):not(.report-table-row-warning):not(.report-table-row-negative):not(.report-table-row-heading):not(.report-table-row-total) td {
  background: var(--grey-white);
}

.report-table tbody tr:nth-child(even):not(.report-table-row-focus):not(.report-table-row-positive):not(.report-table-row-warning):not(.report-table-row-negative):not(.report-table-row-heading):not(.report-table-row-total) td {
  background: var(--grey-10);
}

.report-table-row-focus td {
  background: var(--blue-100);
}

.report-table-row-positive td {
  background: var(--green-100);
}

.report-table-row-warning td {
  background: var(--orange-100);
}

.report-table-row-negative td {
  background: var(--red-100);
}

/* Total row: bolden every cell (td + nested span override the base
   \`.report-table tbody td\` regular weight and \`.report-table-position\`
   semibold weight). */
.report-table tbody tr.report-table-row-total td,
.report-table tbody tr.report-table-row-total td span {
  font-weight: var(--font-semibold);
}

.report-table tbody tr.report-table-row-total td {
  border-top: 2px solid var(--grey-500);
  background: var(--grey-50);
}

/* Heading row: group-label spanning every column. Uses the same visual
   treatment as the standalone \`.report-heading\` block (uppercase, tight
   tracking, slate-900) with a distinct background so it stands apart
   from the zebra-striped data rows. */
.report-table tbody tr.report-table-row-heading td {
  padding: 0.625rem 0.75rem;
  background: var(--blue-50);
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* VALUE EMPHASIS */
.report-table .report-table-cell-numeric {
  color: var(--grey-600);
}

/* HEADING ─────────────────────────────────────────────────────────────────────── */
.report-heading {
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-semibold);
  font-size: var(--font-sm);
  color: var(--grey-900);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* TEXT ────────────────────────────────────────────────────────────────────────── */
/* Text is a single paragraph — rendered as \`<p class="report-text">\`. */
.report-text {
  font-family: var(--font-family);
  font-weight: var(--font-regular);
  font-size: var(--font-sm);
  color: var(--grey-600);
  line-height: 1.6;
  width: 100%;
  margin: 0;
}

/* Sentiment tint. Specificity (0,2,0) beats the in-card
   \`.report-card .report-text\` color override (0,2,0) via order. */
.report-text.positive { color: var(--green-900); }
.report-text.warning  { color: var(--orange-900); }
.report-text.negative { color: var(--red-900); }

/* METRIC LIST ────────────────────────────────────────────────────────────────────────── */
.report-metric-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--grey-white);
  border: 0.0625rem solid var(--grey-300);
  border-radius: 0.5rem;
  width: 100%;
}

.report-metric-list-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-family: var(--font-family);
  font-size: var(--font-sm);
}

.report-metric-list-label {
  color: var(--grey-600);
  font-weight: var(--font-regular);
}

.report-metric-list-value {
  color: var(--slate-600);
  font-weight: var(--font-semibold);
}

.report-metric-list-value.positive { color: var(--green-900); }
.report-metric-list-value.warning  { color: var(--orange-900); }
.report-metric-list-value.negative { color: var(--red-900); }

/* STAT GRID ────────────────────────────────────────────────────────────────────────── */
.report-stat-grid {
  display: grid;
  gap: 1rem;
  width: 100%;
}

.report-stat-grid-item {
  display: flex;
  flex-direction: column;
  padding: 0.875rem 1rem;
  background: var(--grey-10);
  border-bottom: 1px solid var(--grey-300);
  border-radius: 0.5rem;
  gap: 0.25rem;
  min-width: 0;
}

.report-stat-grid-label {
  font-family: var(--font-family);
  font-size: var(--label-sm-size);
  font-weight: var(--font-semibold);
  color: var(--grey-600);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.report-stat-grid-value {
  font-family: var(--font-family);
  font-size: var(--font-xl);
  font-weight: var(--font-bold);
  color: var(--slate-600);
  line-height: 1.2;
}

.report-stat-grid-value.positive { color: var(--green-900); }
.report-stat-grid-value.warning  { color: var(--orange-900); }
.report-stat-grid-value.negative { color: var(--red-900); }

.report-stat-grid-sub {
  font-family: var(--font-family);
  font-size: var(--font-sm);
  font-weight: var(--font-regular);
  color: var(--grey-600);
}

.report-stat-grid-badge {
  align-self: flex-start;
  padding: 2px 8px;
  margin-top: 0.25rem;
  border-radius: 4px;
  background: var(--blue-100);
  color: var(--blue-900);
  font-family: var(--font-family);
  font-size: var(--font-xs);
  font-weight: var(--font-semibold);
}

/* WATERFALL CHART ────────────────────────────────────────────────────────────────────────── */
.report-waterfall-wrap {
  position: relative;
  width: 100%;
}

.report-waterfall {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  font-family: var(--font-family);
}

.report-waterfall-gridline {
  stroke: var(--grey-100);
  stroke-width: 1;
}

.report-waterfall-baseline {
  stroke: var(--grey-300);
  stroke-width: 1;
}

.report-waterfall-connector {
  stroke: var(--grey-300);
  stroke-width: 1;
  stroke-dasharray: 4, 3;
}

.report-waterfall-tick {
  fill: var(--grey-500);
  font-size: 10px;
}

.report-waterfall-label {
  fill: var(--grey-600);
  font-size: 11px;
  font-weight: var(--font-semibold);
}

.report-waterfall-label-secondary {
  font-weight: var(--font-regular);
}

.report-waterfall-bar-total            { fill: var(--blue-900); }
.report-waterfall-bar-captured-saving  { fill: var(--green-900); }
.report-waterfall-bar-avoided-exposure { fill: var(--grey-500); }

.report-waterfall-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  margin: 8px 0 0;
  font-size: var(--font-xs);
  color: var(--grey-600);
}
.report-waterfall-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.report-waterfall-legend-swatch {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
}
.report-waterfall-legend-swatch-total            { background: var(--blue-900); }
.report-waterfall-legend-swatch-captured-saving  { background: var(--green-900); }
.report-waterfall-legend-swatch-avoided-exposure { background: var(--grey-500); }

.report-waterfall-value {
  font-size: 11px;
  font-weight: var(--font-bold);
}
.report-waterfall-value-total            { fill: var(--blue-900); }
.report-waterfall-value-captured-saving  { fill: var(--green-900); }
.report-waterfall-value-avoided-exposure { fill: var(--grey-600); }

/* PIE CHART ────────────────────────────────────────────────────────────────────────── */
.report-pie-wrap {
  position: relative;
  width: 100%;
}

.report-pie {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  font-family: var(--font-family);
}

.report-pie-slice {
  /* Fill colours are applied per-element from the JS palette. */
}

.report-pie-leader {
  stroke: var(--grey-400);
  stroke-width: 1;
  fill: none;
}

.report-pie-label {
  fill: var(--slate-900);
  font-size: 11px;
  font-weight: var(--font-semibold);
}

.report-pie-value {
  fill: var(--grey-600);
  font-size: 10px;
  font-weight: var(--font-regular);
}

.report-pie-empty {
  text-align: center;
  color: var(--grey-500);
  font-size: var(--font-sm);
  margin: 24px 0;
}

/* VEGA-LITE CHART ────────────────────────────────────────────────────────────────────────── */
.report-vega-chart {
  width: 100%;
}

.report-vega-chart-svg {
  display: flex;
  justify-content: center;
}

.report-vega-chart-svg svg {
  max-width: 100%;
  height: auto;
}
  
/* Added by Oasis. Embedded under the project header card, the report spans the
   frame's full width: the page around it supplies the gutters. */
.container { min-width: 0; padding: 0 0 1.5rem 0; }
/* The side nav's entries: left aligned, one line each, cut with an ellipsis. */
.report-sidenav-btn > span:first-child { flex: 1; min-width: 0; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
</style>
</head>
<body>
<div class="container"><div class="report-header-container"><div class="report-summary-container"><div class="report-summary-card report-summary-card-warning wide"><div class="label label-warning">Recommendation</div><div class="report-tag report-tag-warning">Rebalance now and pull the renewal forward</div><div class="card-title-container"><div class="card-title"><span class="truncate">Snowflake capacity commitment</span></div><div class="card-subtitle"><span class="card-subtitle">Opportunity snapshot · Consumption · Term 15 Apr 2026 to 14 Apr 2027, notice by 14 Feb 2027</span></div></div><div class="card-description"><p>The account consumes its <strong>$6.0M</strong> commitment at <strong>118% pace</strong>: $7.1M a year over the last 90 days, the commitment reached in February and <strong>$1.1M of overage</strong> billed at the on-demand rate before the term ends. Development and test warehouses explain a third of the growth. Rebalancing them now and signing the next term early turns the overage into leverage on the rate.</p></div></div><div class="report-summary-card report-summary-card-positive narrow"><div class="label label-positive">Opportunity at a glance</div><div class="report-tag report-tag-positive">High opportunity</div><div class="card-title-container"><div class="card-title"><span class="truncate">$1.1M</span></div><div class="card-subtitle"><span class="card-subtitle">Overage avoided this term</span></div></div><div class="card-data-container"><div class="card-data-row"><span class="card-data-row-label">Three-year effect</span><span class="card-data-row-value">$2.4M lower than renewing as is</span></div><div class="card-data-row"><span class="card-data-row-label">Annual commitment</span><span class="card-data-row-value">$6.0M</span></div><div class="card-data-row"><span class="card-data-row-label">Consumed to date</span><span class="card-data-row-value">$3.65M at 54% of the term</span></div></div></div><div class="report-summary-card report-summary-card-warning narrow"><div class="label label-warning">Priority</div><div class="report-tag report-tag-warning">High priority</div><div class="card-title-container"><div class="card-title"><span class="truncate">Act before February</span></div><div class="card-subtitle"><span class="card-subtitle">The commitment runs out mid-term; every month after it bills 20% above the capacity rate</span></div></div><div class="card-data-container"><div class="card-data-row"><span class="card-data-row-label">Financial opportunity</span><span class="card-data-row-value">Strong</span></div><div class="card-data-row"><span class="card-data-row-label">Negotiation leverage</span><span class="card-data-row-value">Strong</span></div><div class="card-data-row"><span class="card-data-row-label">Decision urgency</span><span class="card-data-row-value">High</span></div><div class="card-data-row"><span class="card-data-row-label">Contractual risk</span><span class="card-data-row-value">Moderate</span></div></div></div></div><div class="report-utility-nav-container"><button class="report-utility-nav-btn active" data-tab-id="snapshot">Opportunity snapshot</button></div></div><div class="report-container-row"><div class="report-sidenav-container"><div class="report-tab-sidenav" data-tab-id="snapshot"><button class="report-sidenav-btn" data-target="about"><span>About the deal</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="glance"><span>Consumption against the commitment</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="terms"><span>Contract terms that matter</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="options"><span>What to do about it</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button><button class="report-sidenav-btn" data-target="assessment"><span>Assessment and recommendation</span><span class="report-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true"><path d="M14.9995368,4.0419593c-.2280282,0-.4570329.0771487-.6450218.236329l-6.354515,5.3706247L1.645485,4.2782882c-.4213882-.3574232-1.0532264-.3046886-1.4091847.1181645-.3564466.4218765-.303712,1.0527382.1181645,1.4091847l7.0000251,5.9165251c.3730482.3154308.917972.3154308,1.2910203,0l7.0000251-5.9165251c.4218765-.3564466.4746111-.9873082.1181645-1.4091847-.1977546-.2343758-.4799822-.3544935-.7641629-.3544935Z" fill="currentColor"></path></svg></span></button></div></div><div class="report-container"><div class="report-tab-content" data-tab-id="snapshot"><div class="report-section" id="about"><div class="report-section-subheader"><p class="report-section-title">About the deal</p><p class="report-section-description">Prepared 29 Oct 2026 from the billing feed; a rough estimate, not an analysis.</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 28%"><col></colgroup><thead><tr><th><span>Item</span></th><th><span>Detail</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Supplier</span></td><td><span>Snowflake</span></td></tr><tr><td><span class="report-table-position">Agreement</span></td><td><span>Capacity commitment, $6.0M a year, $2.40 a credit</span></td></tr><tr><td><span class="report-table-position">Deal type</span></td><td><span>Consumption commitment, year 1</span></td></tr><tr><td><span class="report-table-position">Term</span></td><td><span>15 Apr 2026 to 14 Apr 2027</span></td></tr><tr><td><span class="report-table-position">Notice date</span></td><td><span>14 Feb 2027; 60 days before the term ends</span></td></tr><tr><td><span class="report-table-position">Owner</span></td><td><span>Data platform; negotiation owner Rachel Sato</span></td></tr><tr><td><span class="report-table-position">Source</span></td><td><span>Snowflake ACCOUNT_USAGE metering, daily billing feed to 29 Oct 2026</span></td></tr></tbody></table></div></div><div class="report-section" id="glance"><div class="report-section-subheader-row"><p class="report-section-title">Consumption against the commitment</p><p class="report-section-stat negative">118% pace · $1.1M overage ahead</p></div><div class="report-section-card-container"><div class="report-stat-grid" style="grid-template-columns: repeat(4, minmax(0, 1fr));"><div class="report-stat-grid-item"><span class="report-stat-grid-label">Commitment</span><span class="report-stat-grid-value">$6.0M</span><span class="report-stat-grid-sub">365 days, 198 elapsed (54%)</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Consumed to date</span><span class="report-stat-grid-value warning">$3.65M</span><span class="report-stat-grid-sub">61% of the commitment at 54% of the term</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Pace, last 90 days</span><span class="report-stat-grid-value negative">118%</span><span class="report-stat-grid-sub">$7.1M a year; commitment reached in February</span></div><div class="report-stat-grid-item"><span class="report-stat-grid-label">Projected overage</span><span class="report-stat-grid-value negative">$1.1M</span><span class="report-stat-grid-sub">$0.9M above the commitment at the on-demand rate</span></div></div></div><div class="report-section-card-container"><div class="report-table-wrapper"><p class="report-table-title">Where the consumption goes, last 90 days annualized</p><table class="report-table"><colgroup><col><col style="width: 12%"><col style="width: 16%"><col style="width: 14%"></colgroup><thead><tr><th><span>Workload</span></th><th class="report-table-cell-numeric"><span>Share</span></th><th class="report-table-cell-numeric"><span>Annualized</span></th><th class="report-table-cell-numeric"><span>Since July</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Data engineering pipelines</span></td><td class="report-table-cell-numeric"><span>36%</span></td><td class="report-table-cell-numeric"><span>$2.56M</span></td><td class="report-table-cell-numeric"><span>+8%</span></td></tr><tr><td><span class="report-table-position">Analytics and reporting</span></td><td class="report-table-cell-numeric"><span>25%</span></td><td class="report-table-cell-numeric"><span>$1.78M</span></td><td class="report-table-cell-numeric"><span>+5%</span></td></tr><tr><td><span class="report-table-position">Data science</span></td><td class="report-table-cell-numeric"><span>18%</span></td><td class="report-table-cell-numeric"><span>$1.28M</span></td><td class="report-table-cell-numeric"><span>+14%</span></td></tr><tr class="report-table-row-focus"><td><span class="report-table-position">Development and test</span></td><td class="report-table-cell-numeric"><span>16%</span></td><td class="report-table-cell-numeric"><span>$1.14M</span></td><td class="report-table-cell-numeric"><span class="negative">+60%</span></td></tr><tr><td><span class="report-table-position">Other</span></td><td class="report-table-cell-numeric"><span>5%</span></td><td class="report-table-cell-numeric"><span>$0.34M</span></td><td class="report-table-cell-numeric"><span>+2%</span></td></tr><tr class="report-table-row-total"><td><span class="report-table-position">Total</span></td><td class="report-table-cell-numeric"><span>100%</span></td><td class="report-table-cell-numeric"><span>$7.1M</span></td><td class="report-table-cell-numeric"><span>+13%</span></td></tr></tbody></table></div></div><div class="report-section-card-container"><div class="report-card callout warning"><div class="card-title-row"><span class="card-title warning">What drives the pace</span></div><div class="card-description"><p>Development and test warehouses run <strong>without auto-suspend on 11 of 14 environments</strong>. That alone explains $0.35M of the annual pace, and it is the part of the growth nobody chose.</p></div></div></div></div><div class="report-section" id="terms"><div class="report-section-subheader-row"><p class="report-section-title">Contract terms that matter</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 32%"><col style="width: 34%"><col></colgroup><thead><tr><th><span>Term</span></th><th><span>Today</span></th><th><span>Why it matters</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Capacity rate</span></td><td><span>$2.40 a credit</span></td><td><span>The rate the commitment is bought at; the next term&apos;s rate is the negotiation.</span></td></tr><tr><td><span class="report-table-position">On-demand rate above the commitment</span></td><td><span>$2.88 a credit, 20% higher</span></td><td><span>Every credit past the commitment costs 20% more; the overage is billed at this rate.</span></td></tr><tr><td><span class="report-table-position">Rollover of unused capacity</span></td><td><span>None</span></td><td><span>A commitment sized too high is lost; a top-up bought now and not used is lost too.</span></td></tr><tr><td><span class="report-table-position">Early renewal</span></td><td><span>Allowed; unused balance carries into the new term</span></td><td><span>The next term can start before this one ends, so the overage months can be priced at the capacity rate instead.</span></td></tr></tbody></table></div></div><div class="report-section" id="options"><div class="report-section-subheader"><p class="report-section-title">What to do about it</p><p class="report-section-description">Three options with their effect on this term and on the renewal. The recommended one is the proposal.</p></div><div class="report-section-card-container"><div class="report-card-grid" style="grid-template-columns: repeat(3, minmax(0, 1fr));"><div class="report-card"><span class="label">Recommended</span><div class="card-title-row"><span class="card-title">1. Rebalance now and pull the renewal forward</span><span class="card-title-stat">$2.4M lower over three years</span></div><p class="report-text">Auto-suspend and right-size the development and test warehouses, which brings the pace to $6.4M, then sign the next capacity term in January at a higher commitment and a lower rate.</p><div class="report-metric-list"><div class="report-metric-list-row"><span class="report-metric-list-label">Overage this term</span><span class="report-metric-list-value positive">$0 instead of $1.1M</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Rate on the new term</span><span class="report-metric-list-value positive">$2.21 a credit, 8% lower</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Next-term commitment</span><span class="report-metric-list-value">$7.5M with a 10% flex band</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Three-year effect</span><span class="report-metric-list-value positive">$2.4M lower than renewing as is</span></div></div></div><div class="report-card"><span class="label">Fallback</span><div class="card-title-row"><span class="card-title">2. Rebalance only</span><span class="card-title-stat">$0.6M lower</span></div><p class="report-text">Auto-suspend and right-size development and test; keep the current term and commitment, and renew on 15 Apr 2027 at the market rate then.</p><div class="report-metric-list"><div class="report-metric-list-row"><span class="report-metric-list-label">Overage this term</span><span class="report-metric-list-value">$0.5M instead of $1.1M</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Rate</span><span class="report-metric-list-value">Unchanged</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Renewal</span><span class="report-metric-list-value">15 Apr 2027 at the market rate then</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Three-year effect</span><span class="report-metric-list-value">$0.6M lower</span></div></div></div><div class="report-card"><span class="label">Not recommended</span><div class="card-title-row"><span class="card-title">3. Buy a top-up now</span><span class="card-title-stat">$0.9M lower</span></div><p class="report-text">Add $1.0M of capacity to the current term at the capacity rate to avoid on-demand billing. No leverage is used and the growth is not priced in.</p><div class="report-metric-list"><div class="report-metric-list-row"><span class="report-metric-list-label">Overage this term</span><span class="report-metric-list-value">$0.2M instead of $1.1M</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Rate</span><span class="report-metric-list-value">Unchanged, no leverage used</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Renewal</span><span class="report-metric-list-value">Unchanged; the growth is not priced in</span></div><div class="report-metric-list-row"><span class="report-metric-list-label">Three-year effect</span><span class="report-metric-list-value">$0.9M lower</span></div></div></div></div></div></div><div class="report-section" id="assessment"><div class="report-section-subheader-row"><p class="report-section-title">Assessment and recommendation</p><p class="report-section-stat warning">High priority</p></div><div class="report-section-card-container"><table class="report-table"><colgroup><col style="width: 24%"><col style="width: 18%"><col></colgroup><thead><tr><th><span>Factor</span></th><th><span>Assessment</span></th><th><span>Rationale</span></th></tr></thead><tbody><tr><td><span class="report-table-position">Financial opportunity</span></td><td><span class="positive">Strong</span></td><td><span>$1.1M of overage avoided this term and $2.4M over three years against renewing as is.</span></td></tr><tr><td><span class="report-table-position">Negotiation leverage</span></td><td><span class="positive">Strong</span></td><td><span>A larger commitment, signed early, is what Snowflake&apos;s field wants in its Q4; the rate is the price of it.</span></td></tr><tr><td><span class="report-table-position">Decision urgency</span></td><td><span class="warning">High</span></td><td><span>The commitment is reached in February; the early renewal must be signed in January to price those months at the capacity rate.</span></td></tr><tr><td><span class="report-table-position">Contractual risk</span></td><td><span>Moderate</span></td><td><span>No rollover and a 20% on-demand premium; the flex band on the next term is the protection to secure.</span></td></tr></tbody></table></div><div class="report-section-card-container"><div class="report-card callout"><div class="card-title-row"><span class="card-title">If you accept</span></div><div class="card-description"><p>A <strong>consumption analysis</strong> of the development and test environments within five working days, a <strong>negotiation playbook</strong> for the early renewal before January, and a <strong>savings report</strong> after signing. Dismissing records the reason so the proposal does not come back; snoozing brings it back on the date you choose.</p></div></div></div></div></div></div></div></div>
<script>
  const tabButtons = document.querySelectorAll(".report-utility-nav-btn");
  const header = document.querySelector(".report-header-container");

  // The sidenav sticks flush beneath the header (see styles.css). The header's
  // height varies with its content and shrinks as it collapses, so publish its
  // live height and keep the sidenav in sync — ResizeObserver fires through the
  // collapse transition too, so the sidenav tracks the header frame by frame.
  function publishHeaderHeight() {
    if (!header) return;
    document.documentElement.style.setProperty(
      "--report-header-height",
      header.offsetHeight + "px"
    );
  }

  if (header && "ResizeObserver" in window) {
    new ResizeObserver(publishHeaderHeight).observe(header);
  }
  publishHeaderHeight();

  // Measure the header's expanded and compact heights so the collapse
  // thresholds adapt to the report (summary-card content varies the height).
  // Toggling with the transition suppressed makes the probe instant and
  // invisible. Re-measured on resize / once webfonts settle, since both shift
  // the height.
  let expandedHeaderH = 0;
  let compactHeaderH = 0;
  function measureHeaderHeights() {
    if (!header) return;
    const wasCompact = header.classList.contains("is-compact");
    header.classList.add("is-anim-suppressed");
    header.classList.remove("is-compact");
    expandedHeaderH = header.offsetHeight;
    header.classList.add("is-compact");
    compactHeaderH = header.offsetHeight;
    header.classList.toggle("is-compact", wasCompact);
    header.classList.remove("is-anim-suppressed");
  }
  measureHeaderHeights();
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(measureHeaderHeights);
  }

  // Header collapse controller. The header is expanded at the very top of the
  // report and compact once you've scrolled down into the content. Run on
  // every scroll and after programmatic scrolls (nav-item clicks, tab
  // switches) so both paths behave identically.
  //
  // Driven by scrollY with hysteresis: expand near the very top, collapse once
  // you've scrolled past the header. The catch is that collapsing shrinks the
  // header by \`delta\`, and browser scroll anchoring then shifts scrollY by that
  // same delta to keep your place — so the gap between the two thresholds must
  // exceed \`delta\`, otherwise the post-toggle anchoring shift lands past the
  // opposite threshold and the header flickers. We collapse \`delta\` + margin
  // down and expand within \`margin\` of the top, giving a band of delta + margin.
  const HEADER_MARGIN_PX = 32;
  function updateHeaderCompact() {
    if (!header) return;
    const delta = Math.max(0, expandedHeaderH - compactHeaderH);
    const y = window.pageYOffset;
    if (y <= HEADER_MARGIN_PX) {
      header.classList.remove("is-compact");
    } else if (y >= delta + 2 * HEADER_MARGIN_PX) {
      header.classList.add("is-compact");
    }
  }

  // Scroll a section flush beneath the header (used by nav-item clicks and tab
  // switches). The offset depends on the header height, but landing the scroll
  // *also* changes whether the header is collapsed — and an animated collapse
  // reflows content under the new scroll position, which browser scroll
  // anchoring then chases, drifting us onto the previous section. So: jump,
  // settle the header into its destination state with the collapse animation
  // suppressed (one synchronous reflow, nothing for anchoring to track), then
  // re-pin against the now-final header height. Animation is restored next
  // frame so manual scrolling keeps its smooth collapse.
  function scrollSectionToTop(target) {
    if (!target) return;
    const pin = () =>
      Math.max(0, target.getBoundingClientRect().top + window.pageYOffset - getHeaderOffset());
    window.scrollTo({ top: pin(), behavior: "auto" });
    if (header) header.classList.add("is-anim-suppressed");
    updateHeaderCompact();
    window.scrollTo({ top: pin(), behavior: "auto" });
    if (header) {
      requestAnimationFrame(() => header.classList.remove("is-anim-suppressed"));
    }
  }

  function getHeaderOffset() {
    return header ? header.offsetHeight : 0;
  }

  function updateActiveSidenav() {
    const activePane = document.querySelector('.report-tab-content:not([hidden])');
    if (!activePane) return;

    const sections = activePane.querySelectorAll('.report-section');
    if (!sections.length) return;

    let activeId = sections[0].id;
    const headerOffset = getHeaderOffset() + 24;

    sections.forEach(section => {
      const rect = section.getBoundingClientRect();
      if (rect.top <= headerOffset) {
        activeId = section.id;
      }
    });

    document
      .querySelectorAll('.report-tab-sidenav:not([hidden]) .report-sidenav-btn')
      .forEach(button => {
        button.classList.toggle('active', button.dataset.target === activeId);
      });
  }

  function onScroll() {
    updateHeaderCompact();
    updateActiveSidenav();
  }

  function activateTab(tabId) {
    tabButtons.forEach(button => button.classList.toggle("active", button.dataset.tabId === tabId));

    document.querySelectorAll(".report-tab-sidenav").forEach(element => {
      element.hidden = element.dataset.tabId !== tabId;
    });

    document.querySelectorAll(".report-tab-content").forEach(element => {
      element.hidden = element.dataset.tabId !== tabId;
    });

    document.querySelectorAll(".report-sidenav-btn").forEach(button => button.classList.remove("active"));

    const firstSide = document.querySelector('.report-tab-sidenav:not([hidden]) .report-sidenav-btn');
    if (firstSide) firstSide.classList.add('active');

    const firstSection = document.querySelector('.report-tab-content:not([hidden]) .report-section');
    scrollSectionToTop(firstSection);

    updateActiveSidenav();
  }

  tabButtons.forEach(button => {
    button.addEventListener("click", () => activateTab(button.dataset.tabId));
  });

  const firstExpander = document.querySelector(".report-expander");
  if (firstExpander) firstExpander.setAttribute("open", "");

  const firstSideOnLoad = document.querySelector('.report-tab-sidenav:not([hidden]) .report-sidenav-btn');
  if (firstSideOnLoad) firstSideOnLoad.classList.add('active');

  document.querySelectorAll(".report-sidenav-btn").forEach(button => {
    button.addEventListener("click", () => {
      const target = document.getElementById(button.dataset.target);
      if (!target) return;

      document.querySelectorAll('.report-tab-sidenav:not([hidden]) .report-sidenav-btn').forEach(sideButton => {
        sideButton.classList.remove('active');
      });
      button.classList.add('active');

      scrollSectionToTop(target);
      updateActiveSidenav();
    });
  });

  updateHeaderCompact();
  updateActiveSidenav();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
    measureHeaderHeights();
    onScroll();
  });

    // Fallback colgroup for tables whose schema didn't declare column
    // widths. If the server-side renderer already emitted a <colgroup>
    // (because at least one column in the JSON had a \`width\`), respect
    // it and bail — don't strip and replace.
  document.querySelectorAll('.report-table').forEach((table) => {
    if (table.querySelector(':scope > colgroup')) return;

    const firstRow = table.querySelector('thead tr, tbody tr');
    if (!firstRow) return;

    const columnCount = firstRow.children.length;

    const colgroup = document.createElement('colgroup');

    let widths = [];

    if (columnCount === 2) {
      widths = ['13.75rem', 'auto']; // 220px / rest
    } else if (columnCount === 3) {
      widths = ['13.75rem', '13.125rem', 'auto']; // 220 / 210 / rest
    } else if (columnCount === 4) {
      widths = ['13.75rem', '13.125rem', '13.125rem', 'auto']; // 220 / 210 / 210 / rest
    } else if (columnCount === 5) {
      widths = ['13.75rem', '13.125rem', '12rem', '12rem', '10rem'];
    } else {
      // fallback: first two anchored, rest auto
      widths = Array.from({ length: columnCount }, (_, i) => {
        if (i === 0) return '13.75rem';
        if (i === 1) return '13.125rem';
        return 'auto';
      });
    }

    widths.forEach((width) => {
      const col = document.createElement('col');
      if (width !== 'auto') {
        col.style.width = width;
      }
      colgroup.appendChild(col);
    });

    table.insertBefore(colgroup, table.firstChild);
  });
</script>
</body>
</html>

`;
